import { useState, useEffect, useRef } from "react";

// ---------------------------------------------
//  GLOBAL STYLES
// ---------------------------------------------
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --ink:          #0f1117;
  --ink-soft:     #2a2d35;
  --paper:        #f5f2eb;
  --cream:        #ede9df;
  --cream-dark:   #e2ddd3;
  --warm-white:   #faf8f4;
  --accent:       #2d6a4f;
  --accent-hover: #235c42;
  --accent-light: #52b788;
  --accent-pale:  #d8f3dc;
  --accent-xpale: #edf7f0;
  --warn:         #c9722a;
  --warn-pale:    #fdf0e6;
  --muted:        #7a7468;
  --muted-light:  #a09990;
  --rule:         #d4cfc5;
  --rule-light:   #e8e4dc;
  --shadow-sm:    0 1px 4px rgba(15,17,23,0.06);
  --shadow:       0 2px 16px rgba(15,17,23,0.08);
  --shadow-lg:    0 8px 48px rgba(15,17,23,0.14);
  --radius:       6px;
  --radius-lg:    10px;
}

html, body, #root { height: 100%; }
body {
  font-family: 'DM Sans', sans-serif;
  background: var(--paper);
  color: var(--ink);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* -- TOPBAR -- */
.topbar {
  position: sticky; top: 0; z-index: 200;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 48px; height: 60px;
  background: var(--warm-white);
  border-bottom: 1px solid var(--rule);
  box-shadow: var(--shadow-sm);
}
.logo { font-family:'Playfair Display',serif; font-size:1.25rem; font-weight:700; letter-spacing:-0.01em; color:var(--ink); cursor:pointer; user-select:none; }
.logo span { color:var(--accent); }
.topbar-right { display:flex; align-items:center; gap:14px; }
.topbar-session { font-size:0.75rem; color:var(--muted); border:1px solid var(--rule); padding:4px 12px; border-radius:20px; }
.topbar-badge { font-size:0.68rem; font-weight:500; letter-spacing:0.1em; text-transform:uppercase; color:var(--accent); background:var(--accent-pale); padding:4px 10px; border-radius:20px; }

.app { min-height:calc(100vh - 60px); display:flex; flex-direction:column; }

/* -- LANDING -- */
.landing { flex:1; display:grid; grid-template-columns:55% 45%; min-height:calc(100vh - 60px); }
.landing-left { display:flex; flex-direction:column; justify-content:center; padding:80px 72px; background:var(--warm-white); border-right:1px solid var(--rule); }
.landing-eyebrow { font-size:0.7rem; font-weight:600; letter-spacing:0.16em; text-transform:uppercase; color:var(--accent); margin-bottom:20px; display:flex; align-items:center; gap:8px; }
.landing-eyebrow::before { content:''; display:block; width:24px; height:2px; background:var(--accent); }
.landing-headline { font-family:'Playfair Display',serif; font-size:clamp(2.2rem,3.5vw,3.4rem); font-weight:700; line-height:1.1; letter-spacing:-0.02em; color:var(--ink); margin-bottom:28px; }
.landing-headline em { font-style:italic; color:var(--accent); }
.landing-sub { font-size:1rem; font-weight:300; line-height:1.75; color:var(--muted); max-width:460px; margin-bottom:48px; }
.btn-cta { display:inline-flex; align-items:center; gap:10px; background:var(--accent); color:white; font-family:'DM Sans',sans-serif; font-size:0.95rem; font-weight:500; padding:15px 32px; border:none; border-radius:var(--radius); cursor:pointer; transition:all 0.2s; align-self:flex-start; }
.btn-cta:hover { background:var(--accent-hover); transform:translateY(-1px); box-shadow:0 4px 20px rgba(45,106,79,0.3); }
.btn-cta:hover svg { transform:translateX(4px); }
.btn-cta svg { transition:transform 0.2s; }
.landing-features { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-top:56px; padding-top:48px; border-top:1px solid var(--rule); }
.feature-item { display:flex; flex-direction:column; gap:4px; }
.feature-label { font-size:0.82rem; font-weight:600; color:var(--ink); display:flex; align-items:center; gap:6px; }
.feature-label::before { content:''; width:6px; height:6px; border-radius:50%; background:var(--accent-light); flex-shrink:0; }
.feature-desc { font-size:0.78rem; color:var(--muted); line-height:1.5; padding-left:12px; }

/* -- LANDING RIGHT / PREVIEW CAROUSEL -- */
.landing-right { background:var(--cream); position:relative; overflow:hidden; display:flex; flex-direction:column; justify-content:center; align-items:center; padding:60px 48px; gap:20px; }
.landing-bg-word { position:absolute; bottom:-40px; right:-20px; font-family:'Playfair Display',serif; font-size:14rem; font-weight:700; color:rgba(0,0,0,0.04); pointer-events:none; user-select:none; line-height:1; }

.preview-carousel { display:flex; align-items:center; gap:14px; width:100%; max-width:460px; }
.carousel-arrow { width:34px; height:34px; border-radius:50%; background:white; border:1.5px solid var(--rule); display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s; flex-shrink:0; color:var(--ink-soft); box-shadow:var(--shadow-sm); }
.carousel-arrow:hover { border-color:var(--accent); color:var(--accent); transform:scale(1.05); }

.preview-card { background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); box-shadow:var(--shadow-lg); width:100%; overflow:hidden; position:relative; z-index:1; }
.preview-card-header { background:var(--ink); color:white; padding:14px 20px; display:flex; justify-content:space-between; align-items:center; }
.preview-card-header strong { font-family:'Playfair Display',serif; font-size:0.88rem; }
.preview-industry-badge { font-size:0.65rem; background:var(--accent); color:white; padding:3px 8px; border-radius:10px; letter-spacing:0.06em; text-transform:uppercase; font-weight:500; }
.preview-section { padding:4px 0; border-bottom:1px solid var(--cream); }
.preview-section:last-child { border:none; }
.preview-section-title { font-size:0.62rem; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:var(--accent); padding:6px 18px 3px; }
.preview-row { display:flex; justify-content:space-between; align-items:center; padding:4px 18px; font-size:0.76rem; transition:opacity 0.3s; }
.preview-row span:last-child { font-family:'DM Mono',monospace; font-size:0.7rem; color:var(--muted); }
.preview-more { padding:12px 18px; font-size:0.7rem; color:var(--muted-light); text-align:center; letter-spacing:0.05em; border-top:1px dashed var(--rule); }
.preview-dots { display:flex; gap:6px; justify-content:center; }
.preview-dot { width:6px; height:6px; border-radius:50%; background:var(--rule); transition:all 0.2s; cursor:pointer; }
.preview-dot.active { background:var(--accent); width:18px; border-radius:3px; }

/* -- CENTERED SCREENS -- */
.centered-screen { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; background:var(--warm-white); }
.screen-eyebrow { font-size:0.68rem; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:var(--accent); margin-bottom:14px; text-align:center; }
.screen-headline { font-family:'Playfair Display',serif; font-size:1.9rem; font-weight:600; text-align:center; margin-bottom:10px; color:var(--ink); }
.screen-sub { font-size:0.92rem; color:var(--muted); text-align:center; margin-bottom:48px; max-width:460px; line-height:1.65; }

.mode-cards { display:grid; grid-template-columns:1fr 1fr; gap:18px; width:100%; max-width:660px; margin-bottom:36px; }
.mode-card { background:white; border:2px solid var(--rule); border-radius:var(--radius-lg); padding:28px 24px; cursor:pointer; transition:all 0.2s; text-align:left; position:relative; overflow:hidden; }
.mode-card:hover { border-color:var(--accent-light); transform:translateY(-2px); box-shadow:var(--shadow); }
.mode-card.selected { border-color:var(--accent); background:var(--accent-xpale); }
.mode-card-check { position:absolute; top:14px; right:14px; width:22px; height:22px; background:var(--accent); border-radius:50%; display:flex; align-items:center; justify-content:center; color:white; font-size:0.7rem; opacity:0; transition:opacity 0.2s; }
.mode-card.selected .mode-card-check { opacity:1; }
.mode-icon { font-size:1.6rem; margin-bottom:14px; display:block; }
.mode-card-title { font-family:'Playfair Display',serif; font-size:1.05rem; font-weight:600; margin-bottom:6px; color:var(--ink); }
.mode-card-desc { font-size:0.8rem; color:var(--muted); line-height:1.55; }

.entry-form { width:100%; max-width:540px; background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); padding:28px 32px; box-shadow:var(--shadow); animation:fadeUp 0.3s ease; }
.entry-form-title { font-family:'Playfair Display',serif; font-size:0.95rem; font-weight:600; margin-bottom:22px; padding-bottom:14px; border-bottom:1px solid var(--cream); color:var(--ink); }
.field-group { display:flex; flex-direction:column; gap:16px; }
.field { display:flex; flex-direction:column; gap:5px; }
.field label { font-size:0.72rem; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; color:var(--muted); }
.field input, .field select { font-family:'DM Sans',sans-serif; font-size:0.93rem; padding:11px 13px; border:1.5px solid var(--rule); border-radius:var(--radius); background:var(--warm-white); color:var(--ink); transition:all 0.2s; outline:none; }
.field input:focus, .field select:focus { border-color:var(--accent); background:white; box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.field-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.btn-primary { width:100%; padding:13px; background:var(--accent); color:white; border:none; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.92rem; font-weight:500; cursor:pointer; margin-top:20px; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:8px; }
.btn-primary:hover:not(:disabled) { background:var(--accent-hover); transform:translateY(-1px); }
.btn-primary:disabled { opacity:0.4; cursor:not-allowed; }

/* -- INTERVIEW -- */
.interview-wrap { flex:1; display:flex; min-height:calc(100vh - 60px); }
.sidebar { width:272px; flex-shrink:0; background:var(--ink); color:white; display:flex; flex-direction:column; padding:36px 24px; position:sticky; top:60px; height:calc(100vh - 60px); overflow-y:auto; }
.sidebar-for { font-size:0.62rem; letter-spacing:0.1em; text-transform:uppercase; color:rgba(255,255,255,0.35); margin-bottom:5px; }
.sidebar-name { font-family:'Playfair Display',serif; font-size:1rem; font-weight:600; margin-bottom:3px; }
.sidebar-mode { font-size:0.72rem; color:var(--accent-light); margin-bottom:36px; }
.sidebar-nav { display:flex; flex-direction:column; gap:2px; flex:1; }
.sidebar-section-btn { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:var(--radius); background:none; border:none; cursor:pointer; text-align:left; color:rgba(255,255,255,0.38); font-family:'DM Sans',sans-serif; font-size:0.8rem; transition:all 0.15s; width:100%; }
.sidebar-section-btn:hover:not(:disabled) { color:rgba(255,255,255,0.7); background:rgba(255,255,255,0.05); }
.sidebar-section-btn.active { color:white; background:rgba(255,255,255,0.1); }
.sidebar-section-btn.done { color:var(--accent-light); }
.sidebar-section-btn:disabled { cursor:default; }
.sec-indicator { width:7px; height:7px; border-radius:50%; flex-shrink:0; border:1.5px solid currentColor; transition:all 0.2s; }
.sidebar-section-btn.done .sec-indicator { background:var(--accent-light); }
.sidebar-section-btn.active .sec-indicator { background:white; border-color:white; }
.sidebar-footer { margin-top:28px; padding-top:22px; border-top:1px solid rgba(255,255,255,0.1); }
.progress-meta { display:flex; justify-content:space-between; font-size:0.66rem; letter-spacing:0.08em; text-transform:uppercase; color:rgba(255,255,255,0.35); margin-bottom:8px; }
.progress-track { height:3px; background:rgba(255,255,255,0.1); border-radius:2px; overflow:hidden; }
.progress-fill { height:100%; background:var(--accent-light); border-radius:2px; transition:width 0.4s ease; }
.sidebar-review-btn { margin-top:14px; width:100%; padding:8px; background:none; border:1px solid rgba(255,255,255,0.15); border-radius:var(--radius); color:rgba(255,255,255,0.45); font-family:'DM Sans',sans-serif; font-size:0.75rem; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:6px; }
.sidebar-review-btn:hover { border-color:rgba(255,255,255,0.35); color:rgba(255,255,255,0.7); }

/* -- QUESTION AREA -- */
.q-area { flex:1; display:flex; flex-direction:column; justify-content:center; align-items:center; padding:60px 64px; background:var(--warm-white); }
.q-card { width:100%; max-width:640px; animation:fadeUp 0.3s ease; }
@keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }

.q-breadcrumb { font-size:0.68rem; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:var(--accent); margin-bottom:10px; }
.q-headline { font-family:'Playfair Display',serif; font-size:1.55rem; font-weight:600; line-height:1.3; color:var(--ink); margin-bottom:6px; }
.q-technical { font-size:0.75rem; color:var(--muted-light); margin-bottom:8px; font-style:italic; }
.q-helper-toggle { display:inline-flex; align-items:center; gap:5px; font-size:0.78rem; color:var(--accent); cursor:pointer; background:none; border:none; padding:0; margin-bottom:20px; font-family:'DM Sans',sans-serif; transition:color 0.15s; }
.q-helper-toggle:hover { color:var(--accent-hover); }
.q-helper-icon { width:16px; height:16px; border-radius:50%; background:var(--accent-pale); color:var(--accent); display:inline-flex; align-items:center; justify-content:center; font-size:0.65rem; font-weight:700; }
.q-plain { font-size:0.84rem; color:var(--muted); margin-bottom:20px; line-height:1.6; }

.helper-panel { background:var(--accent-xpale); border:1px solid var(--accent-pale); border-radius:var(--radius-lg); padding:18px 20px; margin-bottom:22px; animation:fadeUp 0.2s ease; }
.helper-panel-title { font-size:0.72rem; font-weight:600; letter-spacing:0.08em; text-transform:uppercase; color:var(--accent); margin-bottom:8px; }
.helper-panel p { font-size:0.84rem; color:var(--ink-soft); line-height:1.65; margin-bottom:8px; }
.helper-panel p:last-child { margin-bottom:0; }
.helper-example { background:white; border-left:3px solid var(--accent-light); padding:10px 14px; border-radius:0 var(--radius) var(--radius) 0; font-size:0.8rem; color:var(--ink-soft); font-style:italic; line-height:1.5; margin-top:10px; }
.helper-why { margin-top:10px; font-size:0.78rem; color:var(--muted); display:flex; align-items:flex-start; gap:6px; line-height:1.5; }
.helper-why::before { content:'*'; color:var(--accent); flex-shrink:0; margin-top:1px; }

/* -- OPTIONS -- */
.options-list { display:flex; flex-direction:column; gap:8px; }
.options-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.opt-btn { display:flex; align-items:center; gap:12px; padding:12px 16px; border:1.5px solid var(--rule); border-radius:var(--radius); cursor:pointer; background:white; transition:all 0.15s; text-align:left; font-family:'DM Sans',sans-serif; color:var(--ink); font-size:0.88rem; line-height:1.4; }
.opt-btn:hover { border-color:var(--accent-light); background:var(--accent-xpale); }
.opt-btn.selected { border-color:var(--accent); background:var(--accent-pale); }
.opt-btn.recommended { border-color:var(--accent); border-width:2px; box-shadow:0 0 0 3px rgba(45,106,79,0.12); position:relative; }
.opt-btn.recommended.selected { background:var(--accent-pale); }
.recommended-badge { position:absolute; top:-1px; right:10px; background:var(--accent); color:white; font-size:0.6rem; font-weight:600; letter-spacing:0.08em; text-transform:uppercase; padding:2px 7px; border-radius:0 0 4px 4px; }
.opt-indicator { width:18px; height:18px; flex-shrink:0; border-radius:50%; border:2px solid var(--rule); transition:all 0.15s; display:flex; align-items:center; justify-content:center; }
.opt-btn.selected .opt-indicator, .opt-btn.recommended.selected .opt-indicator { border-color:var(--accent); background:var(--accent); }
.opt-btn.selected .opt-indicator::after { content:''; width:6px; height:6px; border-radius:50%; background:white; }
.opt-btn.recommended .opt-indicator { border-color:var(--accent); }
.opt-indicator.square { border-radius:3px; }
.opt-btn.selected .opt-indicator.square { background:var(--accent); border-color:var(--accent); }
.opt-check { color:white; font-size:0.65rem; }
.opt-label { flex:1; }
.opt-sublabel { display:block; font-size:0.72rem; color:var(--muted); margin-top:2px; line-height:1.4; }
.opt-other-input { width:100%; margin-top:10px; font-family:'DM Sans',sans-serif; font-size:0.88rem; padding:10px 12px; border:1.5px solid var(--accent-light); border-radius:var(--radius); background:white; color:var(--ink); outline:none; animation:fadeUp 0.2s ease; }
.opt-other-input:focus { border-color:var(--accent); box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.unsure-btn { margin-top:12px; padding:10px 16px; background:none; border:1.5px dashed var(--rule); border-radius:var(--radius); color:var(--muted); font-family:'DM Sans',sans-serif; font-size:0.82rem; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; gap:8px; width:100%; }
.unsure-btn:hover { border-color:var(--warn); color:var(--warn); background:var(--warn-pale); }
.unsure-btn.active { border-color:var(--warn); color:var(--warn); background:var(--warn-pale); }
.uncertain-note { margin-top:10px; font-size:0.75rem; color:var(--warn); display:flex; align-items:center; gap:6px; animation:fadeUp 0.2s ease; }

/* -- ERP CARDS -- */
.erp-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }
.erp-card { display:flex; flex-direction:column; align-items:center; gap:8px; padding:16px 12px; border:1.5px solid var(--rule); border-radius:var(--radius); cursor:pointer; background:white; transition:all 0.15s; text-align:center; }
.erp-card:hover { border-color:var(--accent-light); background:var(--accent-xpale); }
.erp-card.selected { border-color:var(--accent); background:var(--accent-pale); }
.erp-logo { font-size:1.4rem; }
.erp-name { font-size:0.8rem; font-weight:600; color:var(--ink); }
.erp-tier { font-size:0.65rem; color:var(--muted); letter-spacing:0.04em; }
.erp-card.selected .erp-tier { color:var(--accent); }

/* -- NAV -- */
.q-nav { display:flex; justify-content:space-between; align-items:center; margin-top:28px; }
.btn-back { background:none; border:1.5px solid var(--rule); color:var(--muted); font-family:'DM Sans',sans-serif; font-size:0.85rem; padding:10px 20px; border-radius:var(--radius); cursor:pointer; transition:all 0.15s; display:flex; align-items:center; gap:6px; }
.btn-back:hover { border-color:var(--ink); color:var(--ink); }
.btn-next { background:var(--accent); color:white; border:none; font-family:'DM Sans',sans-serif; font-size:0.9rem; font-weight:500; padding:11px 26px; border-radius:var(--radius); cursor:pointer; transition:all 0.15s; display:flex; align-items:center; gap:8px; }
.btn-next:hover:not(:disabled) { background:var(--accent-hover); }
.btn-next:disabled { opacity:0.35; cursor:not-allowed; }
.btn-next.final { background:var(--ink); }
.btn-next.final:hover:not(:disabled) { background:var(--ink-soft); }

/* -- REVIEW -- */
.review-screen { flex:1; display:flex; background:var(--warm-white); }
.review-main { flex:1; padding:56px 64px; max-width:860px; }
.review-headline { font-family:'Playfair Display',serif; font-size:2rem; font-weight:700; color:var(--ink); margin-bottom:8px; }
.review-sub { font-size:0.9rem; color:var(--muted); margin-bottom:36px; line-height:1.6; }
.review-uncertain-banner { background:var(--warn-pale); border:1px solid #f0b07a; border-radius:var(--radius-lg); padding:14px 18px; margin-bottom:28px; display:flex; gap:12px; align-items:flex-start; font-size:0.84rem; color:var(--ink-soft); line-height:1.55; }
.review-section { margin-bottom:28px; }
.review-section-title { font-size:0.68rem; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:var(--accent); margin-bottom:10px; padding-bottom:8px; border-bottom:1px solid var(--rule); display:flex; align-items:center; gap:8px; }
.warn-dot { width:6px; height:6px; border-radius:50%; background:var(--warn); flex-shrink:0; }
.review-item { display:flex; align-items:flex-start; justify-content:space-between; padding:10px 14px; border-radius:var(--radius); margin-bottom:4px; background:white; border:1px solid var(--rule-light); gap:12px; }
.review-item.uncertain-item { border-color:var(--warn); background:var(--warn-pale); }
.review-item-q { font-size:0.78rem; color:var(--muted); margin-bottom:2px; }
.review-item-a { font-size:0.88rem; color:var(--ink); font-weight:500; }

/* inline edit inside review */
.review-item-edit-panel { margin-top:10px; padding:12px; background:var(--accent-xpale); border-radius:var(--radius); border:1px solid var(--accent-pale); animation:fadeUp 0.2s ease; }
.review-inline-options { display:flex; flex-direction:column; gap:6px; max-height:260px; overflow-y:auto; }
.review-inline-opt { display:flex; align-items:center; gap:10px; padding:8px 12px; border:1.5px solid var(--rule); border-radius:var(--radius); cursor:pointer; background:white; font-family:'DM Sans',sans-serif; font-size:0.84rem; transition:all 0.15s; }
.review-inline-opt:hover { border-color:var(--accent-light); background:var(--accent-xpale); }
.review-inline-opt.selected { border-color:var(--accent); background:var(--accent-pale); }
.review-inline-actions { display:flex; gap:8px; margin-top:10px; }
.btn-save-inline { padding:7px 16px; background:var(--accent); color:white; border:none; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; cursor:pointer; transition:background 0.15s; }
.btn-save-inline:hover { background:var(--accent-hover); }
.btn-cancel-inline { padding:7px 14px; background:none; border:1.5px solid var(--rule); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.8rem; color:var(--muted); cursor:pointer; }
.btn-cancel-inline:hover { border-color:var(--ink); color:var(--ink); }

.review-edit-btn { background:none; border:1px solid var(--rule); color:var(--muted); font-family:'DM Sans',sans-serif; font-size:0.72rem; padding:4px 10px; border-radius:20px; cursor:pointer; transition:all 0.15s; flex-shrink:0; white-space:nowrap; }
.review-edit-btn:hover { border-color:var(--accent); color:var(--accent); }
.review-edit-btn.open { border-color:var(--accent); color:var(--accent); background:var(--accent-xpale); }
.review-actions { display:flex; gap:12px; align-items:center; margin-top:32px; padding-top:28px; border-top:1px solid var(--rule); }
.btn-generate { background:var(--ink); color:white; border:none; font-family:'DM Sans',sans-serif; font-size:0.95rem; font-weight:500; padding:14px 32px; border-radius:var(--radius); cursor:pointer; transition:all 0.2s; display:flex; align-items:center; gap:10px; }
.btn-generate:hover { background:var(--ink-soft); transform:translateY(-1px); }
.review-note { font-size:0.78rem; color:var(--muted); line-height:1.5; }

/* -- PROCESSING -- */
.processing-screen { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; text-align:center; background:var(--warm-white); }
.processing-orb { width:64px; height:64px; border-radius:50%; border:3px solid var(--cream-dark); border-top-color:var(--accent); animation:spin 1s linear infinite; margin-bottom:32px; }
@keyframes spin { to{transform:rotate(360deg)} }
.processing-steps { display:flex; flex-direction:column; gap:10px; margin-top:32px; width:100%; max-width:340px; }
.processing-step { display:flex; align-items:center; gap:12px; font-size:0.82rem; padding:10px 14px; border-radius:var(--radius); background:white; border:1px solid var(--rule); animation:fadeUp 0.4s ease both; }
.processing-step:nth-child(1){animation-delay:0.1s}
.processing-step:nth-child(2){animation-delay:0.6s}
.processing-step:nth-child(3){animation-delay:1.1s}
.processing-step:nth-child(4){animation-delay:1.6s}
.step-dot { width:8px; height:8px; border-radius:50%; background:var(--accent-light); flex-shrink:0; animation:pulse 1.5s ease infinite; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }

/* -- OUTPUT -- */
.output-screen { flex:1; display:flex; flex-direction:column; background:var(--warm-white); padding:48px 64px; max-width:1180px; margin:0 auto; width:100%; }
.output-topbar { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:28px; padding-bottom:22px; border-bottom:1px solid var(--rule); }
.output-title { font-family:'Playfair Display',serif; font-size:1.7rem; font-weight:700; color:var(--ink); margin-bottom:4px; }
.output-meta { font-size:0.8rem; color:var(--muted); }
.output-actions { display:flex; gap:10px; align-items:center; }
.btn-export { background:var(--accent); color:white; border:none; font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; padding:10px 20px; border-radius:var(--radius); cursor:pointer; transition:all 0.2s; display:flex; align-items:center; gap:7px; white-space:nowrap; }
.btn-export:hover { background:var(--accent-hover); }
.btn-export.secondary { background:none; border:1.5px solid var(--rule); color:var(--ink-soft); }
.btn-export.secondary:hover { border-color:var(--ink); background:var(--cream); }

/* -- COA TABLE + INLINE EDIT -- */
.coa-table { width:100%; border-collapse:collapse; font-size:0.84rem; }
.coa-table th { text-align:left; font-size:0.65rem; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:var(--muted); padding:9px 14px; background:var(--cream); border-bottom:1px solid var(--rule); position:sticky; top:60px; z-index:10; }
.coa-table td { padding:9px 14px; border-bottom:1px solid var(--rule-light); vertical-align:middle; }
.coa-table tbody tr:hover td { background:var(--cream); }
.coa-table tbody tr.flagged td { background:#fdf5ec; }
.coa-table tbody tr.editing td { background:var(--accent-xpale); }
.coa-section-row td { font-size:0.7rem; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:var(--accent); background:var(--accent-xpale) !important; padding:7px 14px; }
.acct-num { font-family:'DM Mono',monospace; font-size:0.8rem; color:var(--muted); }
.type-badge { font-size:0.66rem; padding:2px 8px; border-radius:10px; background:var(--cream-dark); color:var(--muted); font-weight:500; letter-spacing:0.03em; white-space:nowrap; }
.flag-badge { font-size:0.66rem; padding:2px 8px; border-radius:10px; background:var(--warn-pale); color:var(--warn); font-weight:500; border:1px solid #f0b07a; white-space:nowrap; }
.desc-cell { font-size:0.78rem; color:var(--muted); }

/* three-dot menu */
.row-actions { position:relative; display:flex; align-items:center; justify-content:flex-end; }
.three-dot-btn { background:none; border:none; cursor:pointer; color:var(--muted); padding:4px 6px; border-radius:4px; font-size:1rem; line-height:1; opacity:0; transition:all 0.15s; }
.coa-table tbody tr:hover .three-dot-btn, .three-dot-btn.open { opacity:1; color:var(--ink); background:var(--cream-dark); }
.dot-menu { position:absolute; right:0; top:calc(100% + 4px); background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); box-shadow:var(--shadow-lg); z-index:100; min-width:180px; overflow:hidden; animation:fadeUp 0.15s ease; }
.dot-menu-item { display:flex; align-items:center; gap:10px; padding:10px 14px; font-family:'DM Sans',sans-serif; font-size:0.82rem; color:var(--ink); cursor:pointer; transition:background 0.1s; border:none; background:none; width:100%; text-align:left; }
.dot-menu-item:hover { background:var(--cream); }
.dot-menu-item.danger { color:#c0392b; }
.dot-menu-item.danger:hover { background:#fdf2f2; }
.dot-menu-divider { height:1px; background:var(--rule-light); margin:4px 0; }

/* inline edit row */
.inline-edit-row td { background:var(--accent-xpale) !important; padding:16px 14px; }
.inline-edit-form { display:flex; flex-direction:column; gap:10px; }
.inline-edit-grid { display:grid; grid-template-columns:120px 1fr 200px; gap:10px; }
.inline-input { font-family:'DM Sans',sans-serif; font-size:0.88rem; padding:8px 10px; border:1.5px solid var(--rule); border-radius:var(--radius); background:white; color:var(--ink); outline:none; width:100%; }
.inline-input:focus { border-color:var(--accent); box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.inline-select { font-family:'DM Sans',sans-serif; font-size:0.84rem; padding:8px 10px; border:1.5px solid var(--rule); border-radius:var(--radius); background:white; color:var(--ink); outline:none; width:100%; }
.inline-select:focus { border-color:var(--accent); }
.inline-desc-input { font-family:'DM Sans',sans-serif; font-size:0.84rem; padding:8px 10px; border:1.5px solid var(--rule); border-radius:var(--radius); background:white; color:var(--ink); outline:none; width:100%; }
.inline-desc-input:focus { border-color:var(--accent); }
.inline-context { font-size:0.78rem; color:var(--muted); line-height:1.5; padding:8px 12px; background:white; border-radius:var(--radius); border-left:3px solid var(--accent-light); }
.inline-actions { display:flex; gap:8px; align-items:center; }
.btn-inline-save { padding:7px 18px; background:var(--accent); color:white; border:none; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; cursor:pointer; }
.btn-inline-save:hover { background:var(--accent-hover); }
.btn-inline-cancel { padding:7px 14px; background:none; border:1.5px solid var(--rule); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.82rem; color:var(--muted); cursor:pointer; }
.btn-inline-cancel:hover { border-color:var(--ink); color:var(--ink); }
.inline-confirm-note { font-size:0.74rem; color:var(--muted); font-style:italic; }

/* add account */
.add-account-section { margin-top:32px; padding-top:24px; border-top:1px dashed var(--rule); }
.btn-add-account { display:flex; align-items:center; gap:8px; padding:10px 20px; background:none; border:1.5px dashed var(--rule); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.85rem; color:var(--muted); cursor:pointer; transition:all 0.2s; }
.btn-add-account:hover { border-color:var(--accent); color:var(--accent); background:var(--accent-xpale); }
.add-account-form { margin-top:16px; background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); padding:20px 24px; animation:fadeUp 0.25s ease; }
.add-account-grid { display:grid; grid-template-columns:120px 1fr 200px; gap:12px; margin-bottom:12px; }
.add-desc-row { margin-bottom:14px; }

/* coming soon banner */
.coming-soon-banner { margin-top:40px; background:var(--ink); color:white; border-radius:var(--radius-lg); padding:24px 32px; display:flex; justify-content:space-between; align-items:center; gap:24px; }
.csb-left strong { font-family:'Playfair Display',serif; font-size:1rem; font-weight:600; display:block; margin-bottom:4px; }
.csb-left p { font-size:0.8rem; color:rgba(255,255,255,0.55); line-height:1.5; }
.csb-badge { font-size:0.65rem; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:var(--accent-light); border:1px solid rgba(82,183,136,0.4); padding:5px 12px; border-radius:20px; white-space:nowrap; }

/* confirm modal */
.modal-overlay { position:fixed; inset:0; background:rgba(15,17,23,0.5); z-index:500; display:flex; align-items:center; justify-content:center; animation:fadeIn 0.2s ease; }
@keyframes fadeIn { from{opacity:0} to{opacity:1} }
.modal-box { background:white; border-radius:var(--radius-lg); padding:28px 32px; max-width:440px; width:90%; box-shadow:var(--shadow-lg); animation:fadeUp 0.25s ease; }
.modal-title { font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:600; margin-bottom:10px; }
.modal-body { font-size:0.88rem; color:var(--muted); line-height:1.6; margin-bottom:20px; }
.modal-actions { display:flex; gap:10px; justify-content:flex-end; }
.btn-modal-confirm { padding:9px 22px; background:var(--accent); color:white; border:none; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:500; cursor:pointer; }
.btn-modal-confirm:hover { background:var(--accent-hover); }
.btn-modal-confirm.danger { background:#c0392b; }
.btn-modal-confirm.danger:hover { background:#a93226; }
.btn-modal-cancel { padding:9px 18px; background:none; border:1.5px solid var(--rule); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.88rem; color:var(--muted); cursor:pointer; }
.btn-modal-cancel:hover { border-color:var(--ink); color:var(--ink); }


/* -- API KEY SCREEN -- */
.apikey-screen { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; background:var(--warm-white); }
.apikey-card { width:100%; max-width:480px; background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); padding:40px 40px; box-shadow:var(--shadow); }
.apikey-logo { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:700; color:var(--ink); margin-bottom:6px; }
.apikey-logo span { color:var(--accent); }
.apikey-title { font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:600; color:var(--ink); margin-bottom:8px; margin-top:28px; }
.apikey-sub { font-size:0.84rem; color:var(--muted); line-height:1.6; margin-bottom:24px; }
.apikey-input-wrap { position:relative; margin-bottom:8px; }
.apikey-input { width:100%; font-family:'DM Mono',monospace; font-size:0.82rem; padding:12px 44px 12px 14px; border:1.5px solid var(--rule); border-radius:var(--radius); background:var(--warm-white); color:var(--ink); outline:none; letter-spacing:0.04em; transition:all 0.2s; }
.apikey-input:focus { border-color:var(--accent); background:white; box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.apikey-toggle { position:absolute; right:12px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:var(--muted); font-size:0.8rem; padding:4px; }
.apikey-toggle:hover { color:var(--ink); }
.apikey-note { font-size:0.74rem; color:var(--muted-light); line-height:1.5; margin-bottom:20px; display:flex; gap:6px; align-items:flex-start; }
.apikey-note::before { content:"🔑"; flex-shrink:0; }
.apikey-divider { height:1px; background:var(--rule-light); margin:24px 0; }
.apikey-skip { width:100%; padding:10px; background:none; border:1.5px solid var(--rule); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.84rem; color:var(--muted); cursor:pointer; transition:all 0.2s; margin-top:10px; }
.apikey-skip:hover { border-color:var(--ink); color:var(--ink); }

/* -- AI FOLLOW-UP SCREEN -- */
.followup-screen { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; background:var(--warm-white); }
.followup-card { width:100%; max-width:640px; }
.followup-eyebrow { font-size:0.68rem; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:var(--accent); margin-bottom:12px; display:flex; align-items:center; gap:8px; }
.followup-eyebrow::before { content:''; width:20px; height:2px; background:var(--accent); }
.followup-headline { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:600; color:var(--ink); margin-bottom:8px; line-height:1.3; }
.followup-sub { font-size:0.88rem; color:var(--muted); margin-bottom:32px; line-height:1.6; }
.followup-loading { display:flex; flex-direction:column; align-items:center; gap:16px; padding:48px 0; }
.followup-spinner { width:40px; height:40px; border:2.5px solid var(--cream-dark); border-top-color:var(--accent); border-radius:50%; animation:spin 0.9s linear infinite; }
.followup-loading-text { font-size:0.84rem; color:var(--muted); }
.followup-qa { display:flex; flex-direction:column; gap:24px; margin-bottom:32px; }
.followup-item { background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); padding:24px 28px; }
.followup-q-label { font-size:0.66rem; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:var(--accent); margin-bottom:10px; }
.followup-q-text { font-family:'Playfair Display',serif; font-size:1.05rem; font-weight:600; color:var(--ink); margin-bottom:16px; line-height:1.35; }
.followup-textarea { width:100%; font-family:'DM Sans',sans-serif; font-size:0.9rem; padding:12px 14px; border:1.5px solid var(--rule); border-radius:var(--radius); background:var(--warm-white); color:var(--ink); outline:none; resize:vertical; min-height:80px; line-height:1.6; transition:all 0.2s; }
.followup-textarea:focus { border-color:var(--accent); background:white; box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.followup-skip-q { font-size:0.76rem; color:var(--muted-light); margin-top:6px; cursor:pointer; background:none; border:none; padding:0; font-family:'DM Sans',sans-serif; }
.followup-skip-q:hover { color:var(--muted); }
.followup-nav { display:flex; justify-content:space-between; align-items:center; }
.followup-error { background:var(--warn-pale); border:1px solid #f0b07a; border-radius:var(--radius); padding:12px 16px; font-size:0.82rem; color:var(--ink-soft); margin-bottom:20px; display:flex; gap:10px; align-items:flex-start; }
.followup-options { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
.followup-option { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; border:1.5px solid var(--rule); border-radius:var(--radius); background:var(--warm-white); cursor:pointer; transition:all 0.15s; text-align:left; font-family:'DM Sans',sans-serif; font-size:0.88rem; color:var(--ink); line-height:1.5; }
.followup-option:hover { border-color:var(--accent); background:white; }
.followup-option.selected { border-color:var(--accent); background:white; box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.followup-option-radio { width:16px; height:16px; border:1.5px solid var(--rule); border-radius:50%; flex-shrink:0; margin-top:2px; position:relative; transition:all 0.15s; }
.followup-option.selected .followup-option-radio { border-color:var(--accent); }
.followup-option.selected .followup-option-radio::after { content:''; position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); width:8px; height:8px; border-radius:50%; background:var(--accent); }
.followup-other-toggle { font-size:0.76rem; color:var(--muted); margin-top:4px; margin-bottom:8px; cursor:pointer; background:none; border:none; padding:0; font-family:'DM Sans',sans-serif; text-decoration:underline; text-decoration-style:dotted; text-underline-offset:3px; }
.followup-other-toggle:hover { color:var(--accent); }
.followup-counter { font-size:0.78rem; color:var(--muted-light); }
.followup-counter strong { color:var(--ink-soft); font-weight:600; }

/* -- AI SUGGESTION PANEL -- */
.ai-suggestion-panel { position:absolute; right:0; top:calc(100% + 4px); background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); box-shadow:var(--shadow-lg); z-index:150; width:320px; padding:16px; animation:fadeUp 0.15s ease; }
.ai-suggestion-title { font-size:0.7rem; font-weight:600; letter-spacing:0.1em; text-transform:uppercase; color:var(--accent); margin-bottom:12px; }
.ai-suggestion-loading { display:flex; align-items:center; gap:10px; font-size:0.82rem; color:var(--muted); padding:8px 0; }
.ai-suggestion-spinner { width:16px; height:16px; border:2px solid var(--cream-dark); border-top-color:var(--accent); border-radius:50%; animation:spin 0.8s linear infinite; flex-shrink:0; }
.ai-suggestion-item { padding:8px 10px; border-radius:var(--radius); cursor:pointer; transition:background 0.1s; border:1px solid transparent; margin-bottom:4px; }
.ai-suggestion-item:hover { background:var(--accent-xpale); border-color:var(--accent-pale); }
.ai-suggestion-name { font-size:0.84rem; font-weight:500; color:var(--ink); margin-bottom:2px; }
.ai-suggestion-rationale { font-size:0.74rem; color:var(--muted); line-height:1.4; }
.ai-suggestion-error { font-size:0.78rem; color:var(--warn); padding:4px 0; }


/* -- SESSION ACTIONS (bottom of output) -- */
.session-actions-bar {
  display:flex; align-items:center; justify-content:space-between;
  margin-top:32px; padding:20px 24px;
  background:var(--cream); border-radius:var(--radius-lg);
  border:1px solid var(--rule);
  gap:16px; flex-wrap:wrap;
}
.session-actions-left { display:flex; flex-direction:column; gap:4px; }
.session-actions-title { font-size:0.82rem; font-weight:600; color:var(--ink); }
.session-actions-sub { font-size:0.76rem; color:var(--muted); }
.session-actions-right { display:flex; gap:10px; align-items:center; flex-shrink:0; }
.btn-session { display:flex; align-items:center; gap:7px; padding:10px 20px; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; cursor:pointer; transition:all 0.2s; white-space:nowrap; }
.btn-session.save { background:none; border:1.5px solid var(--rule); color:var(--ink-soft); }
.btn-session.save:hover { border-color:var(--accent); color:var(--accent); background:var(--accent-xpale); }
.btn-session.new-build { background:var(--ink); color:white; border:none; }
.btn-session.new-build:hover { background:var(--ink-soft); transform:translateY(-1px); }

/* -- SAVE CONFIRMATION TOAST -- */
.save-toast {
  position:fixed; bottom:28px; left:50%; transform:translateX(-50%);
  background:var(--ink); color:white; padding:10px 20px; border-radius:20px;
  font-size:0.82rem; font-weight:500; z-index:300;
  animation:toastIn 0.25s ease, toastOut 0.3s ease 2s forwards;
  display:flex; align-items:center; gap:8px; box-shadow:var(--shadow-lg);
  white-space:nowrap;
}
@keyframes toastIn { from{opacity:0;transform:translateX(-50%) translateY(10px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
@keyframes toastOut { from{opacity:1} to{opacity:0} }


/* -- FLOW SELECT (Build vs Audit) -- */
.flow-select-screen { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; background:var(--warm-white); }
.flow-cards { display:grid; grid-template-columns:1fr 1fr; gap:20px; width:100%; max-width:720px; margin-bottom:0; }
.flow-card { background:white; border:2px solid var(--rule); border-radius:var(--radius-lg); padding:32px 28px; cursor:pointer; transition:all 0.2s; text-align:left; position:relative; overflow:hidden; }
.flow-card:hover { border-color:var(--accent-light); transform:translateY(-2px); box-shadow:var(--shadow); }
.flow-card.selected { border-color:var(--accent); background:var(--accent-xpale); }
.flow-card-icon { font-size:2rem; margin-bottom:16px; display:block; }
.flow-card-title { font-family:'Playfair Display',serif; font-size:1.15rem; font-weight:600; margin-bottom:8px; color:var(--ink); }
.flow-card-desc { font-size:0.82rem; color:var(--muted); line-height:1.6; }
.flow-card-tag { display:inline-block; margin-top:12px; font-size:0.68rem; font-weight:600; letter-spacing:0.08em; text-transform:uppercase; padding:3px 10px; border-radius:10px; }
.flow-card-tag.build { background:var(--accent-pale); color:var(--accent); }
.flow-card-tag.audit { background:#e8f0fe; color:#3b5bdb; }
.flow-card-check { position:absolute; top:14px; right:14px; width:22px; height:22px; background:var(--accent); border-radius:50%; display:flex; align-items:center; justify-content:center; color:white; font-size:0.7rem; opacity:0; transition:opacity 0.2s; }
.flow-card.selected .flow-card-check { opacity:1; }

/* -- COA UPLOAD SCREEN -- */
.upload-screen { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; background:var(--warm-white); }
.upload-card { width:100%; max-width:600px; }
.upload-zone { border:2px dashed var(--rule); border-radius:var(--radius-lg); padding:48px 32px; text-align:center; cursor:pointer; transition:all 0.2s; background:white; position:relative; }
.upload-zone:hover, .upload-zone.dragover { border-color:var(--accent); background:var(--accent-xpale); }
.upload-zone.has-file { border-color:var(--accent); border-style:solid; background:var(--accent-xpale); }
.upload-icon { font-size:2.5rem; margin-bottom:16px; display:block; }
.upload-title { font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:600; color:var(--ink); margin-bottom:8px; }
.upload-sub { font-size:0.84rem; color:var(--muted); line-height:1.6; margin-bottom:16px; }
.upload-formats { display:flex; gap:8px; justify-content:center; flex-wrap:wrap; }
.upload-format-badge { font-size:0.68rem; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; padding:3px 10px; border-radius:10px; background:var(--cream); color:var(--muted); }
.upload-input { position:absolute; inset:0; opacity:0; cursor:pointer; }
.upload-file-info { display:flex; align-items:center; gap:12px; padding:14px 18px; background:white; border:1px solid var(--rule); border-radius:var(--radius); margin-top:16px; }
.upload-file-icon { font-size:1.4rem; }
.upload-file-name { font-size:0.88rem; font-weight:500; color:var(--ink); }
.upload-file-meta { font-size:0.76rem; color:var(--muted); }
.upload-file-remove { margin-left:auto; background:none; border:none; color:var(--muted-light); cursor:pointer; font-size:1rem; padding:4px; transition:color 0.15s; }
.upload-file-remove:hover { color:var(--warn); }

/* column mapping */
.col-map-card { width:100%; max-width:600px; background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); padding:28px 32px; box-shadow:var(--shadow); margin-top:20px; animation:fadeUp 0.3s ease; }
.col-map-title { font-family:'Playfair Display',serif; font-size:0.95rem; font-weight:600; margin-bottom:6px; }
.col-map-sub { font-size:0.8rem; color:var(--muted); margin-bottom:20px; line-height:1.5; }
.col-map-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:16px; }
.col-map-preview { margin-top:16px; padding:12px; background:var(--cream); border-radius:var(--radius); font-size:0.76rem; color:var(--muted); }
.col-map-preview-title { font-weight:600; color:var(--ink); margin-bottom:6px; font-size:0.76rem; letter-spacing:0.05em; text-transform:uppercase; }
.col-map-row { display:flex; gap:8px; padding:3px 0; border-bottom:1px solid var(--rule-light); }
.col-map-row:last-child { border:none; }
.col-map-cell { flex:1; font-family:'DM Mono',monospace; font-size:0.72rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* -- AUDIT RECOMMENDATIONS SCREEN -- */
.audit-screen { flex:1; display:flex; background:var(--warm-white); }
.audit-main { flex:1; padding:48px 64px; max-width:1100px; }
.audit-headline { font-family:'Playfair Display',serif; font-size:1.9rem; font-weight:700; color:var(--ink); margin-bottom:6px; }
.audit-sub { font-size:0.88rem; color:var(--muted); margin-bottom:32px; line-height:1.6; }
.audit-summary-bar { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:36px; }
.audit-stat { background:white; border:1px solid var(--rule); border-radius:var(--radius); padding:16px 20px; text-align:center; }
.audit-stat-number { font-family:'Playfair Display',serif; font-size:2rem; font-weight:700; margin-bottom:4px; }
.audit-stat-label { font-size:0.72rem; color:var(--muted); letter-spacing:0.06em; text-transform:uppercase; }
.audit-stat.critical .audit-stat-number { color:#c0392b; }
.audit-stat.warning .audit-stat-number { color:var(--warn); }
.audit-stat.suggestion .audit-stat-number { color:var(--accent); }
.audit-stat.total .audit-stat-number { color:var(--ink); }

.finding-card { background:white; border:1px solid var(--rule); border-radius:var(--radius-lg); margin-bottom:12px; overflow:hidden; transition:box-shadow 0.2s; }
.finding-card:hover { box-shadow:var(--shadow); }
.finding-card.accepted { border-color:var(--accent); }
.finding-card.ignored { opacity:0.55; }
.finding-header { display:flex; align-items:center; gap:12px; padding:16px 20px; cursor:pointer; }
.finding-severity { font-size:0.65rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; padding:3px 10px; border-radius:10px; flex-shrink:0; white-space:nowrap; }
.finding-severity.critical { background:#fdf2f2; color:#c0392b; border:1px solid #f5b7b1; }
.finding-severity.warning { background:var(--warn-pale); color:var(--warn); border:1px solid #f0b07a; }
.finding-severity.suggestion { background:var(--accent-xpale); color:var(--accent); border:1px solid var(--accent-pale); }
.finding-account { font-family:'DM Mono',monospace; font-size:0.8rem; color:var(--muted); flex-shrink:0; }
.finding-issue { font-size:0.88rem; font-weight:500; color:var(--ink); flex:1; }
.finding-actions { display:flex; gap:8px; margin-left:auto; flex-shrink:0; }
.btn-accept { padding:6px 14px; background:var(--accent); color:white; border:none; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.76rem; font-weight:500; cursor:pointer; transition:all 0.15s; }
.btn-accept:hover { background:var(--accent-hover); }
.btn-accept.active { background:var(--accent); }
.btn-modify { padding:6px 14px; background:none; border:1.5px solid var(--rule); color:var(--muted); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.76rem; cursor:pointer; transition:all 0.15s; }
.btn-modify:hover { border-color:var(--accent); color:var(--accent); }
.btn-ignore { padding:6px 14px; background:none; border:1.5px solid var(--rule); color:var(--muted); border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.76rem; cursor:pointer; transition:all 0.15s; }
.btn-ignore:hover { border-color:var(--muted); color:var(--ink); }
.btn-ignore.active { background:var(--cream); color:var(--muted); border-color:var(--rule); }
.finding-body { padding:0 20px 16px; border-top:1px solid var(--rule-light); }
.finding-detail { font-size:0.84rem; color:var(--muted); line-height:1.6; padding:12px 0 8px; }
.finding-recommendation { background:var(--accent-xpale); border-left:3px solid var(--accent); padding:10px 14px; border-radius:0 var(--radius) var(--radius) 0; font-size:0.84rem; color:var(--ink-soft); line-height:1.55; margin-bottom:8px; }
.finding-rec-label { font-size:0.68rem; font-weight:600; letter-spacing:0.08em; text-transform:uppercase; color:var(--accent); margin-bottom:4px; }
.finding-modify-input { width:100%; font-family:'DM Sans',sans-serif; font-size:0.88rem; padding:10px 12px; border:1.5px solid var(--accent-light); border-radius:var(--radius); outline:none; margin-top:8px; background:white; color:var(--ink); }
.finding-modify-input:focus { border-color:var(--accent); box-shadow:0 0 0 3px rgba(45,106,79,0.08); }
.finding-modify-save { margin-top:8px; padding:7px 16px; background:var(--accent); color:white; border:none; border-radius:var(--radius); font-family:'DM Sans',sans-serif; font-size:0.8rem; cursor:pointer; }

.audit-actions { display:flex; gap:12px; align-items:center; margin-top:32px; padding-top:24px; border-top:1px solid var(--rule); flex-wrap:wrap; }
.btn-generate-audit { background:var(--ink); color:white; border:none; font-family:'DM Sans',sans-serif; font-size:0.95rem; font-weight:500; padding:14px 32px; border-radius:var(--radius); cursor:pointer; transition:all 0.2s; display:flex; align-items:center; gap:10px; }
.btn-generate-audit:hover { background:var(--ink-soft); transform:translateY(-1px); }
.audit-loading { display:flex; flex-direction:column; align-items:center; padding:80px 24px; gap:20px; }
.audit-loading-spinner { width:48px; height:48px; border:3px solid var(--cream-dark); border-top-color:var(--accent); border-radius:50%; animation:spin 1s linear infinite; }


/* -- FLOATING ADD ACCOUNT BUTTON -- */
.fab-add-account {
  position:fixed; bottom:32px; right:32px; z-index:150;
  display:flex; align-items:center; gap:8px;
  padding:13px 22px; background:var(--accent); color:white;
  border:none; border-radius:40px; cursor:pointer;
  font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:500;
  box-shadow:0 4px 20px rgba(45,106,79,0.35);
  transition:all 0.2s;
}
.fab-add-account:hover { background:var(--accent-hover); transform:translateY(-2px); box-shadow:0 6px 28px rgba(45,106,79,0.4); }
.fab-add-account svg { flex-shrink:0; }

/* section-level add button */
.section-add-btn {
  background:none; border:none; color:var(--accent); cursor:pointer;
  font-family:'DM Sans',sans-serif; font-size:0.7rem; font-weight:500;
  padding:2px 8px; border-radius:10px; margin-left:10px;
  opacity:0; transition:opacity 0.15s;
  display:inline-flex; align-items:center; gap:4px;
}
.coa-section-row:hover .section-add-btn { opacity:1; }
.section-add-btn:hover { background:var(--accent-pale); }


/* -- PLATFORM QUICK-SELECT -- */
.platform-select-row { display:flex; gap:8px; margin-bottom:16px; flex-wrap:wrap; }
.platform-btn { padding:6px 14px; border:1.5px solid var(--rule); border-radius:20px; background:white; font-family:'DM Sans',sans-serif; font-size:0.78rem; color:var(--muted); cursor:pointer; transition:all 0.15s; }
.platform-btn:hover { border-color:var(--accent-light); color:var(--ink); }
.platform-btn.active { border-color:var(--accent); background:var(--accent-pale); color:var(--accent); font-weight:500; }

/* -- COLUMN MAPPING HELPER -- */
.col-map-helper-icon { display:inline-flex; align-items:center; justify-content:center; width:15px; height:15px; border-radius:50%; background:var(--cream-dark); color:var(--muted); font-size:0.6rem; font-weight:700; cursor:pointer; margin-left:5px; transition:all 0.15s; flex-shrink:0; vertical-align:middle; }
.col-map-helper-icon:hover { background:var(--accent-pale); color:var(--accent); }
.col-map-helper-panel { background:var(--accent-xpale); border:1px solid var(--accent-pale); border-radius:var(--radius); padding:10px 14px; margin-top:6px; margin-bottom:4px; font-size:0.78rem; color:var(--ink-soft); line-height:1.55; animation:fadeUp 0.15s ease; }
.col-map-helper-panel strong { color:var(--accent); }


/* -- HELP BUTTON (floating, persistent) -- */
.help-fab {
  position:fixed; bottom:32px; right:28px; z-index:190;
  display:flex; align-items:center; gap:9px;
  padding:11px 18px; background:white;
  border:1.5px solid var(--rule); border-radius:40px;
  cursor:pointer; font-family:'DM Sans',sans-serif;
  font-size:0.82rem; font-weight:500; color:var(--ink-soft);
  box-shadow:0 2px 16px rgba(15,17,23,0.10);
  transition:all 0.2s; text-decoration:none;
}
.help-fab:hover {
  border-color:var(--accent); color:var(--accent);
  box-shadow:0 4px 24px rgba(45,106,79,0.18);
  transform:translateY(-2px);
}
.help-fab-dot {
  width:8px; height:8px; border-radius:50%;
  background:var(--accent-light); flex-shrink:0;
  animation:pulse 2s ease infinite;
}
.help-fab-label { display:flex; flex-direction:column; gap:1px; }
.help-fab-title { font-size:0.82rem; font-weight:600; line-height:1.2; }
.help-fab-sub { font-size:0.68rem; color:var(--muted); font-weight:400; letter-spacing:0.01em; }
.help-fab:hover .help-fab-sub { color:var(--accent); }


/* -- ADASTRA FOOTER ATTRIBUTION -- */
.adastra-footer {
  text-align:center; padding:20px 0 4px;
  font-size:0.72rem; color:var(--muted-light);
  letter-spacing:0.04em;
  display:flex; align-items:center; justify-content:center; gap:6px;
}
.adastra-footer a {
  color:var(--muted); text-decoration:none; font-weight:500;
  transition:color 0.15s;
}
.adastra-footer a:hover { color:var(--accent); }
.adastra-footer-dot {
  width:4px; height:4px; border-radius:50%;
  background:var(--rule); flex-shrink:0;
}

/* -- ADASTRA CTA CARD (output screen) -- */
.adastra-cta-card {
  margin-top:28px;
  background:linear-gradient(135deg, #0f1117 0%, #1a2a1f 100%);
  border-radius:var(--radius-lg);
  padding:28px 36px;
  display:flex; justify-content:space-between; align-items:center;
  gap:32px; flex-wrap:wrap;
  position:relative; overflow:hidden;
}
.adastra-cta-card::before {
  content:'';
  position:absolute; top:-40px; right:-40px;
  width:180px; height:180px; border-radius:50%;
  background:rgba(82,183,136,0.06);
  pointer-events:none;
}
.adastra-cta-left { flex:1; min-width:240px; }
.adastra-cta-eyebrow {
  font-size:0.65rem; font-weight:600; letter-spacing:0.14em;
  text-transform:uppercase; color:var(--accent-light);
  margin-bottom:8px; display:flex; align-items:center; gap:6px;
}
.adastra-cta-eyebrow::before {
  content:''; width:16px; height:1.5px; background:var(--accent-light);
}
.adastra-cta-headline {
  font-family:'Playfair Display',serif;
  font-size:1.15rem; font-weight:600; color:white;
  margin-bottom:8px; line-height:1.3;
}
.adastra-cta-desc {
  font-size:0.82rem; color:rgba(255,255,255,0.5);
  line-height:1.6; max-width:400px;
}
.adastra-cta-right { display:flex; flex-direction:column; gap:10px; align-items:flex-end; flex-shrink:0; }
.btn-adastra-primary {
  padding:12px 24px; background:var(--accent); color:white;
  border:none; border-radius:var(--radius);
  font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:500;
  cursor:pointer; transition:all 0.2s; white-space:nowrap;
  text-decoration:none; display:inline-block;
}
.btn-adastra-primary:hover { background:var(--accent-hover); transform:translateY(-1px); }
.btn-adastra-secondary {
  padding:10px 20px; background:none;
  border:1px solid rgba(255,255,255,0.2); color:rgba(255,255,255,0.65);
  border-radius:var(--radius);
  font-family:'DM Sans',sans-serif; font-size:0.82rem;
  cursor:pointer; transition:all 0.2s; white-space:nowrap;
  text-decoration:none; display:inline-block; text-align:center;
}
.btn-adastra-secondary:hover { border-color:rgba(255,255,255,0.5); color:white; }

@media (max-width:900px) {
  .landing{grid-template-columns:1fr}
  .landing-right{display:none}
  .landing-left{padding:48px 32px}
  .topbar{padding:0 24px}
  .mode-cards{grid-template-columns:1fr}
  .interview-wrap{flex-direction:column}
  .sidebar{width:100%;height:auto;position:static}
  .q-area{padding:40px 24px}
  .output-screen{padding:32px 24px}
  .review-main{padding:40px 28px}
  .options-grid,.erp-grid{grid-template-columns:1fr 1fr}
  .inline-edit-grid,.add-account-grid{grid-template-columns:1fr}
}
`;

// ---------------------------------------------
//  LANDING PREVIEW DATA
// ---------------------------------------------
const INDUSTRY_PREVIEWS = [
  {
    industry: "SaaS",
    sections: [
      { label: "Revenue", rows: [["4020","Subscription / Recurring Revenue (MRR)"],["4025","Usage-Based / Overage Revenue"],["4030","Contract Revenue — Fixed Fee"]] },
      { label: "Cost of Revenue", rows: [["5100","Hosting & Infrastructure (AWS/GCP)"],["5110","Third-Party API & Licensing Costs"]] },
      { label: "Assets", rows: [["1560","Capitalized Software Development Costs"],["1100","Accounts Receivable (Trade)"]] },
    ],
  },
  {
    industry: "Construction",
    sections: [
      { label: "Cost of Goods Sold", rows: [["5050","Job Costs — Labor"],["5060","Job Costs — Materials"],["5070","Job Costs — Subcontractors"]] },
      { label: "Assets — Current", rows: [["1230","Costs in Excess of Billings (CIE)"],["1240","Retainage Receivable"]] },
      { label: "Liabilities", rows: [["2090","Billings in Excess of Costs (BIE)"],["2095","Retainage Payable"]] },
    ],
  },
  {
    industry: "Professional Services",
    sections: [
      { label: "Revenue", rows: [["4000","Service Revenue"],["4005","Professional Fees Revenue"],["4040","Commission Revenue"]] },
      { label: "Expenses — Compensation", rows: [["6000","Salaries & Wages — Regular"],["6010","Payroll Tax — Employer Portion"]] },
      { label: "Expenses — G&A", rows: [["6200","Professional Services — Legal"],["6270","Subscriptions & Software (SaaS Tools)"]] },
    ],
  },
  {
    industry: "Retail",
    sections: [
      { label: "Revenue", rows: [["4010","Product Sales Revenue"],["4015","Sales Returns & Allowances"],["4016","Sales Discounts"]] },
      { label: "Inventory", rows: [["1200","Inventory — Raw Materials"],["1210","Inventory — Work in Process"],["1220","Inventory — Finished Goods"]] },
      { label: "Cost of Goods Sold", rows: [["5000","Cost of Goods Sold — Materials"],["5030","Freight & Shipping — Inbound"]] },
    ],
  },
  {
    industry: "Real Estate",
    sections: [
      { label: "Revenue", rows: [["4050","Rental / Lease Income"],["4030","Contract Revenue — Fixed Fee"]] },
      { label: "Assets — Fixed", rows: [["1520","Land"],["1530","Buildings & Improvements"],["1535","Accum. Depreciation — Buildings"]] },
      { label: "Liabilities — Long-Term", rows: [["2220","Mortgage Payable — Long-Term"],["2040","Accrued Liabilities"]] },
    ],
  },
];

// ---------------------------------------------
//  ERP DEFINITIONS
// ---------------------------------------------
const ERP_OPTIONS = [
  { value:"qbo", name:"QuickBooks Online", logo:"📚", tier:"Tier 1 — Full Export" },
  { value:"xero", name:"Xero", logo:"🌐", tier:"Tier 1 — Full Export" },
  { value:"sage50", name:"Sage 50", logo:"📒", tier:"Tier 1 — Full Export" },
  { value:"netsuite", name:"NetSuite", logo:"☁", tier:"Tier 2 — Best-Effort" },
  { value:"sap", name:"SAP", logo:"⚙", tier:"Tier 2 — Best-Effort" },
  { value:"dynamics", name:"MS Dynamics 365", logo:"🪟", tier:"Tier 2 — Best-Effort" },
  { value:"freshbooks", name:"FreshBooks", logo:"📖", tier:"Tier 1 — Full Export" },
  { value:"wave", name:"Wave", logo:"🌊", tier:"Tier 1 — Full Export" },
  { value:"other", name:"Other / Generic", logo:"📋", tier:"Generic CSV" },
];

// ---------------------------------------------
//  ERP EXPORT ADAPTERS
// ---------------------------------------------
// QBO Detail Type mapping
const QBO_TYPE_MAP = {
  "Bank": "Checking",
  "Accounts Receivable": "Accounts Receivable (A/R)",
  "Other Current Asset": "Other Current Assets",
  "Fixed Asset": "Machinery & Equipment",
  "Other Asset": "Other Long-term Assets",
  "Accounts Payable": "Accounts Payable (A/P)",
  "Credit Card": "Credit Card",
  "Other Current Liability": "Other Current Liabilities",
  "Long Term Liability": "Notes Payable",
  "Equity": "Opening Balance Equity",
  "Retained Earnings": "Retained Earnings",
  "Income": "Service/Fee Income",
  "Other Income": "Other Miscellaneous Income",
  "Cost of Goods Sold": "Supplies & Materials - COGS",
  "Expense": "Other Business Expenses",
  "Other Expense": "Other Miscellaneous Expense",
};
const QBO_ACCT_TYPE_MAP = {
  "Bank":"Bank","Accounts Receivable":"Accounts receivable (A/R)",
  "Other Current Asset":"Other current assets","Fixed Asset":"Fixed assets",
  "Other Asset":"Other assets","Accounts Payable":"Accounts payable (A/P)",
  "Credit Card":"Credit card","Other Current Liability":"Other current liabilities",
  "Long Term Liability":"Long term liabilities","Equity":"Equity",
  "Retained Earnings":"Equity","Income":"Income","Other Income":"Other income",
  "Cost of Goods Sold":"Cost of goods sold","Expense":"Expenses","Other Expense":"Other expense",
};

const XERO_TYPE_MAP = {
  "Bank":"BANK","Accounts Receivable":"CURRENT","Other Current Asset":"CURRENT",
  "Fixed Asset":"FIXED","Other Asset":"NONCURRENT","Accounts Payable":"CURRLIAB",
  "Credit Card":"CURRLIAB","Other Current Liability":"CURRLIAB","Long Term Liability":"TERMLIAB",
  "Equity":"EQUITY","Retained Earnings":"EQUITY","Income":"REVENUE","Other Income":"OTHERINCOME",
  "Cost of Goods Sold":"DIRECTCOSTS","Expense":"OVERHEADS","Other Expense":"OVERHEADS",
};

const SAGE50_TYPE_MAP = {
  "Bank":"Cash","Accounts Receivable":"Accounts Receivable","Other Current Asset":"Other Current Assets",
  "Fixed Asset":"Property and Equipment","Other Asset":"Other Assets",
  "Accounts Payable":"Accounts Payable","Credit Card":"Other Current Liabilities",
  "Other Current Liability":"Other Current Liabilities","Long Term Liability":"Long-Term Liabilities",
  "Equity":"Equity","Retained Earnings":"Equity","Income":"Income",
  "Other Income":"Other Income","Cost of Goods Sold":"Cost of Sales",
  "Expense":"Expenses","Other Expense":"Other Expenses",
};

function buildCSV(rows, headers, mapper) {
  const mapped = rows.map(mapper);
  return [headers, ...mapped].map(r => r.map(v => `"${(v||"").replace(/"/g,'""')}"`).join(",")).join("\n");
}

function exportForERP(coa, erp, businessName) {
  const accounts = coa.filter(r => !r.section && !r.deleted);
  let csv, filename;

  if (erp === "qbo") {
    const headers = ["Account Number","Account Name","Account Type","Detail Type","Description"];
    csv = buildCSV(accounts, headers, r => [
      r.num, r.name,
      QBO_ACCT_TYPE_MAP[r.type] || r.type,
      QBO_TYPE_MAP[r.type] || r.type,
      r.desc
    ]);
    filename = `${businessName}_COA_QuickBooks.csv`;
  } else if (erp === "xero") {
    const headers = ["Code","Name","Type","Tax Code","Description"];
    csv = buildCSV(accounts, headers, r => [
      r.num, r.name,
      XERO_TYPE_MAP[r.type] || r.type,
      "NONE", r.desc
    ]);
    filename = `${businessName}_COA_Xero.csv`;
  } else if (erp === "sage50") {
    const headers = ["Account ID","Account Description","Account Type","1099 Type","Beginning Balance"];
    csv = buildCSV(accounts, headers, r => [
      r.num, r.name,
      SAGE50_TYPE_MAP[r.type] || r.type,
      "NONE","0.00"
    ]);
    filename = `${businessName}_COA_Sage50.csv`;
  } else if (erp === "freshbooks" || erp === "wave") {
    const headers = ["Account Number","Account Name","Account Type","Description"];
    csv = buildCSV(accounts, headers, r => [r.num, r.name, r.type, r.desc]);
    filename = `${businessName}_COA_${erp}.csv`;
  } else {
    const headers = ["Account Number","Account Name","Account Type","Description"];
    csv = buildCSV(accounts, headers, r => [r.num, r.name, r.type, r.desc]);
    filename = `${businessName}_COA_Generic.csv`;
  }

  const blob = new Blob([csv], { type:"text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------
//  INDUSTRY / ENTITY DATA
// ---------------------------------------------
const INDUSTRY_TREE = {
  "Professional Services":{icon:"⚖️",sub:["Legal (Law Firm / Solo Practice)","Accounting / CPA / Bookkeeping","Management Consulting","Engineering / Architecture","Marketing / PR / Creative Agency","Staffing / HR Consulting","Financial Advisory / Wealth Management","Other Professional Services"]},
  "Technology":{icon:"💻",sub:["SaaS / Subscription Software","Hardware + Software (IoT / Devices)","Custom Development / Dev Agency","IT Managed Services (MSP)","Marketplace / Platform","Cybersecurity","Data / AI / Analytics","Embedded / OEM Technology","Other Technology"]},
  "Retail & E-commerce":{icon:"🛍️",sub:["Brick-and-Mortar Retail","E-commerce (Online Only)","Omnichannel (Online + Physical)","Wholesale / Distribution","Specialty / Boutique Retail","Other Retail"]},
  "Construction & Trades":{icon:"🔧",sub:["General Contractor","Electrical","Plumbing / HVAC","Landscaping / Grounds","Renovation / Remodeling","Civil / Infrastructure","Specialty Trade","Other Construction"]},
  "Food & Beverage":{icon:"🍽️",sub:["Full-Service Restaurant","Quick Service / Fast Casual","Catering / Events","Food Manufacturing / CPG","Brewery / Winery / Distillery","Food Truck / Pop-up","Other Food & Beverage"]},
  "Healthcare & Life Sciences":{icon:"🏥",sub:["Medical Practice / Clinic","Dental Practice","Physical Therapy / Rehab","Mental Health / Behavioral","Pharmacy","Medical Device / Biotech","Home Health / Hospice","Other Healthcare"]},
  "Real Estate":{icon:"🏠",sub:["Residential Brokerage","Commercial Brokerage","Property Management","Real Estate Development","REITs / Investment","Other Real Estate"]},
  "Manufacturing":{icon:"🏭",sub:["Discrete / Batch Manufacturing","Process Manufacturing","Contract Manufacturing","Aerospace / Defense","Consumer Goods","Industrial Equipment","Other Manufacturing"]},
  "Transportation & Logistics":{icon:"🚚",sub:["Freight / Trucking","Last-Mile Delivery","Warehousing / 3PL","Fleet Management","Maritime / Air Cargo","Other Transportation"]},
  "Nonprofit / Education":{icon:"🎓",sub:["501(c)(3) Charitable Org","Foundation / Grant-Making","K-12 School / Charter","Higher Education","Religious Organization","Trade Association / 501(c)(6)","Other Nonprofit"]},
  "Media & Entertainment":{icon:"🎬",sub:["Publishing / News Media","Film / Video Production","Music / Podcasting","Gaming / Interactive","Advertising / Ad Tech","Events / Live Entertainment","Other Media"]},
  "Other / Hybrid":{icon:"🔀",sub:["Multi-industry Holding Company","Early-Stage Startup (Pre-revenue)","Other — I'll describe my business"]},
};

const ENTITY_TYPES = [
  {value:"sole_prop",label:"Sole Proprietorship",sub:"Schedule C filer, no separate entity"},
  {value:"smllc",label:"Single-Member LLC",sub:"Disregarded entity by default"},
  {value:"mmllc",label:"Multi-Member LLC",sub:"Partnership taxation by default"},
  {value:"scorp",label:"S-Corporation",sub:"Pass-through, shareholder restrictions"},
  {value:"ccorp",label:"C-Corporation",sub:"Including VC-backed / Delaware corps"},
  {value:"partnership",label:"Partnership / LP / LLP",sub:"General or limited partnership"},
  {value:"nonprofit",label:"Nonprofit / Tax-Exempt",sub:"501(c)(3), 501(c)(6), etc."},
  {value:"other",label:"Other / Not sure",sub:""},
];

const REVENUE_OPTIONS = [
  {value:"services",label:"Services / Billable Hours",sub:"Time-based or project billing"},
  {value:"products",label:"Product Sales",sub:"Physical goods, inventory"},
  {value:"subscriptions",label:"Subscriptions / Recurring Revenue",sub:"SaaS, memberships, retainers"},
  {value:"contracts",label:"Project-Based Contracts",sub:"Fixed-fee or milestone billing"},
  {value:"commissions",label:"Commissions / Referral Fees",sub:"Brokerage, sales commissions"},
  {value:"rental",label:"Rental / Lease Income",sub:"Property, equipment, IP licensing"},
  {value:"grants",label:"Grants / Donations",sub:"Primarily for nonprofits"},
  {value:"interest",label:"Interest / Investment Income",sub:"Financial income streams"},
  {value:"other",label:"Other Revenue",sub:"Describe below"},
];

const FINANCING_OPTIONS = [
  {value:"bootstrapped",label:"Bootstrapped / Self-Funded",sub:"Owner contributions only"},
  {value:"sba",label:"SBA Loan (7(a) or 504)",sub:"Government-backed term loan"},
  {value:"term_loan",label:"Conventional Term Loan",sub:"Bank or credit union financing"},
  {value:"loc",label:"Revolving Line of Credit",sub:"Drawn and repaid as needed"},
  {value:"equip_finance",label:"Equipment Financing / Capital Lease",sub:"Asset-specific debt"},
  {value:"mortgage",label:"Commercial Real Estate Mortgage",sub:"Property-secured loan"},
  {value:"vc_equity",label:"Venture Capital / Equity Investment",sub:"Priced equity raise (Series A, B, etc.)"},
  {value:"vc_debt",label:"Venture Debt / Growth Capital",sub:"Non-dilutive term debt alongside equity"},
  {value:"convertible",label:"Convertible Notes",sub:"Interest-bearing debt instruments that convert to equity or cash at maturity"},
  {value:"ar_factoring",label:"AR Factoring / Invoice Financing",sub:"Advance against receivables"},
  {value:"shareholder_loan",label:"Shareholder / Related-Party Loans",sub:"Loans from owners or affiliates"},
  {value:"bonds",label:"Bonds / Debentures",sub:"Larger entities, muni bonds"},
  {value:"credit_card",label:"Business Credit Card(s)",sub:"Visa, Amex, Chase Ink — creates credit card payable liability"},
  {value:"deferred_rev",label:"Deferred Revenue (Customer Prepayments)",sub:"Unearned income liability"},
  {value:"other",label:"Other Financing",sub:"Describe below"},
];

const GAAP_FRAMEWORKS = [
  {value:"us_gaap",label:"US GAAP",sub:"FASB ASC — required for US public companies, standard for private entities",recommended:["ccorp","scorp","smllc","mmllc","sole_prop"]},
  {value:"ifrs_full",label:"IFRS (Full)",sub:"IASB — multinational entities, foreign private issuers, 140+ countries",recommended:[]},
  {value:"ifrs_sme",label:"IFRS for SMEs",sub:"IASB simplified standard for smaller international entities",recommended:[]},
  {value:"uk_gaap",label:"UK GAAP (FRS 102)",sub:"Post-Brexit UK standard for non-publicly accountable entities",recommended:[]},
  {value:"swiss_gaap",label:"Swiss GAAP FER",sub:"Foundation for Accounting and Reporting Recommendations",recommended:[]},
  {value:"aspe",label:"Canada — ASPE",sub:"Accounting Standards for Private Enterprises (CPA Canada)",recommended:[]},
  {value:"tax_basis",label:"Tax Basis Accounting",sub:"Income tax basis — common for small businesses, not true GAAP",recommended:["sole_prop","smllc"]},
  {value:"not_sure",label:"Not sure — help me choose",sub:"",recommended:[]},
];

const ACCOUNT_TYPES = ["Bank","Accounts Receivable","Other Current Asset","Fixed Asset","Other Asset","Accounts Payable","Credit Card","Other Current Liability","Long Term Liability","Equity","Retained Earnings","Income","Other Income","Cost of Goods Sold","Expense","Other Expense"];

const ENTITY_STRUCTURE_OPTIONS = [
  {value:"single",       label:"Single entity",                    sub:"One legal entity, no subsidiaries or affiliates"},
  {value:"parent_subs",  label:"Parent with subsidiaries",         sub:"Consolidated group — operating companies under a holdco"},
  {value:"holdco",       label:"Holding company / HoldCo",         sub:"HoldCo with one or more operating entities below"},
  {value:"spv",          label:"SPV / Special Purpose Vehicle(s)",  sub:"Ring-fenced entity for specific assets or projects"},
  {value:"mgmt_co",      label:"Management company structure",      sub:"ManageCo charges fees to one or more OpCos"},
  {value:"jv",           label:"Joint venture / Partnership",       sub:"Shared ownership with another party"},
  {value:"franchise",    label:"Franchise structure",               sub:"Franchisor or franchisee with related-party transactions"},
  {value:"other_multi",  label:"Other multi-entity structure",      sub:"Describe in follow-up"},
];

const CONSOLIDATION_OPTIONS = [
  {value:"standalone",   label:"Standalone only",                   sub:"No consolidated statements required"},
  {value:"consolidated", label:"Consolidated financial statements", sub:"Full consolidation of subsidiaries"},
  {value:"combined",     label:"Combined statements",               sub:"Entities under common control, no formal parent"},
  {value:"equity_method",label:"Equity method investments",         sub:"Significant influence, not control (20-50% ownership)"},
  {value:"not_sure",     label:"Not yet determined",                sub:""},
];

const SECTION_ORDER = ["Business Profile","Entity Structure","Revenue","Cost Structure","Assets & Liabilities","Operations","Accounting Standards","ERP & Export","Reporting Preferences"];

// ---------------------------------------------
//  QUESTION ENGINE
// ---------------------------------------------
function buildQuestions(answers) {
  const qs = [];
  qs.push({id:"industry_primary",section:"Business Profile",headline:"What industry best describes this business?",plain:"Select the primary industry — you'll specify the business type next.",helper:{what:"Your industry shapes the entire structure of your Chart of Accounts. A restaurant needs food cost and labor accounts; a software company needs capitalized development costs and deferred revenue.",example:"A company that builds mobile apps for hospitals might pick 'Technology' or 'Healthcare.' Pick whichever reflects your primary revenue driver.",why:"Your industry selection determines which specialized accounts appear in your COA."},type:"industry_primary"});
  if (answers.industry_primary) {
    qs.push({id:"industry_sub",section:"Business Profile",headline:`What type of ${answers.industry_primary} business is this?`,plain:null,helper:{what:"Many industries have very different accounting needs depending on how the business operates.",example:"A General Contractor needs job-costing and retainage tracking. An Electrical subcontractor needs different COGS accounts.",why:"Your subtype determines which specialized line items appear."},type:"radio",options:(INDUSTRY_TREE[answers.industry_primary]?.sub||[]).map(s=>({value:s,label:s}))});
  }
  qs.push({id:"entity_type",section:"Business Profile",headline:"What is the legal structure of this business?",technical:"Entity Type / Tax Classification",plain:null,helper:{what:"Your legal structure determines how equity accounts are set up. A sole proprietor has 'Owner's Draw' accounts. A C-Corp has 'Common Stock' and 'Additional Paid-In Capital.'",example:"If you filed for an LLC with the state but haven't elected S-Corp taxation, you're likely a Single-Member or Multi-Member LLC.",why:"Entity type shapes your entire equity section and some liability accounts."},type:"radio_sub",options:ENTITY_TYPES});
  // -- ENTITY STRUCTURE --
  qs.push({
    id:"entity_structure",
    section:"Entity Structure",
    headline:"How is this business legally structured?",
    technical:"Entity / Group Structure",
    plain:null,
    helper:{
      what:"Multi-entity structures require intercompany accounts — loans, open balances, management fees, and elimination entries for consolidation. Getting this right at the COA stage avoids significant rework later.",
      example:"A HoldCo that owns an OpCo and an SPV needs intercompany receivable/payable accounts in each entity, plus a consolidation elimination structure. A single standalone entity needs none of this.",
      why:"Entity structure determines whether intercompany, elimination, and consolidation accounts are included."
    },
    type:"radio_sub",
    options:ENTITY_STRUCTURE_OPTIONS
  });

  if(answers.entity_structure && answers.entity_structure !== "single") {
    qs.push({
      id:"num_entities",
      section:"Entity Structure",
      headline:"How many legal entities are in the group?",
      technical:"Group Entity Count",
      plain:null,
      helper:{
        what:"The number of entities affects the intercompany account structure. A two-entity group needs a simple intercompany pair; a larger group may need entity-coded account segments.",
        example:"A HoldCo + 3 OpCos might use accounts like 1800-A, 1800-B, 1800-C for intercompany receivables — one per entity.",
        why:"Entity count determines the depth of intercompany account coding."
      },
      type:"radio",
      options:[
        {value:"two",   label:"2 entities"},
        {value:"three_five", label:"3–5 entities"},
        {value:"six_plus",   label:"6 or more entities"},
      ]
    });

    qs.push({
      id:"interco_types",
      section:"Entity Structure",
      headline:"What types of intercompany transactions occur between entities?",
      technical:"Intercompany Transaction Types",
      plain:null,
      helper:{
        what:"Each intercompany transaction type needs a matched pair of accounts — one in each entity — so they net to zero on consolidation. These are among the most important accounts to get right from the start.",
        example:"An intercompany loan from HoldCo to OpCo requires 'Intercompany Loan Receivable' in HoldCo and 'Intercompany Loan Payable' in OpCo, both with the same balance for elimination.",
        why:"These drive the specific intercompany account set included in the COA."
      },
      type:"multi",
      options:[
        {value:"loans",     label:"Intercompany loans / notes receivable & payable"},
        {value:"open_bal",  label:"Open intercompany balances / current accounts"},
        {value:"mgmt_fees", label:"Management fees or shared services charges"},
        {value:"dividends", label:"Intercompany dividends / distributions"},
        {value:"sales",     label:"Intercompany sales of goods or services"},
        {value:"capex",     label:"Intercompany asset transfers / capital contributions"},
        {value:"royalties", label:"Royalties or IP licensing between entities"},
        {value:"guarantees",label:"Intercompany guarantees or cross-collateralisation"},
      ]
    });

    qs.push({
      id:"consolidation_req",
      section:"Entity Structure",
      headline:"What level of consolidated reporting is required?",
      technical:"Consolidation Requirements",
      plain:null,
      helper:{
        what:"Consolidation requirements determine whether elimination accounts are needed. Full consolidation under ASC 810 or IFRS 10 requires elimination of all intercompany balances and transactions.",
        example:"A VC-backed parent with three subsidiaries preparing GAAP financials for investors needs full consolidation with elimination entries. A founder with two LLCs sharing costs may only need combined statements.",
        why:"Consolidation scope determines the elimination account structure and investment in subsidiary accounts."
      },
      type:"radio_sub",
      options:CONSOLIDATION_OPTIONS
    });
  }

  qs.push({id:"revenue_streams",section:"Revenue",headline:"How does this business generate revenue?",technical:"Revenue Recognition Sources",plain:"Select all applicable revenue recognition sources.",helper:{what:"Each revenue stream typically gets its own account so you can track performance separately.",example:"A consulting firm might have 'Consulting Revenue,' 'Training Revenue,' and 'Software License Revenue' as three separate accounts.",why:"Multiple revenue streams = multiple revenue accounts, giving you better P&L visibility."},type:"multi",options:REVENUE_OPTIONS,hasOther:true});
  qs.push({id:"bill_pay_services",section:"Operations",headline:"Does the business use any third-party bill payment or expense management platforms?",technical:"AP Clearing Liabilities — Bill.com, Ramp, Brex, PayPal",plain:"Select all that apply.",helper:{what:"Platforms like Bill.com and Ramp pay vendor invoices on behalf of the business and invoice the company at month-end. This timing difference creates a clearing liability account.",example:"A company using Bill.com receives a consolidated invoice at month-end. Until paid, the balance sits in Bill.com Clearing Payable.",why:"Without a dedicated clearing account, these platform balances get misclassified and create reconciliation issues."},type:"multi",options:[{value:"bill_com",label:"Bill.com"},{value:"ramp",label:"Ramp"},{value:"brex",label:"Brex"},{value:"paypal",label:"PayPal / PayPal Business"},{value:"melio",label:"Melio"},{value:"other_billpay",label:"Other third-party bill pay or AP platform"},{value:"none_billpay",label:"No — we pay vendors directly from our bank account"}]});
  qs.push({id:"has_cogs",section:"Cost Structure",headline:"Does this business have direct costs tied to delivering its product or service?",technical:"Cost of Goods Sold (COGS) / Cost of Revenue",plain:null,helper:{what:"COGS separates the direct costs of delivery from overhead, producing a Gross Profit line on your P&L.",example:"A web agency's COGS might be freelancer fees and hosting. A manufacturer's COGS includes raw materials and factory labor.",why:"This determines whether your COA includes a COGS section above the operating expense line."},type:"radio",options:[{value:"yes_clear",label:"Yes — we have clear, direct costs (materials, labor, subcontractors)"},{value:"yes_labor",label:"Mostly labor — time-based or service delivery costs"},{value:"minimal",label:"Minimal — primarily overhead, small COGS footprint"},{value:"no",label:"No — pure overhead / operating expenses only"}]});
  qs.push({id:"assets",section:"Assets & Liabilities",headline:"What significant assets does the business own or use?",technical:"Balance Sheet — Asset Classification",plain:"Select all applicable asset classes.",helper:{what:"Assets appear on your Balance Sheet. Fixed assets are depreciated over time. Current assets convert to cash within a year.",example:"A landscaping company owns trucks, equipment, and has fuel inventory. A law firm may only have computers and accounts receivable.",why:"Each asset type generates specific accounts including accumulated depreciation contra-accounts."},type:"multi",options:[{value:"equipment",label:"Equipment / Machinery"},{value:"vehicles",label:"Vehicles"},{value:"real_estate",label:"Real Estate / Property"},{value:"inventory",label:"Inventory"},{value:"technology",label:"Computers / Technology / Software"},{value:"ip",label:"Intellectual Property / Patents / Trademarks"},{value:"investments",label:"Long-Term Investments / Securities"},{value:"none",label:"None of the above — minimal fixed assets"}]});
  qs.push({id:"leases",section:"Assets & Liabilities",headline:"Does the business have any leases — office space, equipment, vehicles, or other assets?",technical:"Lease Classification — ASC 842 / IFRS 16",plain:"Select all that apply.",helper:{what:"Under ASC 842, most leases create a Right-of-Use (ROU) asset and a corresponding lease liability on the balance sheet. Finance leases also create interest and amortization expense.",example:"A startup leasing office space for 3 years needs an Operating Lease ROU Asset, Operating Lease Liability (current and long-term), and an operating lease amortization expense account.",why:"Leases are one of the most commonly missed balance sheet items. Any lease over 12 months must be capitalized under ASC 842."},type:"multi",options:[{value:"operating_office",label:"Operating lease — office or facility space"},{value:"operating_equip",label:"Operating lease — equipment, technology, or vehicles"},{value:"finance_lease",label:"Finance lease (formerly capital lease) — asset-specific"},{value:"short_term",label:"Short-term leases only (month-to-month, under 12 months)"},{value:"none",label:"No leases — business owns its assets or works remotely"}]});
  qs.push({id:"financing",section:"Assets & Liabilities",headline:"How is or has the business been financed?",technical:"Liability & Equity Structure",plain:"Select all applicable financing instruments.",helper:{what:"Each financing type creates specific liability or equity accounts. VC equity creates preferred stock accounts. Convertible notes are debt until conversion.",example:"A bootstrapped LLC only needs basic equity accounts. A VC-backed C-Corp needs preferred stock series, convertible note liabilities, and potentially venture debt accounts.",why:"Your financing structure determines the entire right side of your Balance Sheet."},type:"multi",options:FINANCING_OPTIONS,hasOther:true});
  qs.push({id:"employees",section:"Operations",headline:"Does the business have employees or engage contractors?",technical:"Payroll & Labor Classification",plain:null,helper:{what:"W-2 employees create payroll tax liabilities and may include benefits accounts. 1099 contractors are simpler — typically just an expense account.",example:"A business with 10 employees needs payroll tax payable, accrued wages, benefits expense, and 401(k) liability accounts.",why:"Labor structure determines 6–12 accounts in your operating expenses section."},type:"radio",options:[{value:"employees_only",label:"W-2 Employees only"},{value:"contractors_only",label:"1099 Contractors / Freelancers only"},{value:"both",label:"Both employees and contractors"},{value:"solo",label:"Owner only — no employees or contractors yet"}]});
  qs.push({id:"accounting_method",section:"Accounting Standards",headline:"How does this business track income and expenses?",technical:"Accounting Method — Cash vs. Accrual Basis",plain:null,helper:{what:"Cash basis records transactions when money actually moves. Accrual basis records when they are earned or incurred — even if cash hasn't changed hands.",example:"Cash: Invoice in December, paid in January — record in January. Accrual: Same scenario, record in December when earned.",why:"This affects whether you need Accounts Receivable, Accounts Payable, and accrued liability accounts."},type:"radio",options:[{value:"cash",label:"Cash Basis",sub:"Simpler — record when money moves"},{value:"accrual",label:"Accrual Basis",sub:"More complete — record when earned/incurred"},{value:"help",label:"Help me decide",sub:"Answer two quick questions"}]});
  if (answers.accounting_method==="help") {
    qs.push({id:"accrual_diagnostic_revenue",section:"Accounting Standards",headline:"Does the business regularly invoice clients and wait to be paid?",technical:"Accounts Receivable Diagnostic",plain:"If customers usually pay on the spot (retail, POS), answer No.",helper:{what:"Businesses that invoice and wait for payment almost always benefit from accrual accounting.",example:"A contractor who bills net-30 with $200K in outstanding invoices should use accrual so income appears when earned, not when paid.",why:"This is the single biggest signal for whether you need accrual accounting."},type:"radio",options:[{value:"yes",label:"Yes — we invoice and collect over time"},{value:"no",label:"No — customers pay at point of sale"}]});
  }
  if (answers.accounting_method==="accrual"||answers.accrual_diagnostic_revenue==="yes") {
    qs.push({id:"gaap_framework",section:"Accounting Standards",headline:"Which accounting framework will this COA be built under?",technical:"GAAP Framework / Reporting Standard",plain:null,helper:{what:"Different frameworks have different rules for revenue recognition, lease accounting, and financial statement presentation.",example:"A US-based S-Corp preparing financials for a bank typically uses US GAAP. A startup with international investors considering a foreign IPO might elect IFRS from the start.",why:"The framework affects account names, structure, and which disclosures are required."},type:"radio_sub",options:GAAP_FRAMEWORKS.map(f=>({...f,sub:(f.recommended||[]).includes(answers.entity_type)?`${f.sub}`:f.sub,isRecommended:(f.recommended||[]).includes(answers.entity_type)}))});
  }
  qs.push({id:"target_erp",section:"ERP & Export",headline:"Which accounting system will this Chart of Accounts be imported into?",technical:"Target ERP / Accounting Platform",plain:"Select the target platform — the export file will be formatted to match its import specification.",helper:{what:"Each platform uses distinct field names, account type enumerations, and file structures for COA import. COA Builder maps your output to the correct specification automatically.",example:"QBO requires 'Detail Type' with platform-specific enumerations. Xero uses type codes such as CURRENT, OVERHEADS, and DIRECTCOSTS. Sage 50 uses its own classification set.",why:"Correct field mapping ensures a clean first import with no manual post-processing."},type:"erp"});
  return qs;
}

// ---------------------------------------------
//  COA GENERATOR
// ---------------------------------------------
function generateCOA(answers) {
  const coa = [];
  const isAccrual = answers.accounting_method==="accrual"||answers.accrual_diagnostic_revenue==="yes";
  const entityType = answers.entity_type||"smllc";
  const assets = answers.assets||[];
  const financing = answers.financing||[];
  const revenues = answers.revenue_streams||[];
  const hasInventory = assets.includes("inventory");
  const hasEquipment = assets.includes("equipment")||assets.includes("vehicles");
  const hasRealEstate = assets.includes("real_estate");
  const hasPayroll = ["employees_only","both"].includes(answers.employees);
  const hasContractors = ["contractors_only","both"].includes(answers.employees);
  const hasCOGS = ["yes_clear","yes_labor","minimal"].includes(answers.has_cogs);
  const isConstruction = answers.industry_primary==="Construction & Trades";
  const isSaaS = (answers.industry_sub||"").toLowerCase().includes("saas")||(answers.industry_sub||"").toLowerCase().includes("subscription software");
  const isNonprofit = entityType==="nonprofit"||answers.industry_primary==="Nonprofit / Education";
  const isVC = financing.includes("vc_equity")||financing.includes("convertible")||financing.includes("vc_debt");
  const isMultiEntity = answers.entity_structure && answers.entity_structure !== "single";
  const intercoTypes = answers.interco_types || [];
  const needsConsolidation = ["consolidated","combined"].includes(answers.consolidation_req);

  const push = (section, rows) => { coa.push({section}); rows.forEach(r=>coa.push(r)); };

  // ASSETS - CURRENT
  let currentAssets = [
    {num:"1000",name:"Checking Account",type:"Bank",desc:"Primary operating checking account"},
    {num:"1010",name:"Savings / Money Market",type:"Bank",desc:"Business savings and short-term reserves"},
  ];
  if(isAccrual){currentAssets.push({num:"1100",name:"Accounts Receivable (Trade)",type:"Accounts Receivable",desc:"Amounts owed by customers for delivered goods/services"},{num:"1105",name:"Allowance for Doubtful Accounts",type:"Accounts Receivable",desc:"Contra-asset — estimated uncollectible receivables"});}
  if(isSaaS||revenues.includes("subscriptions")){currentAssets.push({num:"1110",name:"Unbilled Revenue / Accrued Revenue",type:"Other Current Asset",desc:"Revenue earned but not yet invoiced"});}
  if(hasInventory){currentAssets.push({num:"1200",name:"Inventory — Raw Materials",type:"Other Current Asset",desc:"Unprocessed materials awaiting use"},{num:"1210",name:"Inventory — Work in Process (WIP)",type:"Other Current Asset",desc:"Partially completed goods"},{num:"1220",name:"Inventory — Finished Goods",type:"Other Current Asset",desc:"Completed goods available for sale"});}
  if(isConstruction){currentAssets.push({num:"1230",name:"Costs in Excess of Billings (CIE)",type:"Other Current Asset",desc:"Underbilled job costs — construction WIP"},{num:"1240",name:"Retainage Receivable",type:"Other Current Asset",desc:"Amounts withheld by customers pending project completion"});}
  currentAssets.push({num:"1300",name:"Prepaid Expenses",type:"Other Current Asset",desc:"Expenses paid in advance — insurance, subscriptions, rent"},{num:"1310",name:"Undeposited Funds",type:"Other Current Asset",desc:"Payments received, not yet deposited"});
  push("Assets — Current", currentAssets);

  if(hasEquipment||hasRealEstate||assets.includes("technology")){
    let fixedAssets=[];
    if(hasEquipment){fixedAssets.push({num:"1500",name:"Equipment & Machinery",type:"Fixed Asset",desc:"Owned equipment, at cost"},{num:"1505",name:"Accumulated Depreciation — Equipment",type:"Fixed Asset",desc:"Contra-asset — cumulative depreciation on equipment"});}
    if(assets.includes("vehicles")){fixedAssets.push({num:"1510",name:"Vehicles",type:"Fixed Asset",desc:"Owned business vehicles, at cost"},{num:"1515",name:"Accumulated Depreciation — Vehicles",type:"Fixed Asset",desc:"Contra-asset — cumulative depreciation on vehicles"});}
    if(hasRealEstate){fixedAssets.push({num:"1520",name:"Land",type:"Fixed Asset",desc:"Owned land — not depreciated"},{num:"1530",name:"Buildings & Improvements",type:"Fixed Asset",desc:"Owned structures, at cost"},{num:"1535",name:"Accumulated Depreciation — Buildings",type:"Fixed Asset",desc:"Contra-asset — cumulative depreciation on buildings"});}
    if(assets.includes("technology")){fixedAssets.push({num:"1540",name:"Computer Equipment & Technology",type:"Fixed Asset",desc:"Hardware, servers, networking equipment"},{num:"1545",name:"Accumulated Depreciation — Technology",type:"Fixed Asset",desc:"Contra-asset"});}
    if(isSaaS||assets.includes("ip")){fixedAssets.push({num:"1560",name:"Capitalized Software Development Costs",type:"Fixed Asset",desc:"Internally developed software — post-feasibility stage (ASC 350)"},{num:"1565",name:"Accumulated Amortization — Software",type:"Fixed Asset",desc:"Contra-asset — amortization of capitalized software"});}
    push("Assets — Fixed", fixedAssets);
  }

  if(assets.includes("ip")||assets.includes("investments")){
    let otherAssets=[];
    if(assets.includes("ip")){otherAssets.push({num:"1700",name:"Intangible Assets — Patents & Trademarks",type:"Other Asset",desc:"Intellectual property, at cost less amortization"});}
    if(assets.includes("investments")){otherAssets.push({num:"1750",name:"Long-Term Investments",type:"Other Asset",desc:"Securities or equity stakes held long-term"});}
    push("Assets — Other Long-Term", otherAssets);
  }

  // LIABILITIES
  let currLiab=[];
  if(isAccrual){currLiab.push({num:"2000",name:"Accounts Payable (Trade)",type:"Accounts Payable",desc:"Amounts owed to vendors and suppliers"});}
  if(financing.includes("loc")){currLiab.push({num:"2010",name:"Line of Credit — Current Balance",type:"Other Current Liability",desc:"Outstanding balance on revolving credit facility"});}
  if(revenues.includes("subscriptions")||revenues.includes("contracts")||financing.includes("deferred_rev")){currLiab.push({num:"2030",name:"Deferred Revenue — Current",type:"Other Current Liability",desc:"Customer prepayments for undelivered goods/services"});}
  currLiab.push({num:"2040",name:"Accrued Liabilities",type:"Other Current Liability",desc:"Expenses incurred but not yet billed or paid"},{num:"2050",name:"Accrued Interest Payable",type:"Other Current Liability",desc:"Interest accrued but not yet paid"});
  if(hasPayroll){currLiab.push({num:"2060",name:"Accrued Wages & Salaries",type:"Other Current Liability",desc:"Wages earned by employees, not yet paid"},{num:"2065",name:"Payroll Taxes Payable",type:"Other Current Liability",desc:"FICA, FUTA, SUTA withheld and employer match"},{num:"2070",name:"Employee Benefits Payable",type:"Other Current Liability",desc:"Health insurance, 401(k) match accruals"});}
  if(financing.includes("convertible")){currLiab.push({num:"2080",name:"Convertible Notes Payable — Current",type:"Other Current Liability",desc:"Convertible debt instruments, current portion"});}
  if(isConstruction){currLiab.push({num:"2090",name:"Billings in Excess of Costs (BIE)",type:"Other Current Liability",desc:"Overbilled amounts — construction deferred revenue"},{num:"2095",name:"Retainage Payable",type:"Other Current Liability",desc:"Amounts withheld from subcontractors"});}
  push("Liabilities — Current", currLiab);

  const hasLTDebt=financing.some(f=>["sba","term_loan","mortgage","equip_finance","vc_debt","bonds","shareholder_loan"].includes(f));
  if(hasLTDebt){
    let ltLiab=[];
    if(financing.includes("sba")){ltLiab.push({num:"2200",name:"SBA Loan Payable — Long-Term",type:"Long Term Liability",desc:"SBA 7(a) or 504 loan, non-current portion"});}
    if(financing.includes("term_loan")){ltLiab.push({num:"2210",name:"Term Loan Payable — Long-Term",type:"Long Term Liability",desc:"Conventional bank term debt, non-current portion"});}
    if(financing.includes("mortgage")){ltLiab.push({num:"2220",name:"Mortgage Payable — Long-Term",type:"Long Term Liability",desc:"Commercial real estate debt, non-current portion"});}
    if(financing.includes("equip_finance")){ltLiab.push({num:"2230",name:"Equipment Financing Payable",type:"Long Term Liability",desc:"Capital lease or equipment loan obligations"});}
    if(financing.includes("vc_debt")){ltLiab.push({num:"2240",name:"Venture Debt / Growth Capital",type:"Long Term Liability",desc:"Non-dilutive term debt from venture lending"});}
    if(financing.includes("shareholder_loan")){ltLiab.push({num:"2250",name:"Shareholder / Related-Party Loans",type:"Long Term Liability",desc:"Loans from owners or affiliated entities"});}
    if(financing.includes("bonds")){ltLiab.push({num:"2260",name:"Bonds / Debentures Payable",type:"Long Term Liability",desc:"Long-term bond obligations"});}
    push("Liabilities — Long-Term", ltLiab);
  }

  // EQUITY
  let equity=[];
  if(entityType==="sole_prop"){equity.push({num:"3000",name:"Owner's Capital",type:"Equity",desc:"Cumulative owner contributions"},{num:"3010",name:"Owner's Draw",type:"Equity",desc:"Withdrawals by owner — reduces equity"});}
  else if(entityType==="smllc"){equity.push({num:"3000",name:"Member Capital — Contributions",type:"Equity",desc:"Capital contributions by LLC member"},{num:"3010",name:"Member Distributions",type:"Equity",desc:"Distributions to LLC member"});}
  else if(entityType==="mmllc"){equity.push({num:"3000",name:"Member Capital — Contributions",type:"Equity",desc:"Capital contributions by LLC members"},{num:"3010",name:"Member Distributions",type:"Equity",desc:"Distributions to LLC members"},{num:"3020",name:"Member Capital — [Member 2]",type:"Equity",desc:"Capital account for additional member — rename as needed"});}
  else if(entityType==="scorp"){equity.push({num:"3000",name:"Common Stock",type:"Equity",desc:"Par value of issued common shares"},{num:"3010",name:"Additional Paid-In Capital (APIC)",type:"Equity",desc:"Capital in excess of par value"},{num:"3020",name:"Shareholder Distributions",type:"Equity",desc:"S-Corp distributions to shareholders"});}
  else if(entityType==="ccorp"){equity.push({num:"3000",name:"Common Stock ($.001 par)",type:"Equity",desc:"Authorized and issued common shares at par"},{num:"3010",name:"Additional Paid-In Capital — Common",type:"Equity",desc:"Premium paid above par for common stock"});if(isVC){equity.push({num:"3020",name:"Preferred Stock — Series Seed",type:"Equity",desc:"Series Seed preferred shares at stated value"},{num:"3025",name:"Additional Paid-In Capital — Preferred",type:"Equity",desc:"Premium on preferred stock issuances"},{num:"3030",name:"Preferred Stock — Series A",type:"Equity",desc:"Series A preferred — rename per cap table"});}}
  else if(entityType==="nonprofit"){equity.push({num:"3000",name:"Net Assets Without Donor Restrictions",type:"Equity",desc:"Unrestricted net assets"},{num:"3010",name:"Net Assets With Donor Restrictions",type:"Equity",desc:"Donor-restricted funds"});}
  else if(entityType==="partnership"){equity.push({num:"3000",name:"Partner Capital — General Partner",type:"Equity",desc:"GP capital account"},{num:"3010",name:"Partner Capital — Limited Partners",type:"Equity",desc:"Aggregate LP capital accounts"},{num:"3020",name:"Partner Distributions",type:"Equity",desc:"Distributions to partners"});}
  else{equity.push({num:"3000",name:"Owner's / Member's Equity",type:"Equity",desc:"Capital contributions"},{num:"3010",name:"Owner's Draw / Distributions",type:"Equity",desc:"Owner withdrawals or distributions"});}
  equity.push({num:"3900",name:"Retained Earnings",type:"Retained Earnings",desc:"Cumulative net income (loss) since inception"});
  push("Equity", equity);

  // INTERCOMPANY (multi-entity structures only)
  if(isMultiEntity) {
    let interco = [];
    if(intercoTypes.includes("loans")||intercoTypes.includes("open_bal")) {
      interco.push({num:"1800",name:"Intercompany Receivable — Current",type:"Other Current Asset",desc:"Amounts owed by related entities — current portion"});
      interco.push({num:"1810",name:"Intercompany Loans Receivable — Long-Term",type:"Other Asset",desc:"Long-term intercompany loan balances receivable"});
    }
    if(intercoTypes.includes("capex")) {
      interco.push({num:"1820",name:"Investment in Subsidiaries",type:"Other Asset",desc:"Carrying value of equity interests in subsidiaries"});
    }
    if(intercoTypes.includes("loans")||intercoTypes.includes("open_bal")) {
      interco.push({num:"2300",name:"Intercompany Payable — Current",type:"Other Current Liability",desc:"Amounts owed to related entities — current portion"});
      interco.push({num:"2310",name:"Intercompany Loans Payable — Long-Term",type:"Long Term Liability",desc:"Long-term intercompany loan balances payable"});
    }
    if(intercoTypes.includes("mgmt_fees")) {
      interco.push({num:"4850",name:"Management Fee Revenue — Intercompany",type:"Income",desc:"Management fees charged to affiliated entities"});
      interco.push({num:"6900",name:"Management Fee Expense — Intercompany",type:"Expense",desc:"Management fees paid to affiliated entities"});
    }
    if(intercoTypes.includes("sales")) {
      interco.push({num:"4860",name:"Intercompany Sales Revenue",type:"Income",desc:"Revenue from sales to related entities — eliminated on consolidation"});
      interco.push({num:"5900",name:"Intercompany Cost of Sales",type:"Cost of Goods Sold",desc:"COGS on intercompany sales — eliminated on consolidation"});
    }
    if(intercoTypes.includes("royalties")) {
      interco.push({num:"4870",name:"Intercompany Royalty Revenue",type:"Income",desc:"Royalties received from affiliated entities"});
      interco.push({num:"6910",name:"Intercompany Royalty Expense",type:"Expense",desc:"Royalties paid to affiliated entities"});
    }
    if(intercoTypes.includes("dividends")) {
      interco.push({num:"4880",name:"Intercompany Dividend Income",type:"Other Income",desc:"Dividends received from subsidiaries"});
    }
    if(needsConsolidation) {
      interco.push({num:"3800",name:"Investment in Subsidiaries — Elimination",type:"Equity",desc:"Consolidation elimination — investment vs. equity of subsidiary"});
      interco.push({num:"3810",name:"Intercompany Profit Elimination",type:"Equity",desc:"Elimination of unrealised intercompany profit in inventory/assets"});
    }
    if(interco.length) push("Intercompany & Related Party", interco);
  }

  // REVENUE
  let rev=[];
  if(revenues.includes("services")){rev.push({num:"4000",name:"Service Revenue",type:"Income",desc:"Revenue from primary service delivery"},{num:"4005",name:"Professional Fees Revenue",type:"Income",desc:"Consulting, advisory, or professional engagement fees"});}
  if(revenues.includes("products")){rev.push({num:"4010",name:"Product Sales Revenue",type:"Income",desc:"Revenue from sale of tangible goods"},{num:"4015",name:"Sales Returns & Allowances",type:"Income",desc:"Contra-revenue — customer returns and credits"},{num:"4016",name:"Sales Discounts",type:"Income",desc:"Contra-revenue — early payment and volume discounts"});}
  if(revenues.includes("subscriptions")||isSaaS){rev.push({num:"4020",name:"Subscription / Recurring Revenue (MRR)",type:"Income",desc:"Monthly and annual recurring subscription fees"},{num:"4025",name:"Usage-Based / Overage Revenue",type:"Income",desc:"Variable fees above contracted limits"});}
  if(revenues.includes("contracts")){rev.push({num:"4030",name:"Contract Revenue — Fixed Fee",type:"Income",desc:"Project-based fixed-fee billings"},{num:"4035",name:"Contract Revenue — Time & Materials",type:"Income",desc:"T&M project billings"});}
  if(revenues.includes("commissions")){rev.push({num:"4040",name:"Commission Revenue",type:"Income",desc:"Sales commissions and referral fees earned"});}
  if(revenues.includes("rental")){rev.push({num:"4050",name:"Rental / Lease Income",type:"Income",desc:"Income from property, equipment, or IP licensing"});}
  if(revenues.includes("grants")||isNonprofit){rev.push({num:"4060",name:"Grant Revenue — Government",type:"Income",desc:"Federal, state, and local government grants"},{num:"4065",name:"Grant Revenue — Private Foundation",type:"Income",desc:"Private and corporate foundation grants"},{num:"4070",name:"Contribution / Donation Revenue",type:"Income",desc:"Individual donor contributions"});}
  if(revenues.includes("interest")){rev.push({num:"4080",name:"Interest Income",type:"Other Income",desc:"Interest earned on cash and investments"});}
  rev.push({num:"4900",name:"Other Revenue",type:"Other Income",desc:"Miscellaneous income not classified above"});
  push("Revenue", rev);

  // COGS
  if(hasCOGS){
    let cogs=[];
    if(answers.has_cogs==="yes_clear"){cogs.push({num:"5000",name:"Cost of Goods Sold — Materials",type:"Cost of Goods Sold",desc:"Direct material costs for products sold"},{num:"5010",name:"Cost of Goods Sold — Direct Labor",type:"Cost of Goods Sold",desc:"Labor directly attributable to production"},{num:"5020",name:"Cost of Goods Sold — Subcontractors",type:"Cost of Goods Sold",desc:"Third-party labor on client deliverables"},{num:"5030",name:"Freight & Shipping — Inbound",type:"Cost of Goods Sold",desc:"Inbound freight on purchased materials"},{num:"5040",name:"Manufacturing Overhead",type:"Cost of Goods Sold",desc:"Allocated overhead for production facilities"});}
    else if(answers.has_cogs==="yes_labor"){cogs.push({num:"5000",name:"Cost of Revenue — Direct Labor",type:"Cost of Goods Sold",desc:"Labor hours directly delivering billable services"},{num:"5010",name:"Cost of Revenue — Subcontractors",type:"Cost of Goods Sold",desc:"Contracted delivery labor"},{num:"5020",name:"Cost of Revenue — Direct Materials",type:"Cost of Goods Sold",desc:"Materials consumed in service delivery"});}
    else{cogs.push({num:"5000",name:"Cost of Revenue",type:"Cost of Goods Sold",desc:"Direct costs attributable to revenue delivery"});}
    if(isConstruction){cogs.push({num:"5050",name:"Job Costs — Labor",type:"Cost of Goods Sold",desc:"Field labor allocated by job"},{num:"5060",name:"Job Costs — Materials",type:"Cost of Goods Sold",desc:"Materials allocated by job"},{num:"5070",name:"Job Costs — Subcontractors",type:"Cost of Goods Sold",desc:"Sub-trade costs allocated by job"},{num:"5080",name:"Job Costs — Equipment",type:"Cost of Goods Sold",desc:"Equipment rental/usage by job"});}
    if(isSaaS){cogs.push({num:"5100",name:"Hosting & Infrastructure Costs",type:"Cost of Goods Sold",desc:"AWS, Azure, GCP — cloud infrastructure supporting product"},{num:"5110",name:"Third-Party API & Licensing Costs",type:"Cost of Goods Sold",desc:"External APIs and licensed components in product"});}
    push("Cost of Goods Sold / Cost of Revenue", cogs);
  }

  // OPEX
  let comp=[];
  if(hasPayroll){comp.push({num:"6000",name:"Salaries & Wages — Regular",type:"Expense",desc:"Base compensation for W-2 employees"},{num:"6005",name:"Salaries & Wages — Overtime",type:"Expense",desc:"Overtime premium pay"},{num:"6010",name:"Payroll Tax Expense — Employer Portion",type:"Expense",desc:"FICA (Social Security + Medicare), FUTA, SUTA"},{num:"6015",name:"Employee Benefits — Health Insurance",type:"Expense",desc:"Employer-paid health, dental, and vision premiums"},{num:"6020",name:"Employee Benefits — Retirement (401k)",type:"Expense",desc:"Employer matching contributions to retirement plans"},{num:"6025",name:"Employee Benefits — Other",type:"Expense",desc:"Life insurance, disability, FSA/HSA contributions"});}
  if(hasContractors){comp.push({num:"6040",name:"Contract Labor / Freelancer Fees",type:"Expense",desc:"1099 contractor and freelancer payments"});}
  if(isVC){comp.push({num:"6050",name:"Stock-Based Compensation Expense",type:"Expense",desc:"Fair value of equity awards expensed over vesting period (ASC 718)"});}
  if(comp.length) push("Operating Expenses — Compensation", comp);

  push("Operating Expenses — Facilities & Infrastructure",[
    {num:"6100",name:"Rent & Occupancy",type:"Expense",desc:"Office, warehouse, or facility lease payments"},
    {num:"6105",name:"Utilities",type:"Expense",desc:"Electricity, gas, water, telecommunications"},
    {num:"6110",name:"Repairs & Maintenance",type:"Expense",desc:"Upkeep of owned or leased facilities and equipment"},
    ...(isAccrual?[{num:"6115",name:"Right-of-Use Asset Amortization",type:"Expense",desc:"Operating lease ROU asset amortization (ASC 842)"}]:[]),
  ]);

  let ga=[
    {num:"6200",name:"Professional Services — Legal",type:"Expense",desc:"Attorney fees, legal counsel"},
    {num:"6205",name:"Professional Services — Accounting & Audit",type:"Expense",desc:"CPA, bookkeeping, and audit fees"},
    {num:"6210",name:"Professional Services — Consulting",type:"Expense",desc:"Management and strategy consulting"},
    {num:"6220",name:"Insurance — General Liability",type:"Expense",desc:"Business general liability premiums"},
    {num:"6225",name:"Insurance — Property",type:"Expense",desc:"Commercial property insurance"},
  ];
  if(hasPayroll){ga.push({num:"6230",name:"Insurance — Workers Compensation",type:"Expense",desc:"Workers comp premiums"});}
  ga.push({num:"6240",name:"Office Supplies & Expenses",type:"Expense",desc:"Consumable supplies and minor office items"},{num:"6250",name:"Postage & Shipping",type:"Expense",desc:"Outbound shipping and courier costs"},{num:"6260",name:"Bank & Merchant Processing Fees",type:"Expense",desc:"Credit card processing, wire fees, bank charges"},{num:"6270",name:"Subscriptions & Software (SaaS Tools)",type:"Expense",desc:"Business software subscriptions not capitalized"},{num:"6280",name:"Licenses & Permits",type:"Expense",desc:"Business licenses, regulatory permits"},{num:"6290",name:"Taxes — Property & Local",type:"Expense",desc:"Property taxes, business personal property taxes"});
  push("Operating Expenses — General & Administrative", ga);

  push("Operating Expenses — Sales & Marketing",[
    {num:"6400",name:"Advertising & Digital Marketing",type:"Expense",desc:"Paid advertising, SEM, social media campaigns"},
    {num:"6405",name:"Marketing Materials & Content",type:"Expense",desc:"Creative production, print, collateral"},
    {num:"6410",name:"Trade Shows & Events",type:"Expense",desc:"Conference fees, booth costs, event sponsorships"},
    {num:"6415",name:"Public Relations",type:"Expense",desc:"PR agency and media relations costs"},
    {num:"6420",name:"Sales Commissions — Internal",type:"Expense",desc:"Commissions paid to employee salespeople"},
    {num:"6425",name:"Sales Commissions — External",type:"Expense",desc:"Channel partner and referral commissions"},
  ]);

  push("Operating Expenses — Travel & Entertainment",[
    {num:"6500",name:"Travel — Airfare & Lodging",type:"Expense",desc:"Business travel transportation and accommodation"},
    {num:"6505",name:"Travel — Ground Transportation",type:"Expense",desc:"Rental cars, taxis, rideshare"},
    {num:"6510",name:"Meals & Entertainment — Business",type:"Expense",desc:"Client entertainment and business meals (50% deductible)"},
    {num:"6515",name:"Meals — Employee (Internal)",type:"Expense",desc:"Team meals, working lunches"},
  ]);

  if(hasEquipment){
    let da=[{num:"6600",name:"Depreciation Expense — Equipment",type:"Expense",desc:"Periodic depreciation on owned equipment"}];
    if(assets.includes("vehicles")){da.push({num:"6605",name:"Depreciation Expense — Vehicles",type:"Expense",desc:"Periodic depreciation on business vehicles"});}
    if(hasRealEstate){da.push({num:"6610",name:"Depreciation Expense — Buildings",type:"Expense",desc:"Straight-line depreciation on owned structures"});}
    if(assets.includes("ip")||isSaaS){da.push({num:"6620",name:"Amortization — Intangibles & Software",type:"Expense",desc:"Amortization of capitalized intangible assets"});}
    push("Operating Expenses — Depreciation & Amortization", da);
  }

  push("Other Income & Expense",[
    {num:"7000",name:"Interest Expense",type:"Other Expense",desc:"Interest on debt obligations"},
    {num:"7010",name:"Gain on Sale of Assets",type:"Other Income",desc:"Proceeds above book value on asset disposals"},
    {num:"7020",name:"Loss on Sale / Disposal of Assets",type:"Other Expense",desc:"Book value in excess of proceeds on asset disposals"},
    ...(answers.accounting_method!=="cash"?[{num:"7030",name:"Foreign Currency Transaction Gain / Loss",type:"Other Income",desc:"FX gains and losses on foreign-denominated transactions"}]:[]),
    ...(!isNonprofit?[{num:"7900",name:"Income Tax Expense",type:"Other Expense",desc:"Federal and state income taxes"}]:[]),
  ]);

  return coa;
}

// ---------------------------------------------
//  SMALL COMPONENTS
// ---------------------------------------------
function HelperPanel({helper}) {
  return (
    <div className="helper-panel">
      <div className="helper-panel-title">Technical Detail</div>
      <p>{helper.what}</p>
      {helper.example&&<div className="helper-example">Example: {helper.example}</div>}
      {helper.why&&<div className="helper-why">{helper.why}</div>}
    </div>
  );
}

function ConfirmModal({title,body,confirmLabel,danger,onConfirm,onCancel}) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>
        <div className="modal-title">{title}</div>
        <div className="modal-body">{body}</div>
        <div className="modal-actions">
          <button className="btn-modal-cancel" onClick={onCancel}>Cancel</button>
          <button className={`btn-modal-confirm${danger?" danger":""}`} onClick={onConfirm}>{confirmLabel||"Confirm"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------
//  LANDING CAROUSEL
// ---------------------------------------------
function LandingPreviewCarousel() {
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const prev = INDUSTRY_PREVIEWS;

  const go = (dir) => {
    setFading(true);
    setTimeout(() => {
      setIdx(i => (i + dir + prev.length) % prev.length);
      setFading(false);
    }, 200);
  };

  const current = prev[idx];
  return (
    <div className="preview-carousel">
      <button className="carousel-arrow" onClick={() => go(-1)}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      <div style={{flex:1}}>
        <div className="preview-card" style={{opacity: fading ? 0 : 1, transition:"opacity 0.2s"}}>
          <div className="preview-card-header">
            <strong>Sample Chart of Accounts</strong>
            <span className="preview-industry-badge">{current.industry}</span>
          </div>
          {current.sections.map(sec => (
            <div className="preview-section" key={sec.label}>
              <div className="preview-section-title">{sec.label}</div>
              {sec.rows.map(([num, name]) => (
                <div className="preview-row" key={num}>
                  <span>{name}</span>
                  <span>{num}</span>
                </div>
              ))}
            </div>
          ))}
          <div className="preview-more">+ accounts tailored to your specific business</div>
        </div>
        <div className="preview-dots" style={{marginTop:12}}>
          {prev.map((_, i) => (
            <div key={i} className={`preview-dot${i===idx?" active":""}`} onClick={() => { setFading(true); setTimeout(()=>{setIdx(i);setFading(false)},200); }} />
          ))}
        </div>
      </div>
      <button className="carousel-arrow" onClick={() => go(1)}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
    </div>
  );
}

// ---------------------------------------------
//  SCREENS
// ---------------------------------------------
function LandingScreen({onStart}) {
  return (
    <div className="landing">
      <div className="landing-left">
        <div className="landing-eyebrow">For CPAs, Controllers &amp; Financial Professionals</div>
        <h1 className="landing-headline">Custom COAs,<br/><em>in minutes.</em><br/>Export-ready.</h1>
        <p className="landing-sub">Build a new Chart of Accounts from scratch, or upload an existing one for a structured audit — complete with GAAP-aligned recommendations, inline fixes, and ERP-formatted export.</p>
        <button className="btn-cta" onClick={onStart}>
          Build a Chart of Accounts
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <div className="landing-features">
          {[
            ["Adaptive Interview Engine","Branches dynamically by industry, entity type, and financing structure."],
            ["GAAP-Aligned Output","Industry-standard account naming — US GAAP, IFRS, Swiss GAAP FER, and more."],
            ["ERP-Formatted Export","Correct field mapping for QBO, Xero, Sage 50, and others — no manual cleanup."],
            ["Full Editorial Control","Inline edit, rename, flag, delete, or add accounts before export."],
          ].map(([label,desc],i) => i===1 ? (
            <div className="feature-item" key="guidance">
              <div className="feature-label">GAAP-Aligned Output</div>
              <div className="feature-desc">Industry-standard account naming — US GAAP, IFRS, Swiss GAAP FER, and more.</div>
            </div>
          ) : (
            <div className="feature-item" key={label}>
              <div className="feature-label">{label}</div>
              <div className="feature-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="landing-right">
        <LandingPreviewCarousel/>
        <div className="landing-bg-word">COA</div>
      </div>
    </div>
  );
}

function ModeSelectScreen({onProceed, appFlow="build"}) {
  const [mode, setMode] = useState(null);
  const [data, setData] = useState({businessName:"",preparerName:"",firmName:""});
  const canProceed = mode && data.businessName.trim().length > 1;
  return (
    <div className="centered-screen">
      <div className="screen-eyebrow">Step 1 of 2</div>
      <h2 className="screen-headline">Select your engagement type.</h2>
      <p className="screen-sub">{appFlow==="audit"?"Who are you auditing this COA for? This frames the analysis and output.":"COA Builder is built for financial professionals. Select how you're working on this engagement."}</p>
      <div className="mode-cards">
        {[{key:"self",icon:"🏠",title:"In-House / Internal",desc:"I'm a Controller, CFO, or internal accounting professional building or refreshing our company's Chart of Accounts."},{key:"agency",icon:"📏",title:"Client Engagement",desc:"I'm a CPA, bookkeeper, or fractional CFO preparing a Chart of Accounts as a client deliverable."}].map(c=>(
          <button key={c.key} className={`mode-card${mode===c.key?" selected":""}`} onClick={()=>setMode(c.key)}>
            <div className="mode-card-check">✔</div>
            <span className="mode-icon">{c.icon}</span>
            <div className="mode-card-title">{c.title}</div>
            <div className="mode-card-desc">{c.desc}</div>
          </button>
        ))}
      </div>
      {mode&&(
        <div className="entry-form">
          <div className="entry-form-title">{mode==="self"?"Entity Details":"Client & Preparer Details"}</div>
          <div className="field-group">
            <div className="field">
              <label>{mode==="self"?"Business Name":"Client Business Name"}</label>
              <input type="text" placeholder={mode==="self"?"e.g. Greenfield Consulting LLC":"Client entity name"} value={data.businessName} onChange={e=>setData(d=>({...d,businessName:e.target.value}))}/>
            </div>
            {mode==="agency"&&(
              <div className="field-row">
                <div className="field"><label>Preparer Name</label><input type="text" placeholder="Your name" value={data.preparerName} onChange={e=>setData(d=>({...d,preparerName:e.target.value}))}/></div>
                <div className="field"><label>Firm / Agency</label><input type="text" placeholder="Firm name" value={data.firmName} onChange={e=>setData(d=>({...d,firmName:e.target.value}))}/></div>
              </div>
            )}
          </div>
          <button className="btn-primary" disabled={!canProceed} onClick={()=>onProceed({...data,mode})}>{appFlow==="audit"?"Continue to Upload *":"Begin Interview *"}</button>
        </div>
      )}
    </div>
  );
}

function QuestionCard({q, answers, onAnswer, uncertainFlags, onUncertain, otherTexts, onOtherText}) {
  const [showHelper, setShowHelper] = useState(false);
  const answer = answers[q.id];

  const renderOptions = () => {
    if(q.type==="industry_primary") return (
      <div className="options-grid">
        {Object.entries(INDUSTRY_TREE).map(([name,{icon}])=>(
          <button key={name} className={`opt-btn${answer===name?" selected":""}`} onClick={()=>onAnswer(q.id,name)}>
            <span style={{fontSize:"1.1rem"}}>{icon}</span>
            <span className="opt-label">{name}</span>
            <div className="opt-indicator"/>
          </button>
        ))}
      </div>
    );
    if(q.type==="erp") return (
      <div className="erp-grid">
        {ERP_OPTIONS.map(e=>(
          <div key={e.value} className={`erp-card${answer===e.value?" selected":""}`} onClick={()=>onAnswer(q.id,e.value)}>
            <div className="erp-logo">{e.logo}</div>
            <div className="erp-name">{e.name}</div>
            <div className="erp-tier">{e.tier}</div>
          </div>
        ))}
      </div>
    );
    if(q.type==="radio"||q.type==="radio_sub") return (
      <div className="options-list">
        {q.options.map(opt=>(
          <button key={opt.value} className={`opt-btn${answer===opt.value?" selected":""}${opt.isRecommended?" recommended":""}`} onClick={()=>onAnswer(q.id,opt.value)} style={{position:"relative"}}>
            {opt.isRecommended&&<span className="recommended-badge">★ Recommended</span>}
            <div className="opt-indicator"/>
            <span className="opt-label">{opt.label}{opt.sub&&<span className="opt-sublabel">{opt.sub}</span>}</span>
          </button>
        ))}
      </div>
    );
    if(q.type==="multi") {
      const cur = answer||[];
      const toggle = val => onAnswer(q.id, cur.includes(val)?cur.filter(x=>x!==val):[...cur,val]);
      return (
        <div className="options-list">
          {q.options.map(opt=>(
            <button key={opt.value} className={`opt-btn${cur.includes(opt.value)?" selected":""}`} onClick={()=>toggle(opt.value)}>
              <div className="opt-indicator square">{cur.includes(opt.value)&&<span className="opt-check">✔</span>}</div>
              <span className="opt-label">{opt.label}{opt.sub&&<span className="opt-sublabel">{opt.sub}</span>}</span>
            </button>
          ))}
          {q.hasOther&&cur.includes("other")&&(
            <input className="opt-other-input" placeholder="Describe your other option…" value={otherTexts[q.id]||""} onChange={e=>onOtherText(q.id,e.target.value)}/>
          )}
        </div>
      );
    }
  };

  const canFlag = q.type !== "erp";
  return (
    <div className="q-card">
      <div className="q-breadcrumb">{q.section}</div>
      <div className="q-headline">{q.headline}</div>
      {q.technical&&<div className="q-technical">Technical term: {q.technical}</div>}
      {q.helper&&(
        <button className="q-helper-toggle" onClick={()=>setShowHelper(v=>!v)} title={showHelper?"Hide detail":"Show detail"}>
          <span className="q-helper-icon">{showHelper?"✕":"?"}</span>
          {showHelper?"Hide detail":""}
        </button>
      )}
      {showHelper&&q.helper&&<HelperPanel helper={q.helper}/>}
      {!showHelper&&q.plain&&!q.technical&&<div className="q-plain">{q.plain}</div>}
      {renderOptions()}
      {canFlag&&(
        <button className={`unsure-btn${uncertainFlags[q.id]?" active":""}`} onClick={()=>onUncertain(q.id)}>
          <span>👤</span>
          {uncertainFlags[q.id]?"Marked uncertain — flagged for review":"I'm not sure about this one — flag for review"}
        </button>
      )}
      {uncertainFlags[q.id]&&<div className="uncertain-note">⚠ This answer will be highlighted on the review screen.</div>}
    </div>
  );
}

function InterviewScreen({session, initialData, onComplete, onGoToReview, startAtQuestion}) {
  const [answers, setAnswers] = useState(initialData?.answers||{});
  const [uncertainFlags, setUncertainFlags] = useState(initialData?.uncertainFlags||{});
  const [otherTexts, setOtherTexts] = useState(initialData?.otherTexts||{});
  const [currentIdx, setCurrentIdx] = useState(()=>{
    if(!startAtQuestion) return 0;
    // Find the index of the target question in the initial question list
    const qs = buildQuestions(initialData?.answers||{});
    const idx = qs.findIndex(q=>q.id===startAtQuestion);
    return idx >= 0 ? idx : 0;
  });

  // Derive synchronously every render — the only safe approach when list shape
  // depends on answers. No useEffect, no stale state gap, no timing window.
  const builtQuestions = buildQuestions(answers);
  // Clamp defensively so index never exceeds current list length.
  const safeIdx = Math.min(currentIdx, Math.max(builtQuestions.length - 1, 0));
  const q = builtQuestions[safeIdx] || null;

  const answer = q ? answers[q.id] : undefined;
  // Can advance if a real answer exists OR if the question has been flagged as uncertain
  const canAdvance = uncertainFlags[q?.id] || (answer!==undefined&&answer!==null&&(Array.isArray(answer)?answer.length>0:true));
  const progress = Math.round((safeIdx/Math.max(builtQuestions.length,1))*100);
  const isFinal = safeIdx>=builtQuestions.length-1;

  const sectionStatus = (sec) => {
    const sqs = builtQuestions.filter(x=>x.section===sec);
    if(!sqs.length) return "locked";
    const allDone = sqs.every(x=>answers[x.id]!==undefined);
    const isActive = sqs.some(x=>builtQuestions.indexOf(x)===safeIdx);
    if(isActive) return "active";
    if(allDone) return "done";
    const firstIdx = builtQuestions.indexOf(sqs[0]);
    return firstIdx<=safeIdx?"active":"locked";
  };

  function handleAnswer(id, val) {
    setAnswers(prev => ({...prev, [id]: val}));
  }
  function handleNext(){if(safeIdx<builtQuestions.length-1){setCurrentIdx(safeIdx+1);}else{onComplete({answers,uncertainFlags,otherTexts});}}
  function handleBack(){if(safeIdx>0)setCurrentIdx(safeIdx-1);}
  function jumpToSection(sec){const firstIdx=builtQuestions.findIndex(x=>x.section===sec);if(firstIdx!==-1&&firstIdx<=safeIdx)setCurrentIdx(firstIdx);}

  return (
    <div className="interview-wrap">
      <div className="sidebar">
        <div className="sidebar-for">Building for</div>
        <div className="sidebar-name">{session.businessName}</div>
        <div className="sidebar-mode">{session.mode==="agency"?`via ${session.firmName||"Agency"}`:"Direct"}</div>
        <div className="sidebar-nav">
          {SECTION_ORDER.map(sec=>{
            const status=sectionStatus(sec);
            return(
              <button key={sec} className={`sidebar-section-btn${status==="active"?" active":""}${status==="done"?" done":""}`} disabled={status==="locked"} onClick={()=>jumpToSection(sec)}>
                <div className="sec-indicator"/>
                {sec}
              </button>
            );
          })}
        </div>
        <div className="sidebar-footer">
          <div className="progress-meta"><span>Progress</span><span>{safeIdx+1} / {builtQuestions.length}</span></div>
          <div className="progress-track"><div className="progress-fill" style={{width:`${progress}%`}}/></div>
          {Object.keys(answers).length>2&&(
            <button className="sidebar-review-btn" onClick={()=>onGoToReview({answers,uncertainFlags,otherTexts})}>📏 Review Answers</button>
          )}
        </div>
      </div>
      <div className="q-area">
        {q&&(
          <>
            <QuestionCard q={q} answers={answers} onAnswer={handleAnswer} uncertainFlags={uncertainFlags}
              onUncertain={id=>setUncertainFlags(f=>({...f,[id]:!f[id]}))}
              otherTexts={otherTexts} onOtherText={(id,val)=>setOtherTexts(t=>({...t,[id]:val}))}/>
            <div className="q-nav" style={{width:"100%",maxWidth:640}}>
              <button className="btn-back" onClick={handleBack} style={{visibility:safeIdx===0?"hidden":"visible"}}>← Back</button>
              <button className={`btn-next${isFinal?" final":""}`} onClick={handleNext} disabled={!canAdvance}>
                {isFinal?"Review & Generate *":"Next *"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReviewScreen({session, interviewData, onGoToQuestion, onGoToInterview, onGenerate, appFlow="build"}) {
  const {answers, otherTexts} = interviewData;
  // Local copy of uncertainFlags so we can clear flags when answers are saved
  const [uncertainFlags, setUncertainFlags] = useState(interviewData.uncertainFlags||{});
  const [additionalContext, setAdditionalContext] = useState(interviewData.additionalContext||"");
  const questions = buildQuestions(answers);
  const uncertainCount = Object.values(uncertainFlags).filter(Boolean).length;
  const [openEditId, setOpenEditId] = useState(null);
  const [pendingAnswers, setPendingAnswers] = useState({});

  const grouped = SECTION_ORDER.reduce((acc,sec)=>{
    // Include a question if it has an answer OR if it has been flagged uncertain —
    // flagged-but-unanswered questions must appear so the user can see and resolve them.
    const sqs=questions.filter(q=>
      q.section===sec &&
      (answers[q.id]!==undefined || uncertainFlags[q.id])
    );
    if(sqs.length) acc[sec]=sqs;
    return acc;
  },{});

  function formatAnswer(q,ans){
    if(ans===undefined||ans===null||ans===""||
      (Array.isArray(ans)&&ans.length===0)) return "Not answered";
    if(Array.isArray(ans)){
      const labels=(q.options||[]).filter(o=>ans.includes(o.value)).map(o=>o.label);
      if(otherTexts[q.id]&&ans.includes("other")) labels.push(`Other: "${otherTexts[q.id]}"`);
      return labels.join(", ")||"Not answered";
    }
    const opt=(q.options||[]).find(o=>o.value===ans);
    return opt?opt.label:ans;
  }

  // Derive a clean display answer for accounting_method when resolved via diagnostic
  function displayAnswer(q, ans) {
    if(q.id === "accounting_method" && ans === "accrual" &&
       answers.accrual_diagnostic_revenue) {
      return "Accrual Basis (confirmed via diagnostic)";
    }
    if(q.id === "accounting_method" && ans === "cash" &&
       answers.accrual_diagnostic_revenue) {
      return "Cash Basis (confirmed via diagnostic)";
    }
    return formatAnswer(q, ans);
  }

  const [progressSaved, setProgressSaved] = useState(false);

  function handleSaveProgress() {
    const timestamp = new Date().toISOString().slice(0,10);
    const safeName = (session.businessName||"session").replace(/[^a-z0-9]/gi,"_");
    const payload = {
      meta: {
        savedAt: new Date().toISOString(),
        status: "in-progress",
        businessName: session.businessName,
        mode: session.mode,
        preparerName: session.preparerName||null,
        firmName: session.firmName||null,
        completedQuestions: Object.keys(answers).length,
        uncertainCount,
      },
      session,
      interviewAnswers: answers,
      uncertainFlags,
      otherTexts,
    };
    const blob = new Blob([JSON.stringify(payload,null,2)],{type:"application/json"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url;
    a.download=`${safeName}_COABuilder_progress_${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setProgressSaved(true);
    setTimeout(()=>setProgressSaved(false),2600);
  }

  function startEdit(qid,currentAns){setOpenEditId(qid);setPendingAnswers(a=>({...a,[qid]:currentAns}));}
  function cancelEdit(){setOpenEditId(null);}
  function saveEdit(qid){
    const newAns = pendingAnswers[qid];
    interviewData.answers[qid] = newAns;

    // Special case: "Help me decide" on accounting_method needs the diagnostic
    // follow-up questions — inline editing can't handle branching sub-flows.
    // Route the user into the interview at the diagnostic question instead.
    if(qid === "accounting_method" && newAns === "help"){
      setOpenEditId(null);
      if(onGoToInterview) onGoToInterview("accrual_diagnostic_revenue");
      return;
    }

    // If the user has now provided a real answer, auto-clear the uncertain flag
    const hasRealAnswer = newAns !== undefined && newAns !== null &&
      (Array.isArray(newAns) ? newAns.length > 0 : newAns !== "");
    if(hasRealAnswer && uncertainFlags[qid]){
      setUncertainFlags(f => ({...f, [qid]: false}));
    }
    setOpenEditId(null);
  }

  return (
    <div className="review-screen">
      <div className="review-main">
        <h2 className="review-headline">Review Your Answers</h2>
        <p className="review-sub">Review all selections before generating. Click <strong>Edit</strong> on any item to revise inline — no need to restart the interview.</p>
        {uncertainCount>0&&(
          <div className="review-uncertain-banner">
            <span style={{fontSize:"1.1rem"}}>⚠️</span>
            <div>You flagged <strong>{uncertainCount} answer{uncertainCount>1?"s":""}</strong> as uncertain — highlighted below. We recommend revisiting them, but you can proceed and regenerate at any time.</div>
          </div>
        )}
        {Object.entries(grouped).map(([sec,qs])=>{
          const hasUncertain=qs.some(q=>uncertainFlags[q.id]);
          return(
            <div className="review-section" key={sec}>
              <div className="review-section-title">
                {hasUncertain&&<div className="warn-dot"/>}
                {sec}
              </div>
              {qs.map(q=>{
                const isOpen=openEditId===q.id;
                const cur=isOpen?(pendingAnswers[q.id]??answers[q.id]):answers[q.id];
                return(
                  <div key={q.id} className={`review-item${uncertainFlags[q.id]?" uncertain-item":""}`} style={{flexDirection:"column",gap:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                      <div style={{flex:1}}>
                        <div className="review-item-q">{q.headline}</div>
                        <div className="review-item-a" style={{color:(!answers[q.id]&&uncertainFlags[q.id])?"var(--muted-light)":"inherit",fontStyle:(!answers[q.id]&&uncertainFlags[q.id])?"italic":"normal"}}>
                          {displayAnswer(q,answers[q.id])}
                          {uncertainFlags[q.id]&&<span style={{marginLeft:8,fontSize:"0.72rem",color:"var(--warn)",fontStyle:"normal"}}>⚠ Flagged — needs answer</span>}
                        </div>
                      </div>
                      <button className={`review-edit-btn${isOpen?" open":""}`} onClick={()=>isOpen?cancelEdit():startEdit(q.id,answers[q.id])}>
                        {isOpen?"Cancel":"Edit"}
                      </button>
                    </div>
                    {isOpen&&(
                      <div className="review-item-edit-panel">
                        {(q.type==="radio"||q.type==="radio_sub")&&(
                          <div className="review-inline-options">
                            {(q.options||[]).map(opt=>(
                              <div key={opt.value} className={`review-inline-opt${cur===opt.value?" selected":""}`} onClick={()=>setPendingAnswers(a=>({...a,[q.id]:opt.value}))}>
                                <div style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${cur===opt.value?"var(--accent)":"var(--rule)"}`,background:cur===opt.value?"var(--accent)":"transparent",flexShrink:0}}/>
                                <span style={{flex:1}}>{opt.label}{opt.sub&&<span style={{display:"block",fontSize:"0.7rem",color:"var(--muted)"}}>{opt.sub}</span>}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {q.type==="multi"&&(
                          <div className="review-inline-options">
                            {(q.options||[]).map(opt=>{
                              const arr=Array.isArray(cur)?cur:[];
                              const sel=arr.includes(opt.value);
                              return(
                                <div key={opt.value} className={`review-inline-opt${sel?" selected":""}`}
                                  onClick={()=>setPendingAnswers(a=>({...a,[q.id]:sel?arr.filter(x=>x!==opt.value):[...arr,opt.value]}))}>
                                  <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${sel?"var(--accent)":"var(--rule)"}`,background:sel?"var(--accent)":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:"0.6rem"}}>{sel?"✔":""}</div>
                                  <span style={{flex:1}}>{opt.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {q.type==="industry_primary"&&(
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                            {Object.entries(INDUSTRY_TREE).map(([name,{icon}])=>(
                              <div key={name} className={`review-inline-opt${cur===name?" selected":""}`} onClick={()=>setPendingAnswers(a=>({...a,[q.id]:name}))}>
                                <span>{icon}</span><span>{name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {q.id==="accounting_method"&&pendingAnswers[q.id]==="help"&&(
                          <div style={{fontSize:"0.78rem",color:"var(--accent)",background:"var(--accent-xpale)",border:"1px solid var(--accent-pale)",borderRadius:"var(--radius)",padding:"8px 12px",marginBottom:8}}>
                            * You'll be taken to two quick diagnostic questions, then returned here.
                          </div>
                        )}
                        <div className="review-inline-actions">
                          <button className="btn-save-inline" onClick={()=>saveEdit(q.id)}>
                            {q.id==="accounting_method"&&pendingAnswers[q.id]==="help"
                              ? "Continue to Diagnostics *"
                              : "Save & Return"}
                          </button>
                          <button className="btn-cancel-inline" onClick={cancelEdit}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {/* Additional Context — free text before generation */}
        <div style={{margin:"28px 0 8px",padding:"20px 24px",background:"var(--accent-xpale)",border:"1.5px solid var(--accent-pale)",borderRadius:"var(--radius-lg)"}}>
          <div style={{fontSize:"0.78rem",fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--accent)",marginBottom:6}}>Anything else the AI should know?</div>
          <div style={{fontSize:"0.83rem",color:"var(--muted)",marginBottom:12,lineHeight:1.55}}>Entity structure nuances, upcoming transactions, regulatory requirements, specific accounts you know you'll need — anything from your discovery call notes that would sharpen the output.</div>
          <textarea
            style={{width:"100%",minHeight:90,padding:"10px 13px",fontSize:"0.88rem",fontFamily:"'DM Sans',sans-serif",border:"1.5px solid var(--rule)",borderRadius:"var(--radius)",background:"white",color:"var(--ink)",resize:"vertical",outline:"none",lineHeight:1.6}}
            placeholder="e.g. Complex MSO/PLLC structure with management fee arrangements. Client has a pending Series A. Needs covenant reporting accounts for bank debt. Uses Bill.com and Ramp for AP."
            value={additionalContext}
            onChange={e=>setAdditionalContext(e.target.value)}
            maxLength={1500}
          />
          <div style={{fontSize:"0.72rem",color:"var(--muted-light)",marginTop:4,textAlign:"right"}}>{additionalContext.length}/1500</div>
        </div>

        <div className="review-actions" style={{flexWrap:"wrap",gap:12}}>
          <button className="btn-generate" onClick={()=>{
              // Sync cleared flags and additional context back before generating
              interviewData.uncertainFlags = uncertainFlags;
              interviewData.additionalContext = additionalContext;
              onGenerate();
            }}
            disabled={uncertainCount>0&&Object.keys(answers).length===0}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8h12M8 2l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {appFlow==="audit" ? "Run COA Audit *" : "Generate Chart of Accounts"}
          </button>
          <button className="btn-session save" style={{padding:"12px 20px"}} onClick={handleSaveProgress}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M13 11v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {progressSaved?"✔ Saved":"Save Progress"}
          </button>
          <div className="review-note">
            {uncertainCount>0
              ? <><strong style={{color:"var(--warn)"}}>{uncertainCount} question{uncertainCount>1?"s":""} need{uncertainCount===1?"s":""} an answer.</strong><br/>Resolve flagged items or proceed — editable after generation.</>
              : <>All accounts are editable on the output screen before export.</>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

function ProcessingScreen({aiMode}) {
  const steps = aiMode
    ? ["Compiling business profile & follow-up responses…","Applying framework-specific account conventions…","Structuring balance sheet with entity-appropriate accounts…","Building P&L hierarchy and COGS structure…","Finalising ERP field mapping…"]
    : ["Compiling industry & entity profile…","Structuring balance sheet accounts…","Building P&L account hierarchy…","Formatting for target ERP specification…"];
  return (
    <div className="processing-screen">
      <div className="processing-orb"/>
      <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.7rem",fontWeight:600,marginBottom:8}}>Generating Chart of Accounts…</h2>
      <p style={{fontSize:"0.9rem",color:"var(--muted)",maxWidth:380,lineHeight:1.6}}>
        {aiMode
          ? "Applying GAAP framework conventions and professional accounting judgment to your business profile."
          : "Building account structure based on your selections. This takes just a moment."
        }
      </p>
      <div className="processing-steps">
        {steps.map((s,i)=>(
          <div key={i} className="processing-step"><div className="step-dot" style={{animationDelay:`${i*0.5}s`}}/>{s}</div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------
//  OUTPUT SCREEN — full inline editing
// ---------------------------------------------
function OutputScreen({session, interviewData, aiCOA, apiKey, generationError, onNewBuild, auditData, appFlow}) {
  const [coa, setCoa] = useState(() => {
    const base = aiCOA || generateCOA(interviewData.answers).map((r,i)=>({...r,_id:i,flagged:false,deleted:false}));
    return base;
  });
  const [suggestionState, setSuggestionState] = useState({}); // {rowId: {loading, items, error, open}}
  const [openMenu, setOpenMenu] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [addingAccount, setAddingAccount] = useState(false);
  const [sectionAddTarget, setSectionAddTarget] = useState(null); // section name to pre-target
  const [newAccount, setNewAccount] = useState({num:"",name:"",type:"Expense",desc:""});
  const [confirmModal, setConfirmModal] = useState(null);
  const targetERP = interviewData.answers.target_erp || "other";
  const erpLabel = ERP_OPTIONS.find(e=>e.value===targetERP)?.name||"Generic CSV";

  const activeAccounts = coa.filter(r=>!r.deleted);
  const accountCount = activeAccounts.filter(r=>!r.section).length;
  const flaggedCount = activeAccounts.filter(r=>!r.section&&r.flagged).length;

  function openEdit(row){
    setEditingId(row._id);
    setEditDraft({num:row.num,name:row.name,type:row.type,desc:row.desc});
    setOpenMenu(null);
  }
  function saveEdit(){
    setConfirmModal({
      title:"Confirm Account Edit",
      body:`Renaming account ${editDraft.num} to "${editDraft.name}" (${editDraft.type}). This is a structural change — make sure the account name and type are correct before exporting.`,
      confirmLabel:"Save Changes",
      onConfirm:()=>{
        setCoa(prev=>{
          const idx = prev.findIndex(r=>r._id===editingId);
          if(idx<0) return prev;
          const original = prev[idx];
          const updated = {...original, ...editDraft};

          const origCat = TYPE_CATEGORY[original.type]?.cat;
          const newCat  = TYPE_CATEGORY[updated.type]?.cat;
          const currentRange = getRowSectionRange(prev, editingId);

          // Decide if we need to re-place the row in a different section.
          // We re-place when:
          //   (a) the type's category changed (e.g. Expense -> Income), OR
          //   (b) the type stayed but the current section is a poor fit for it
          //       (the AI may have placed it oddly originally and a better
          //       section now exists).
          let needsReplace = false;
          if(origCat !== newCat){
            needsReplace = true;
          } else if(currentRange){
            const currentScore = scoreSectionFit(prev, currentRange, updated);
            const best = findBestSection(prev, updated);
            if(best && best.headerIdx !== currentRange.headerIdx){
              const bestScore = scoreSectionFit(prev, best, updated);
              // Only move if the better section is meaningfully better, to
              // avoid jitter on minor edits.
              if(bestScore > currentScore + 25) needsReplace = true;
            }
          }

          if(!needsReplace){
            const next = [...prev];
            next[idx] = updated;
            return next;
          }

          // Remove the row from its current position, then re-place using
          // the same insertion logic addAccount uses.
          const without = prev.filter(r=>r._id!==editingId);
          return placeRowInCoa(without, updated);
        });
        setEditingId(null);
        setConfirmModal(null);
      }
    });
  }
  function cancelEdit(){setEditingId(null);setEditDraft({});}
  function toggleFlag(id){setCoa(prev=>prev.map(r=>r._id===id?{...r,flagged:!r.flagged}:r));setOpenMenu(null);}
  function deleteAccount(id){
    const row=coa.find(r=>r._id===id);
    setConfirmModal({
      title:"Delete Account",
      body:`Remove "${row.name}" (${row.num}) from this Chart of Accounts? This cannot be undone without regenerating.`,
      confirmLabel:"Delete",
      danger:true,
      onConfirm:()=>{setCoa(prev=>prev.map(r=>r._id===id?{...r,deleted:true}:r));setOpenMenu(null);setConfirmModal(null);}
    });
  }
  // -- AI ACCOUNT SUGGESTIONS --------------------------------------------------
  async function fetchSuggestions(row) {
    if (!apiKey) return;
    setSuggestionState(s=>({...s,[row._id]:{loading:true,items:[],error:"",open:true}}));
    setOpenMenu(null);
    try {
      const items = await getAccountSuggestions(apiKey, row, session, interviewData.answers);
      setSuggestionState(s=>({...s,[row._id]:{loading:false,items,error:"",open:true}}));
    } catch(e) {
      setSuggestionState(s=>({...s,[row._id]:{loading:false,items:[],error:e.message,open:true}}));
    }
  }

  function applySuggestion(rowId, suggestion) {
    setConfirmModal({
      title:"Apply Suggestion",
      body:`Replace current account name with "${suggestion.name}" (${suggestion.type})?

${suggestion.rationale}`,
      confirmLabel:"Apply",
      onConfirm:()=>{
        setCoa(prev=>prev.map(r=>r._id===rowId?{...r,name:suggestion.name,type:suggestion.type}:r));
        setSuggestionState(s=>({...s,[rowId]:{...s[rowId],open:false}}));
        setConfirmModal(null);
      }
    });
  }

  function closeSuggestion(rowId){
    setSuggestionState(s=>({...s,[rowId]:{...s[rowId],open:false}}));
  }

  // Classify an account type into a coarse category and numeric band.
  // Categories are framework-agnostic — they describe what the account IS,
  // not what the section is named in any particular GAAP framework.
  // numBand is the conventional first digit of the account number.
  const TYPE_CATEGORY = {
    "Bank":                    {cat:"asset_current",   numBand:1, sub:"cash"},
    "Accounts Receivable":     {cat:"asset_current",   numBand:1, sub:"ar"},
    "Other Current Asset":     {cat:"asset_current",   numBand:1, sub:"oca"},
    "Fixed Asset":             {cat:"asset_fixed",     numBand:1, sub:"fa"},
    "Other Asset":             {cat:"asset_other",     numBand:1, sub:"oa"},
    "Accounts Payable":        {cat:"liab_current",    numBand:2, sub:"ap"},
    "Credit Card":             {cat:"liab_current",    numBand:2, sub:"cc"},
    "Other Current Liability": {cat:"liab_current",    numBand:2, sub:"ocl"},
    "Long Term Liability":     {cat:"liab_long",       numBand:2, sub:"ltl"},
    "Equity":                  {cat:"equity",          numBand:3, sub:"eq"},
    "Retained Earnings":       {cat:"equity",          numBand:3, sub:"re"},
    "Income":                  {cat:"revenue",         numBand:4, sub:"rev"},
    "Other Income":            {cat:"other_inc_exp",   numBand:7, sub:"oi"},
    "Cost of Goods Sold":      {cat:"cogs",            numBand:5, sub:"cogs"},
    "Expense":                 {cat:"opex",            numBand:6, sub:"opex"},
    "Other Expense":           {cat:"other_inc_exp",   numBand:7, sub:"oe"},
  };

  // Fallback English-keyword hints for section header names — used only as a
  // tie-breaker when section contents are sparse or ambiguous.
  const CATEGORY_KEYWORDS = {
    asset_current:  ["current asset","current asset","cash","receivable","prepaid","inventory","debtors","trade and other"],
    asset_fixed:    ["fixed asset","property","plant","equipment","ppe"],
    asset_other:    ["other asset","intangible","long-term asset","goodwill","investment"],
    liab_current:   ["current liabilit","payable","accrued","short-term","creditors"],
    liab_long:      ["long-term liabilit","long term liabilit","non-current liabilit","debt"],
    equity:         ["equity","capital","retained","reserve"],
    revenue:        ["revenue","income","sales","turnover"],
    cogs:           ["cost of good","cost of revenue","cost of sales","cogs"],
    opex:           ["operating expense","operating expenses","general","administrative","selling","sales & marketing","compensation","facilities","payroll","travel","g&a","sg&a","distribution cost","admin"],
    other_inc_exp:  ["other income","other expense","other income & expense","non-operating","interest","tax expense"],
  };

  // Build a list of {start, end, headerIdx, header} entries describing each
  // section's bounds in the current coa array.
  function getSectionRanges(arr) {
    const ranges = [];
    let cur = null;
    for(let i=0;i<arr.length;i++){
      if(arr[i].section){
        if(cur){ cur.end = i-1; ranges.push(cur); }
        cur = {start:i+1, end:arr.length-1, headerIdx:i, header:arr[i].section};
      }
    }
    if(cur) ranges.push(cur);
    return ranges;
  }

  // Score how well a section "fits" a new row by looking at what's already
  // in it. Higher = better fit. Returns 0 if section has no compatible rows.
  function scoreSectionFit(arr, range, row) {
    const wantCat = TYPE_CATEGORY[row.type]?.cat;
    const wantBand = TYPE_CATEGORY[row.type]?.numBand;
    if(!wantCat) return 0;
    const newNum = parseInt((row.num||"").replace(/\D/g,""),10);
    const hasNum = !isNaN(newNum);

    let score = 0;
    let sameTypeCount = 0;
    let sameCatCount = 0;
    let sameBandCount = 0;
    let totalRows = 0;
    let nearestNumDistance = Infinity;

    for(let i=range.start;i<=range.end;i++){
      const r = arr[i];
      if(!r || r.section || r.deleted) continue;
      totalRows++;
      if(r.type === row.type) sameTypeCount++;
      const rCat = TYPE_CATEGORY[r.type]?.cat;
      if(rCat === wantCat) sameCatCount++;
      const rNum = parseInt((r.num||"").replace(/\D/g,""),10);
      if(!isNaN(rNum)){
        if(Math.floor(rNum/1000) === wantBand) sameBandCount++;
        if(hasNum){
          const d = Math.abs(rNum - newNum);
          if(d < nearestNumDistance) nearestNumDistance = d;
        }
      }
    }

    // Empty section gets a small base score from header keyword match only.
    if(totalRows === 0){
      const hdr = (range.header||"").toLowerCase();
      const kws = CATEGORY_KEYWORDS[wantCat]||[];
      if(kws.some(k=>hdr.includes(k))) return 1;
      return 0;
    }

    // Same exact type contributes most; same broad category next; same number
    // band next. All weighted by % of section that matches.
    score += (sameTypeCount / totalRows) * 100;
    score += (sameCatCount  / totalRows) * 60;
    score += (sameBandCount / totalRows) * 20;

    // Header keyword bonus when contents agree
    const hdr = (range.header||"").toLowerCase();
    const kws = CATEGORY_KEYWORDS[wantCat]||[];
    if(kws.some(k=>hdr.includes(k))) score += 15;

    // If the section is mostly the wrong category, zero it out — never place
    // an Expense in a section that's 80% Income, etc.
    if(sameCatCount === 0) return 0;

    // Numeric proximity is a soft tiebreaker
    if(hasNum && nearestNumDistance < Infinity){
      score += Math.max(0, 10 - Math.min(nearestNumDistance/100, 10));
    }

    return score;
  }

  // Find the best section for a row. Returns the section range or null.
  function findBestSection(arr, row) {
    const ranges = getSectionRanges(arr);
    if(!ranges.length) return null;
    let best = null, bestScore = 0;
    for(const r of ranges){
      const s = scoreSectionFit(arr, r, row);
      if(s > bestScore){ bestScore = s; best = r; }
    }
    return bestScore > 0 ? best : null;
  }

  // Find the in-section position to insert a row, ordered by account number.
  function findInsertIdx(arr, range, row) {
    const newNum = parseInt((row.num||"").replace(/\D/g,""),10);
    const hasNum = !isNaN(newNum);
    let insertAfter = range.headerIdx; // default: right after section header
    for(let i=range.start;i<=range.end;i++){
      const r = arr[i];
      if(!r || r.section || r.deleted) continue;
      const rNum = parseInt((r.num||"").replace(/\D/g,""),10);
      if(!hasNum){
        // No number on new row — append to end of section
        insertAfter = i;
      } else if(!isNaN(rNum) && rNum <= newNum){
        insertAfter = i;
      } else if(!isNaN(rNum) && rNum > newNum){
        break;
      } else {
        // existing row is unnumbered — put numbered new rows before it
        break;
      }
    }
    return insertAfter + 1;
  }

  // Insert a row into the right section at the right position. If no section
  // can be confidently identified, append to end (legacy fallback).
  // If pinnedSectionName is provided (user clicked "+ Add" on a specific
  // section header), that section is used regardless of content scoring.
  function placeRowInCoa(arr, row, pinnedSectionName=null) {
    let target = null;
    if(pinnedSectionName){
      const ranges = getSectionRanges(arr);
      target = ranges.find(r=>r.header===pinnedSectionName) || null;
    }
    if(!target) target = findBestSection(arr, row);
    if(!target) return [...arr, row];
    const insertIdx = findInsertIdx(arr, target, row);
    const next = [...arr];
    next.splice(insertIdx, 0, row);
    return next;
  }

  // Given a row id, return the section range it currently lives in, or null.
  function getRowSectionRange(arr, rowId) {
    const idx = arr.findIndex(r=>r._id===rowId);
    if(idx<0) return null;
    const ranges = getSectionRanges(arr);
    return ranges.find(r=>idx>=r.start && idx<=r.end) || null;
  }

  function addAccount(){
    if(!newAccount.num||!newAccount.name) return;
    const nextId = Math.max(...coa.map(r=>r._id))+1;
    const newRow = {...newAccount,_id:nextId,flagged:false,deleted:false};
    setCoa(prev=>placeRowInCoa(prev, newRow, sectionAddTarget));
    setNewAccount({num:"",name:"",type:"Expense",desc:""});
    setAddingAccount(false);
    setSectionAddTarget(null);
  }

  function handleExport(){exportForERP(coa,targetERP,session.businessName);}

  const [showToast, setShowToast] = useState(false);

  function handleSaveSession() {
    const timestamp = new Date().toISOString().slice(0,10);
    const safeName = session.businessName.replace(/[^a-z0-9]/gi,"_");
    const framework = GAAP_FRAMEWORKS.find(f=>f.value===interviewData.answers.gaap_framework)?.label||"US GAAP";
    const erp = ERP_OPTIONS.find(e=>e.value===interviewData.answers.target_erp)?.name||"Generic";

    const sessionPayload = {
      meta: {
        exportedAt: new Date().toISOString(),
        toolVersion: "COABuilder Day 3",
        businessName: session.businessName,
        mode: session.mode,
        preparerName: session.preparerName||null,
        firmName: session.firmName||null,
        framework,
        targetERP: erp,
        accountCount: coa.filter(r=>!r.section&&!r.deleted).length,
        aiGenerated: !!aiCOA,
      },
      interviewAnswers: interviewData.answers,
      followUpQuestions: interviewData.followUpQuestions||[],
      followUpAnswers: interviewData.followUpAnswers||{},
      coa: coa.filter(r=>!r.deleted).map(r=>({
        ...(r.section ? {section:r.section} : {
          num:r.num, name:r.name, type:r.type,
          desc:r.desc, flagged:r.flagged||false
        })
      })),
    };

    const blob = new Blob([JSON.stringify(sessionPayload, null, 2)], {type:"application/json"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}_COABuilder_${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);

    setShowToast(true);
    setTimeout(()=>setShowToast(false), 2600);
  }

  // Close menu on outside mousedown. Using mousedown (not click) so it fires
  // before the button onClick — prevents the open/close same-tick race.
  useEffect(()=>{
    const h=(e)=>{
      if(!e.target.closest(".row-actions")) setOpenMenu(null);
    };
    document.addEventListener("mousedown",h);
    return()=>document.removeEventListener("mousedown",h);
  },[]);

  return (
    <div className="output-screen">
      {confirmModal&&(
        <ConfirmModal {...confirmModal} onCancel={()=>setConfirmModal(null)}/>
      )}
      {aiCOA && !generationError && appFlow!=="audit" && (
        <div style={{background:"var(--accent-xpale)",border:"1px solid var(--accent-pale)",borderRadius:"var(--radius)",padding:"10px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center",fontSize:"0.82rem",color:"var(--accent)"}}>
          <span style={{fontWeight:600}}>✓ AI-Generated</span>
          <span style={{color:"var(--muted)"}}>This COA was produced with professional accounting judgment applied to your business profile and {GAAP_FRAMEWORKS.find(f=>f.value===interviewData.answers.gaap_framework)?.label||"US GAAP"} conventions. Review all accounts before export.</span>
        </div>
      )}
      {appFlow==="audit" && auditData && (
        <div style={{background:"#e8f0fe",border:"1px solid #c5d5fa",borderRadius:"var(--radius-lg)",padding:"16px 20px",marginBottom:20}}>
          <div style={{display:"flex",gap:24,alignItems:"center",flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:"0.7rem",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",color:"#3b5bdb",marginBottom:4}}>🔍 Audit Complete — {session.businessName}</div>
              <div style={{fontSize:"0.84rem",color:"var(--ink-soft)",lineHeight:1.5}}>{auditData.assessment}</div>
            </div>
            <div style={{display:"flex",gap:12,flexShrink:0}}>
              {[
                {label:"Accepted", val:auditData.auditSummary?.accepted||0, color:"var(--accent)"},
                {label:"Modified", val:auditData.auditSummary?.modified||0, color:"#3b5bdb"},
                {label:"Ignored",  val:auditData.auditSummary?.ignored||0,  color:"var(--muted)"},
              ].map(s=>(
                <div key={s.label} style={{textAlign:"center"}}>
                  <div style={{fontSize:"1.4rem",fontWeight:700,color:s.color,fontFamily:"'Playfair Display',serif"}}>{s.val}</div>
                  <div style={{fontSize:"0.68rem",color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.06em"}}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {generationError && (
        <div style={{background:"var(--warn-pale)",border:"1px solid #f0b07a",borderRadius:"var(--radius)",padding:"10px 16px",marginBottom:16,fontSize:"0.82rem",color:"var(--ink-soft)"}}>
          ⚠ AI generation encountered an issue — showing standard output. ({generationError})
        </div>
      )}
      <div className="output-topbar">
        <div>
          <div className="output-title">{appFlow==="audit"?"Revised Chart of Accounts":"Chart of Accounts"}</div>
          <div className="output-meta">
            {session.businessName}
            {session.mode==="agency"&&session.firmName?` · Prepared by ${session.firmName}`:""}
            {` · ${accountCount} accounts`}
            {aiCOA&&<span style={{marginLeft:8,color:"var(--accent)",fontWeight:500}}>· ✓ AI</span>}
            {flaggedCount>0&&<span style={{marginLeft:8,color:"var(--warn)"}}>· {flaggedCount} flagged</span>}
          </div>
        </div>
        <div className="output-actions">
          <button className="btn-export" onClick={handleExport}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v9M4 8l4 4 4-4M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Export for {erpLabel}
          </button>
        </div>
      </div>

      {flaggedCount>0&&(
        <div style={{background:"var(--warn-pale)",border:"1px solid #f0b07a",borderRadius:"var(--radius)",padding:"10px 16px",marginBottom:16,fontSize:"0.82rem",color:"var(--ink-soft)",display:"flex",gap:10,alignItems:"center"}}>
          ⚠️ <span>You have <strong>{flaggedCount} flagged account{flaggedCount>1?"s":""}</strong> — review them before exporting.</span>
        </div>
      )}

      <table className="coa-table">
        <thead>
          <tr>
            <th style={{width:90}}>Number</th>
            <th>Account Name</th>
            <th style={{width:210}}>Type</th>
            <th>Description</th>
            <th style={{width:48}}></th>
          </tr>
        </thead>
        <tbody>
          {activeAccounts.map((row,i)=>{
            if(row.section) return(
              <tr key={row._id} className="coa-section-row">
                <td colSpan={5}>
                  {row.section}
                  <button className="section-add-btn" onClick={()=>{
                    setSectionAddTarget(row.section);
                    // Pick a default type by sampling the section's existing rows.
                    // If empty, fall back to a keyword-based guess on the header.
                    const ranges = getSectionRanges(coa);
                    const range = ranges.find(r=>r.headerIdx===coa.indexOf(row));
                    let defaultType = "Expense";
                    if(range){
                      const counts = {};
                      for(let k=range.start;k<=range.end;k++){
                        const r2 = coa[k];
                        if(!r2 || r2.section || r2.deleted || !r2.type) continue;
                        counts[r2.type] = (counts[r2.type]||0) + 1;
                      }
                      const top = Object.entries(counts).sort((a,b)=>b[1]-a[1])[0];
                      if(top) defaultType = top[0];
                      else {
                        const hdr = (row.section||"").toLowerCase();
                        for(const [type, info] of Object.entries(TYPE_CATEGORY)){
                          const kws = CATEGORY_KEYWORDS[info.cat]||[];
                          if(kws.some(k=>hdr.includes(k))){ defaultType = type; break; }
                        }
                      }
                    }
                    setNewAccount(a=>({...a,type:defaultType}));
                    setAddingAccount(true);
                    setTimeout(()=>{
                      document.getElementById("add-account-form-anchor")?.scrollIntoView({behavior:"smooth",block:"center"});
                    },100);
                  }}>
                    + Add
                  </button>
                </td>
              </tr>
            );
            const isEditing=editingId===row._id;
            if(isEditing) return(
              <tr key={row._id} className="editing">
                <td colSpan={5}>
                  <div className="inline-edit-form">
                    <div className="inline-context">
                      <strong>{row.name}</strong> — {row.desc||"No description."} Changing account name or type is a structural decision. Confirm before exporting.
                    </div>
                    <div className="inline-edit-grid">
                      <input className="inline-input" placeholder="Account #" value={editDraft.num} onChange={e=>setEditDraft(d=>({...d,num:e.target.value}))}/>
                      <input className="inline-input" placeholder="Account Name" value={editDraft.name} onChange={e=>setEditDraft(d=>({...d,name:e.target.value}))}/>
                      <select className="inline-select" value={editDraft.type} onChange={e=>setEditDraft(d=>({...d,type:e.target.value}))}>
                        {ACCOUNT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <input className="inline-desc-input" placeholder="Description (optional)" value={editDraft.desc} onChange={e=>setEditDraft(d=>({...d,desc:e.target.value}))}/>
                    <div className="inline-actions">
                      <button className="btn-inline-save" onClick={saveEdit}>Save Changes</button>
                      <button className="btn-inline-cancel" onClick={cancelEdit}>Cancel</button>
                      <span className="inline-confirm-note">A confirmation step will appear before saving.</span>
                    </div>
                  </div>
                </td>
              </tr>
            );
            return(
              <tr key={row._id} className={row.flagged?"flagged":row._auditRevised?"editing":""}>
                <td><span className="acct-num">{row.num}</span></td>
                <td>
                  {row.name}
                  {row.flagged&&<span className="flag-badge" style={{marginLeft:8}}>Flagged</span>}
                </td>
                <td><span className="type-badge">{row.type}</span></td>
                <td className="desc-cell">{row.desc}</td>
                <td>
                  <div className="row-actions" onClick={e=>e.stopPropagation()}>
                    <button className={`three-dot-btn${openMenu===row._id?" open":""}`} onClick={()=>setOpenMenu(openMenu===row._id?null:row._id)}>⋯</button>
                    {openMenu===row._id&&(
                      <div className="dot-menu">
                        <button className="dot-menu-item" onClick={()=>openEdit(row)}>✏️ Edit Account</button>
                        <button className="dot-menu-item" onClick={()=>toggleFlag(row._id)}>
                          {row.flagged?"📒 Clear Flag":"🚩 Flag for Review"}
                        </button>
                        {apiKey&&(
                          <button className="dot-menu-item" onClick={()=>fetchSuggestions(row)}>
                            ✓ Suggest Alternatives
                          </button>
                        )}
                        <div className="dot-menu-divider"/>
                        <button className="dot-menu-item danger" onClick={()=>deleteAccount(row._id)}>🗑 Delete Account</button>
                      </div>
                    )}
                  </div>
                  {/* AI suggestion panel */}
                  {suggestionState[row._id]?.open && (
                    <div className="ai-suggestion-panel" onClick={e=>e.stopPropagation()}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <div className="ai-suggestion-title">✓ Suggested Alternatives</div>
                        <button onClick={()=>closeSuggestion(row._id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--muted)",fontSize:"0.8rem"}}>✖</button>
                      </div>
                      {suggestionState[row._id]?.loading && (
                        <div className="ai-suggestion-loading">
                          <div className="ai-suggestion-spinner"/>
                          Generating suggestions…
                        </div>
                      )}
                      {suggestionState[row._id]?.error && (
                        <div className="ai-suggestion-error">{suggestionState[row._id].error}</div>
                      )}
                      {(suggestionState[row._id]?.items||[]).map((s,i)=>(
                        <div key={i} className="ai-suggestion-item" onClick={()=>applySuggestion(row._id,s)}>
                          <div className="ai-suggestion-name">{s.name} <span style={{fontSize:"0.7rem",color:"var(--muted)",fontWeight:400}}>({s.type})</span></div>
                          <div className="ai-suggestion-rationale">{s.rationale}</div>
                        </div>
                      ))}
                      {!suggestionState[row._id]?.loading && (suggestionState[row._id]?.items||[]).length===0 && !suggestionState[row._id]?.error && (
                        <div style={{fontSize:"0.78rem",color:"var(--muted)"}}>No suggestions available.</div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Floating Add Account Button — always visible */}
      {!addingAccount && (
        <button className="fab-add-account" onClick={()=>{
          setAddingAccount(true);
          setTimeout(()=>{
            document.getElementById("add-account-form-anchor")?.scrollIntoView({behavior:"smooth",block:"center"});
          },100);
        }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          Add Account
        </button>
      )}

      <div className="add-account-section" id="add-account-form-anchor">
        {!addingAccount?(
          <button className="btn-add-account" onClick={()=>setAddingAccount(true)}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Add Account Manually
          </button>
        ):(
          <div className="add-account-form">
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:"0.95rem",fontWeight:600,marginBottom:14,color:"var(--ink)"}}>New Account</div>
            <div className="add-account-grid">
              <input className="inline-input" placeholder="Account #  e.g. 2100" value={newAccount.num} onChange={e=>setNewAccount(a=>({...a,num:e.target.value}))}/>
              <input className="inline-input" placeholder="Account Name" value={newAccount.name} onChange={e=>setNewAccount(a=>({...a,name:e.target.value}))}/>
              <select className="inline-select" value={newAccount.type} onChange={e=>setNewAccount(a=>({...a,type:e.target.value}))}>
                {ACCOUNT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="add-desc-row">
              <input className="inline-desc-input" style={{width:"100%"}} placeholder="Description (optional)" value={newAccount.desc} onChange={e=>setNewAccount(a=>({...a,desc:e.target.value}))}/>
            </div>
            {newAccount.type&&(()=>{
              const previewRow = {...newAccount, _id:-1, deleted:false};
              const target = findBestSection(coa, previewRow);
              const label = target?.header || "End of list (no matching section found)";
              return (
                <div style={{fontSize:"0.78rem",color:"var(--accent)",background:"var(--accent-xpale)",border:"1px solid var(--accent-pale)",borderRadius:"var(--radius)",padding:"7px 12px",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                  <span>↓</span>
                  <span>This account will be placed under: <strong>{label}</strong></span>
                </div>
              );
            })()}
            <div className="inline-actions">
              <button className="btn-inline-save" onClick={addAccount} disabled={!newAccount.num||!newAccount.name}>Add Account</button>
              <button className="btn-inline-cancel" onClick={()=>setAddingAccount(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── FINISH LINE ── */}
      <div className="finish-line-bar">
        <div className="finish-line-left">
          <div className="finish-line-check">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="var(--accent)"/><path d="M7 12l3.5 3.5L17 8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div>
            <div className="finish-line-title">{appFlow==="audit"?"Audit Complete":"Your COA is Ready"} — {session.businessName}</div>
            <div className="finish-line-sub">{accountCount} accounts{appFlow!=="audit"?" · GAAP-aligned, export-ready":""}{flaggedCount>0?` · ${flaggedCount} flagged for review`:""}</div>
          </div>
        </div>
        <div className="finish-line-actions">
          <button className="btn-session save" onClick={handleSaveSession}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M13 11v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Save Session
          </button>
          <button className="btn-export" style={{borderRadius:"var(--radius)",padding:"11px 24px",fontSize:"0.92rem"}} onClick={handleExport}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v9M4 8l4 4 4-4M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Export for {erpLabel}
          </button>
          <a className="btn-session expert" href="https://calendly.com/phil-adastracfo/discovery-briefing" target="_blank" rel="noopener noreferrer" style={{textDecoration:"none"}}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 13c0-2.761 2.239-4 5-4s5 1.239 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Get Expert Review
          </a>
          <button className="btn-session new-build" onClick={onNewBuild} style={{background:"none",border:"1.5px solid var(--rule)",color:"var(--muted)"}}>New Build</button>
        </div>
      </div>

      {/* AdAstra CFO cross-pollination CTA */}
      <div className="adastra-cta-card">
        <div className="adastra-cta-left">
          <div className="adastra-cta-eyebrow">Behind COA Builder</div>
          <div className="adastra-cta-headline">
            Built by AdAstra CFO — fractional finance leadership for growing companies.
          </div>
          <div className="adastra-cta-desc">
            COA Builder is a product of AdAstra CFO. If your client needs more than a Chart of Accounts — financial systems architecture, ERP implementation, or ongoing fractional CFO support — that's what we do.
          </div>
        </div>
        <div className="adastra-cta-right">
          <a
            className="btn-adastra-primary"
            href="https://calendly.com/phil-adastracfo/discovery-briefing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a Free Discovery Call *
          </a>
          <a
            className="btn-adastra-secondary"
            href="https://adastracfo.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn about AdAstra CFO
          </a>
        </div>
      </div>

      {/* Footer attribution */}
      <div className="adastra-footer">
        <span>COA Builder</span>
        <div className="adastra-footer-dot"/>
        <span>A product of</span>
        <a href="https://adastracfo.com" target="_blank" rel="noopener noreferrer">AdAstra CFO</a>
        <div className="adastra-footer-dot"/>
        <a href="https://adastracfo.com" target="_blank" rel="noopener noreferrer">adastracfo.com</a>
      </div>

      {showToast&&(
        <div className="save-toast">
          ✔ Session saved — {session.businessName.replace(/[^a-z0-9]/gi,"_")}_COABuilder.json
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------
//  AI LAYER — API calls to Claude
// ---------------------------------------------

async function callClaude(apiKey, systemPrompt, userMessage, maxTokens=1800) {
  // Use a server-side proxy in production to avoid CORS.
  // Call Anthropic directly when running locally.
  const isLocal = typeof window !== "undefined" &&
    window.location.hostname === "localhost";
  const endpoint = isLocal
    ? "https://api.anthropic.com/v1/messages"
    : "/api/proxy";

  const headers = { "Content-Type": "application/json" };
  if (isLocal) {
    headers["x-api-key"] = apiKey;
    headers["anthropic-version"] = "2023-06-01";
  } else {
    headers["x-api-key"] = apiKey;
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: maxTokens,
      system: systemPrompt,
      messages: [{ role: "user", content: userMessage }],
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(()=>({}));
    throw new Error(err?.error?.message || `API error ${response.status}`);
  }
  const data = await response.json();
  return data.content?.find(b=>b.type==="text")?.text || "";
}

function buildBusinessProfile(session, answers, interviewData) {
  const erp = ERP_OPTIONS.find(e=>e.value===answers.target_erp)?.name || "Generic";
  const framework = GAAP_FRAMEWORKS.find(f=>f.value===answers.gaap_framework)?.label || "US GAAP";
  const entity = ENTITY_TYPES.find(e=>e.value===answers.entity_type)?.label || answers.entity_type || "Unknown";
  const revenues = (answers.revenue_streams||[]).map(r=>REVENUE_OPTIONS.find(o=>o.value===r)?.label||r).join(", ");
  const financing = (answers.financing||[]).map(f=>FINANCING_OPTIONS.find(o=>o.value===f)?.label||f).join(", ");
  const assets = (answers.assets||[]).join(", ");
  const entityStructure = ENTITY_STRUCTURE_OPTIONS.find(e=>e.value===answers.entity_structure)?.label || "Single entity";
  const intercoTypes = (answers.interco_types||[]).map(t=>{
    const opts = [{value:"loans",label:"Intercompany loans"},{value:"open_bal",label:"Open intercompany balances"},{value:"mgmt_fees",label:"Management fees"},{value:"dividends",label:"Intercompany dividends"},{value:"sales",label:"Intercompany sales"},{value:"capex",label:"Asset transfers/capital contributions"},{value:"royalties",label:"Royalties"},{value:"guarantees",label:"Guarantees"}];
    return opts.find(o=>o.value===t)?.label||t;
  }).join(", ");
  const consolidation = CONSOLIDATION_OPTIONS.find(o=>o.value===answers.consolidation_req)?.label || "";
  const leases = (answers.leases||[]).join(", ");
  const billPay = (answers.bill_pay_services||[]).filter(v=>v!=="none_billpay").join(", ");
  const hasCreditCard = (answers.financing||[]).includes("credit_card");
  const additionalContext = interviewData?.additionalContext || answers.additionalContext || "";
  const catchAllContext = interviewData?.catchAllContext || "";

  return `Business Name: ${session.businessName}
Engagement Type: ${session.mode === "agency" ? "Client engagement via " + (session.firmName||"accounting firm") : "In-house / internal"}
Industry: ${answers.industry_primary||"Not specified"}${answers.industry_sub ? " — " + answers.industry_sub : ""}
Entity Type: ${entity}
Entity Structure: ${entityStructure}${answers.num_entities ? " (" + answers.num_entities + " entities)" : ""}
Intercompany Transactions: ${intercoTypes||"None"}
Consolidation: ${consolidation||"Not applicable"}
Revenue Streams: ${revenues||"Not specified"}
COGS Structure: ${answers.has_cogs||"Not specified"}
Key Assets: ${assets||"None specified"}
Leases: ${leases||"None specified"}
Financing: ${financing||"Not specified"}
Credit Cards: ${hasCreditCard?"Yes — business credit card(s) in use":"No"}
Third-Party Bill Pay Platforms: ${billPay||"None"}
Labor: ${answers.employees||"Not specified"}
Accounting Method: ${answers.accounting_method||"Not specified"}
GAAP Framework: ${framework}
Target ERP: ${erp}${additionalContext ? "\n\nAdditional Context from Preparer:\n" + additionalContext : ""}${catchAllContext ? "\n\nFurther Context (Follow-up Stage):\n" + catchAllContext : ""}`.trim();
}

// -- API KEY SCREEN ---------------------------------------------------------
function ApiKeyScreen({ onSubmit, onSkip }) {
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);

  async function handleSubmit() {
    if (!key.trim().startsWith("sk-ant-")) {
      setError("That doesn't look like a valid Anthropic API key. Keys begin with sk-ant-");
      return;
    }
    setTesting(true);
    setError("");
    try {
      await callClaude(key.trim(),
        "You are a helpful assistant.",
        "Reply with only the word: ready",
        10
      );
      onSubmit(key.trim());
    } catch(e) {
      setError(`Key validation failed: ${e.message}`);
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="apikey-screen">
      <div className="apikey-card">
        <div className="apikey-logo">COA<span>Builder</span></div>
        <div style={{fontSize:"0.72rem",color:"var(--accent)",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",marginTop:4}}>AI-Powered Session</div>

        <div className="apikey-title">Connect your Anthropic API key</div>
        <div className="apikey-sub">
          COA Builder uses Claude to generate intelligent follow-up questions and produce a GAAP-framework-specific Chart of Accounts with professional accounting judgment.
        </div>

        {/* Feature callout */}
        <div style={{background:"var(--cream)",border:"1px solid var(--rule)",borderRadius:"var(--radius)",padding:"12px 16px",marginBottom:20}}>
          <div style={{fontSize:"0.7rem",fontWeight:600,letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--ink)",marginBottom:8}}>Required for AI-powered features</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {[
              ["✓","COA Build","Intelligent follow-up questions + GAAP-framework-specific account generation"],
              ["🔍","COA Audit","Upload an existing COA for structured analysis, recommendations, and a revised export"],
            ].map(([icon,label,desc])=>(
              <div key={label} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{fontSize:"0.9rem",flexShrink:0,marginTop:1}}>{icon}</span>
                <div>
                  <span style={{fontSize:"0.8rem",fontWeight:600,color:"var(--ink)"}}>{label} — </span>
                  <span style={{fontSize:"0.8rem",color:"var(--muted)"}}>{desc}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--rule-light)",fontSize:"0.74rem",color:"var(--muted-light)"}}>
            Without a key, COA Build uses standard generation only. COA Audit is not available.
          </div>
        </div>

        <div className="apikey-input-wrap">
          <input
            className="apikey-input"
            type={show ? "text" : "password"}
            placeholder="sk-ant-api03-..."
            value={key}
            onChange={e=>{setKey(e.target.value);setError("");}}
            onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
          />
          <button className="apikey-toggle" onClick={()=>setShow(v=>!v)}>{show?"Hide":"Show"}</button>
        </div>

        {error && <div style={{fontSize:"0.78rem",color:"var(--warn)",marginBottom:10,marginTop:4}}>{error}</div>}

        <div className="apikey-note">
          Your key is used only for this browser session. It is never stored, logged, or transmitted anywhere except directly to Anthropic's API.
        </div>

        <button className="btn-primary" onClick={handleSubmit} disabled={!key.trim()||testing}>
          {testing ? "Validating…" : "Connect & Continue *"}
        </button>

        <div className="apikey-divider"/>

        <div style={{fontSize:"0.8rem",color:"var(--muted)",marginBottom:10}}>
          Don't have a key yet?
        </div>
        <button className="apikey-skip" onClick={onSkip}>
          Continue without API key — COA Build only, standard generation
        </button>
      </div>
    </div>
  );
}

// -- AGENTIC FOLLOW-UP SCREEN -----------------------------------------------
function FollowUpScreen({ session, interviewData, apiKey, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  // Per question state:
  //   selectedOption[q.id] = index of chosen option, or -1 for none
  //   freeText[q.id]       = optional free-text describing the situation
  //   showFreeText[q.id]   = whether the free-text field is visible
  //   notSure[q.id]        = true if user flagged "Not sure — use AI judgment"
  const [selectedOption, setSelectedOption] = useState({});
  const [freeText, setFreeText] = useState({});
  const [showFreeText, setShowFreeText] = useState({});
  const [notSure, setNotSure] = useState({});
  const [error, setError] = useState("");

  const profile = buildBusinessProfile(session, interviewData.answers, interviewData);

  useEffect(()=>{
    generateFollowUps();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  async function generateFollowUps() {
    setLoading(true);
    setError("");
    try {
      const system = `You are a senior CPA and financial systems architect with deep expertise in Chart of Accounts design across US GAAP, IFRS, and international frameworks. You are helping build a precise, professional COA for a client.

Your task: review the business profile and identify 2-3 targeted clarifying questions that would meaningfully improve the COA output. Focus on nuances the structured interview may have missed — unusual revenue recognition patterns, hybrid business models, specific regulatory requirements, intercompany structures, or anything that would affect account naming or structure.

For each question, provide 3-4 concrete, mutually-exclusive answer options the user can pick from. The final option in each list MUST be exactly "Other — I will describe below" so the user can free-text a specific situation. Keep each option short (under 90 chars) and written so a non-accountant founder can understand it.

Respond ONLY with valid JSON in this exact format, no other text:
{
  "questions": [
    {
      "id": "fq1",
      "question": "Your question here",
      "rationale": "One sentence on why this affects the COA",
      "options": [
        "First concrete option",
        "Second concrete option",
        "Third concrete option",
        "Other — I will describe below"
      ]
    }
  ]
}`;

      const userMsg = `Business profile:
${profile}

Generate 2-3 targeted follow-up questions to refine the COA. Each question must include 3-4 answer options ending with "Other — I will describe below".`;
      const raw = await callClaude(apiKey, system, userMsg, 900);
      const clean = raw.replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(clean);
      const qs = (parsed.questions||[]).map(q=>({
        ...q,
        options: Array.isArray(q.options) && q.options.length
          ? q.options
          : ["Other — I will describe below"]
      }));
      setQuestions(qs);
      const initSel = {}, initText = {}, initShow = {}, initNot = {};
      qs.forEach(q=>{
        initSel[q.id] = -1;
        initText[q.id] = "";
        initShow[q.id] = false;
        initNot[q.id] = false;
      });
      setSelectedOption(initSel);
      setFreeText(initText);
      setShowFreeText(initShow);
      setNotSure(initNot);
    } catch(e) {
      setError(`Could not generate follow-up questions: ${e.message}. You can proceed without them.`);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }

  function isOtherOption(label) {
    return /^other\b/i.test((label||"").trim());
  }

  function pickOption(qId, idx, label) {
    setNotSure(s=>({...s,[qId]:false}));
    setSelectedOption(s=>({...s,[qId]:idx}));
    if(isOtherOption(label)){
      setShowFreeText(s=>({...s,[qId]:true}));
    }
  }

  function toggleNotSure(qId) {
    setNotSure(s=>{
      const next = !s[qId];
      if(next){
        setSelectedOption(p=>({...p,[qId]:-1}));
        setShowFreeText(p=>({...p,[qId]:false}));
        setFreeText(p=>({...p,[qId]:""}));
      }
      return {...s,[qId]:next};
    });
  }

  function toggleFreeText(qId) {
    setShowFreeText(s=>({...s,[qId]:!s[qId]}));
  }

  // A question counts as "answered" if user picked a non-Other option,
  // wrote free text, or explicitly flagged "Not sure" (Not sure is an
  // intentional answer routing the question to AI judgment).
  function isAnswered(q) {
    const sel = selectedOption[q.id];
    const txt = (freeText[q.id]||"").trim();
    const ns  = notSure[q.id];
    if(ns) return true;
    if(sel >= 0){
      const lbl = q.options[sel];
      if(isOtherOption(lbl)) return txt.length > 0;
      return true;
    }
    return txt.length > 0;
  }

  const answeredCount = questions.filter(isAnswered).length;

  // Combine structured selection + free text into a single rich answer
  // string for each question. Downstream generateAICOA reads followUpAnswers
  // as `interviewData.followUpAnswers[q.id]` and calls .trim() on it, so we
  // keep the per-question value as a single string for compatibility.
  function buildAnswerString(q) {
    if(notSure[q.id]) return "";  // empty -> falls through to AI judgment
    const sel = selectedOption[q.id];
    const txt = (freeText[q.id]||"").trim();
    const parts = [];
    if(sel >= 0){
      const lbl = q.options[sel];
      if(!isOtherOption(lbl)) parts.push(lbl);
    }
    if(txt) parts.push(`Additional detail: ${txt}`);
    return parts.join(" — ");
  }

  function handleProceed() {
    const answers = {};
    questions.forEach(q=>{ answers[q.id] = buildAnswerString(q); });
    const enriched = {
      ...interviewData,
      followUpAnswers: answers,
      followUpQuestions: questions,
      followUpNotSure: notSure
    };
    onComplete(enriched);
  }

  return (
    <div className="followup-screen">
      <div className="followup-card">
        <div className="followup-eyebrow">Refinement Questions</div>
        <h2 className="followup-headline">A few targeted questions<br/>before we build your COA.</h2>
        <p className="followup-sub">
          Based on {session.businessName}'s profile, these questions will sharpen the account structure. Pick the closest match, describe your specific situation, or flag any question for AI judgment.
        </p>

        {loading && (
          <div className="followup-loading">
            <div className="followup-spinner"/>
            <div className="followup-loading-text">Reviewing business profile…</div>
          </div>
        )}

        {error && (
          <div className="followup-error">
            <span>⚠</span><span>{error}</span>
          </div>
        )}

        {!loading && questions.length > 0 && (
          <div className="followup-qa">
            {questions.map((q,i)=>(
              <div key={q.id} className="followup-item">
                <div className="followup-q-label">Question {i+1} of {questions.length}</div>
                <div className="followup-q-text">{q.question}</div>
                <div style={{fontSize:"0.74rem",color:"var(--muted)",marginBottom:14,fontStyle:"italic"}}>{q.rationale}</div>

                <div className="followup-options">
                  {q.options.map((opt,idx)=>{
                    const selected = selectedOption[q.id] === idx && !notSure[q.id];
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`followup-option${selected?" selected":""}`}
                        onClick={()=>pickOption(q.id, idx, opt)}>
                        <span className="followup-option-radio"/>
                        <span>{opt}</span>
                      </button>
                    );
                  })}
                </div>

                {!showFreeText[q.id] && !notSure[q.id] && (
                  <button
                    type="button"
                    className="followup-other-toggle"
                    onClick={()=>toggleFreeText(q.id)}>
                    Or describe your specific situation
                  </button>
                )}

                {showFreeText[q.id] && !notSure[q.id] && (
                  <textarea
                    className="followup-textarea"
                    placeholder="Describe your situation in your own words…"
                    value={freeText[q.id]||""}
                    onChange={e=>setFreeText(s=>({...s,[q.id]:e.target.value}))}
                  />
                )}

                <button
                  type="button"
                  className={`unsure-btn${notSure[q.id]?" active":""}`}
                  onClick={()=>toggleNotSure(q.id)}
                  style={{marginTop:12}}>
                  <span>👤</span>
                  {notSure[q.id]?"Flagged — AI will use its judgment":"Not sure — let AI decide"}
                </button>
                {notSure[q.id]&&<div className="uncertain-note">⚠ This question will be answered using AI judgment based on your business profile.</div>}
              </div>
            ))}
          </div>
        )}

        {!loading && (
          <div className="followup-nav">
            <div className="followup-counter">
              {questions.length > 0
                ? <><strong>{answeredCount} of {questions.length} answered</strong> — unanswered questions will use AI judgment</>
                : "Proceeding with structured interview data."}
            </div>
            <button className="btn-next final" onClick={handleProceed}>
              Generate COA *
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// -- AI COA GENERATOR -------------------------------------------------------
async function generateAICOA(apiKey, session, interviewData) {
  const profile = buildBusinessProfile(session, interviewData.answers, interviewData);
  const framework = GAAP_FRAMEWORKS.find(f=>f.value===interviewData.answers.gaap_framework)?.label || "US GAAP";

  const followUpContext = interviewData.followUpQuestions?.length > 0
    ? "\n\nAdditional context from follow-up questions:\n" +
      interviewData.followUpQuestions
        .filter(q=>interviewData.followUpAnswers?.[q.id]?.trim())
        .map(q=>`Q: ${q.question}\nA: ${interviewData.followUpAnswers[q.id]}`)
        .join("\n\n")
    : "";

  const system = `You are a Big 4-trained CPA and financial systems architect with 20+ years of experience designing Charts of Accounts for companies across industries and jurisdictions. You have deep expertise in ${framework} and understand the specific account naming conventions, classification rules, and structural requirements of that framework.

Your task is to generate a complete, professional Chart of Accounts. You must:
1. Use ${framework}-appropriate account naming conventions throughout — not generic names
2. Apply the correct account numbering scheme (1000s=Assets, 2000s=Liabilities, 3000s=Equity, 4000s=Revenue, 5000s=COGS, 6000s=OpEx, 7000s=Other)
3. Include only accounts relevant to this specific business — no generic catch-alls that don't apply
4. Flag any accounts that have special ${framework} treatment with a brief note in the description
5. For IFRS: use IFRS-specific terminology (e.g. "Trade and Other Receivables" not "Accounts Receivable", "Property Plant and Equipment" not "Fixed Assets", capitalize development costs per IAS 38)
6. For UK GAAP: use UK terminology (Debtors, Creditors, etc.)
7. For Swiss GAAP FER: reflect Swiss statutory reserve structure
8. For US GAAP: use ASC codification-aligned naming

Respond ONLY with valid JSON, no other text:
{
  "accounts": [
    {
      "num": "1000",
      "name": "Account Name",
      "type": "Account Type",
      "desc": "Max 10 words — note any framework-specific treatment",
      "section": null
    },
    {
      "section": "Section Header Name",
      "num": null,
      "name": null,
      "type": null,
      "desc": null
    }
  ]
}

Use section objects (where num/name/type/desc are null and section has a value) to group accounts. Include section headers before each group.

ADDITIONAL REQUIRED RULES:
A. LEASES: If leases indicated, always generate: Right-of-Use Asset — Operating Lease | Accumulated Amortization — ROU Asset | Operating Lease Liability — Current | Operating Lease Liability — Long-Term | Operating Lease Expense. Finance leases additionally need: Finance Lease ROU Asset | Finance Lease Liability (Current + Long-Term) | Finance Lease Interest Expense | Finance Lease Amortization Expense.
B. EMPLOYEE vs CONTRACTOR: Always separate W-2 and 1099. Base Salaries & Wages (W-2) and Contract Labor — [function] (1099) must never be one account. If S-Corp, add Owner Salary — Shareholder W-2 as a distinct account.
C. CREDIT CARDS: If business credit cards in financing, generate Credit Card Payable — [Card Name] as Credit Card type. Never roll into Accounts Payable.
D. THIRD-PARTY BILL PAY: If Bill.com, Ramp, Brex, PayPal Business, or Melio indicated, generate a clearing liability per platform (e.g. Bill.com Clearing Payable). Distinct from AP.
E. REPAIRS & MAINTENANCE: Always include as a standalone expense account.
F. REFERRAL FEES: For legal, financial advisory, insurance, real estate, or professional services, include Referral Fee Income and Referral Fee Expense as separate accounts.
G. ADDITIONAL CONTEXT: Any preparer notes in the business profile are HIGHEST PRIORITY. Generate accounts specifically responsive to every nuance mentioned.

Descriptions up to 15 words. Do not include any text outside the JSON object.`;

  const userMsg = `Generate a complete Chart of Accounts for this business:

${profile}${followUpContext}`;

  const raw = await callClaude(apiKey, system, userMsg, 12000);

  // Robust parser — recovers from truncated JSON by extracting
  // all well-formed account objects even if the array is cut off.
  function parseAccountsRobust(text) {
    const clean = text.replace(/```json|```/g,"").trim();

    // First try clean parse
    try {
      const parsed = JSON.parse(clean);
      if (parsed.accounts?.length) return parsed.accounts;
    } catch(_) {}

    // Partial recovery: extract all {...} objects from the accounts array
    const accounts = [];
    // Match individual JSON objects inside the accounts array
    const objRegex = /\{[^{}]*(?:"num"[^{}]*|"section"[^{}]*)[^{}]*\}/g;
    let match;
    while ((match = objRegex.exec(clean)) !== null) {
      try {
        const obj = JSON.parse(match[0]);
        if (obj.section || (obj.num && obj.name)) accounts.push(obj);
      } catch(_) {}
    }
    if (accounts.length > 0) return accounts;

    // If all else fails throw so we fall back to deterministic
    throw new Error("Could not parse COA response — falling back to standard generation.");
  }

  const accounts = parseAccountsRobust(raw);
  return accounts.map((r,i)=>({
    ...r,
    _id: i,
    flagged: false,
    deleted: false,
  }));
}

// -- AI ACCOUNT SUGGESTIONS -------------------------------------------------
async function getAccountSuggestions(apiKey, row, session, answers) {
  const framework = GAAP_FRAMEWORKS.find(f=>f.value===answers.gaap_framework)?.label || "US GAAP";
  const industry = answers.industry_primary || "General";

  const system = `You are a senior CPA specialising in ${framework} for ${industry} companies. Suggest alternative account names and related accounts for a Chart of Accounts. Be concise and technically precise.

Respond ONLY with valid JSON:
{
  "suggestions": [
    {
      "name": "Alternative Account Name",
      "type": "Account Type",
      "rationale": "One sentence — why this alternative under ${framework}"
    }
  ]
}`;

  const userMsg = `Current account: ${row.num} — ${row.name} (${row.type})
Description: ${row.desc}
Industry: ${industry}
Framework: ${framework}

Suggest 3 alternative names or closely related accounts this business might consider.`;

  const raw = await callClaude(apiKey, system, userMsg, 400);
  const clean = raw.replace(/```json|```/g,"").trim();
  const parsed = JSON.parse(clean);
  return parsed.suggestions || [];
}


// ---------------------------------------------
//  FLOW SELECT — Build New vs Audit Existing
// ---------------------------------------------
function FlowSelectScreen({onSelect}) {
  const [flow, setFlow] = useState(null);
  return (
    <div className="flow-select-screen">
      <div className="screen-eyebrow">Choose Your Workflow</div>
      <h2 className="screen-headline" style={{marginBottom:10}}>What would you like to do?</h2>
      <p className="screen-sub" style={{marginBottom:36}}>
        COA Builder supports both new builds and structured reviews of existing Charts of Accounts.
      </p>
      <div className="flow-cards">
        {[
          {
            key:"build",
            icon:"🏗️",
            title:"Build a New COA",
            desc:"Start from scratch. Answer a guided interview and generate a complete, GAAP-aligned Chart of Accounts tailored to the entity — ready to import into your target ERP.",
            tag:"Build",
            tagClass:"build"
          },
          {
            key:"audit",
            icon:"🔍",
            title:"Audit & Clean Existing COA",
            desc:"Upload a client's current Chart of Accounts. COA Builder analyzes it against GAAP conventions and best practices — then returns structured recommendations you can accept, modify, or ignore.",
            tag:"New Feature",
            tagClass:"audit"
          }
        ].map(f=>(
          <button key={f.key}
            className={`flow-card${flow===f.key?" selected":""}`}
            onClick={()=>setFlow(f.key)}>
            <div className="flow-card-check">✔</div>
            <span className="flow-card-icon">{f.icon}</span>
            <div className="flow-card-title">{f.title}</div>
            <div className="flow-card-desc">{f.desc}</div>
            <span className={`flow-card-tag ${f.tagClass}`}>{f.tag}</span>
          </button>
        ))}
      </div>
      {flow && (
        <button
          className="btn-cta"
          style={{marginTop:28}}
          onClick={()=>onSelect(flow)}>
          {flow==="audit" ? "Continue to Upload *" : "Continue to Setup *"}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------
//  COA UPLOAD SCREEN
// ---------------------------------------------

// Parse CSV into array of objects
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if(lines.length < 2) return [];
  // Smart header detection: find first row with 2+ populated cells, skip title/blank rows
  let headerIdx = 0;
  const parseRowSimple = line => line.split(line.includes('	') ? '	' : ',').map(c => c.replace(/^"|"$/g,'').trim());
  for(let i=0;i<Math.min(lines.length,10);i++){
    if(!lines[i].trim()) continue;
    const cells = parseRowSimple(lines[i]);
    if(cells.filter(cell=>cell.length>0).length >= 2){ headerIdx = i; break; }
  }
  const delim = lines[headerIdx].includes('	') ? '	' : ',';
  const parseRow = line => {
    const cells = [];
    let cur = "", inQuote = false;
    for(let i=0;i<line.length;i++){
      const ch = line[i];
      if(ch==='"'){ inQuote=!inQuote; continue; }
      if(ch===delim && !inQuote){ cells.push(cur.trim()); cur=""; continue; }
      cur += ch;
    }
    cells.push(cur.trim());
    return cells;
  };
  const headers = parseRow(lines[headerIdx]);
  return lines.slice(headerIdx+1).filter(l=>l.trim()).map(line=>{
    const cells = parseRow(line);
    const obj = {};
    headers.forEach((h,i)=>{ obj[h] = cells[i]||""; });
    return obj;
  });
}

// Detect likely column mapping from headers
function detectColumns(rows) {
  if(!rows.length) return {};
  const keys = Object.keys(rows[0]);
  const find = (...terms) => keys.find(k=>terms.some(t=>k.toLowerCase().includes(t.toLowerCase())))||"";
  return {
    number:  find("number","num","code","id","acct"),
    name:    find("name","description","account","title"),
    type:    find("type","category","class","kind"),
    detail:  find("detail","subtype","sub"),
    desc:    find("description","desc","note","memo"),
  };
}

// Known platform column mappings for auto-fill
const PLATFORM_COL_MAPS = {
  qbo:   { number:"",          name:"Full Name",    type:"Type",        desc:"Detail Type" },
  xero:  { number:"Code",      name:"Name",         type:"Type",        desc:"Description" },
  sage50:{ number:"Account ID",name:"Account Description", type:"Account Type", desc:"" },
  generic:{ number:"",         name:"",             type:"",            desc:"" },
};

// Helper text for each COA Builder column field
const COL_HELPER_TEXT = {
  number: {
    label: "Account Number",
    what: "A numeric code identifying the account (e.g. 1000, 4200). Optional — QBO does not use account numbers natively.",
    qbo: "QBO does not export account numbers by default. Leave unmapped unless you have added custom numbers.",
    xero: "Map to the 'Code' column in Xero exports.",
  },
  name: {
    label: "Account Name ★ (required)",
    what: "The display name of the account (e.g. 'Checking Account', 'Service Revenue'). This is the primary field COA Builder uses for analysis.",
    qbo: "Map to 'Full Name' in QBO exports — this is the complete account name including parent hierarchy.",
    xero: "Map to 'Name' in Xero exports.",
  },
  type: {
    label: "Account Type",
    what: "The highest-level classification: Bank, Current Asset, Fixed Asset, Accounts Payable, Income, Expense, etc. COA Builder uses this to identify misclassifications.",
    qbo: "Map to 'Type' in QBO exports (e.g. Bank, Accounts Receivable, Other Current Asset, Income, Expense).",
    xero: "Map to 'Type' in Xero exports (e.g. BANK, CURRENT, REVENUE, OVERHEADS).",
  },
  desc: {
    label: "Description / Detail Type",
    what: "A sub-classification or description. In QBO this is 'Detail Type' (e.g. Checking, Undeposited Funds, Buildings). Optional but improves audit quality.",
    qbo: "Map to 'Detail Type' in QBO exports — one level deeper than Type (e.g. Checking, Savings, Accounts Receivable).",
    xero: "Map to 'Description' in Xero exports.",
  },
};

function COAUploadScreen({session, onComplete, onBack}) {
  const [file, setFile] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [colMap, setColMap] = useState({number:"",name:"",type:"",desc:""});
  const [dragover, setDragover] = useState(false);
  const [error, setError] = useState("");
  const [platform, setPlatform] = useState(null);
  const [openHelper, setOpenHelper] = useState(null); // which col helper is open
  // Fix: ref to reset the file input DOM element after X removal
  const fileInputRef = useRef(null);

  function resetFile() {
    setFile(null);
    setRawRows([]);
    setHeaders([]);
    setColMap({number:"",name:"",type:"",desc:""});
    setPlatform(null);
    setError("");
    // Critical: reset the input element value so onChange fires again
    if(fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleFile(f) {
    if(!f) return;
    if(!f.name.match(/\.(csv|txt|tsv)$/i)){
      setError("Please upload a CSV file exported from your accounting platform.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      const rows = parseCSV(text);
      if(!rows.length){ setError("Could not parse file — please check it is a valid CSV."); return; }
      setRawRows(rows);
      const hdrs = Object.keys(rows[0]);
      setHeaders(hdrs);
      const detected = detectColumns(rows);
      setColMap({
        number: detected.number||"",
        name:   detected.name||"",
        type:   detected.type||"",
        desc:   detected.detail||detected.desc||"",
      });
      // Auto-detect platform from column names
      if(hdrs.includes("Full Name") || hdrs.includes("Detail Type")){
        setPlatform("qbo");
        setColMap({number:"", name:"Full Name", type:"Type", desc:"Detail Type"});
      } else if(hdrs.includes("Code") && hdrs.includes("Tax Code")){
        setPlatform("xero");
        setColMap({number:"Code", name:"Name", type:"Type", desc:"Description"});
      } else if(hdrs.includes("Account ID") && hdrs.includes("Account Description")){
        setPlatform("sage50");
        setColMap({number:"Account ID", name:"Account Description", type:"Account Type", desc:""});
      }
      setFile(f);
    };
    reader.readAsText(f);
  }

  function applyPlatformPreset(p) {
    setPlatform(p);
    const preset = PLATFORM_COL_MAPS[p];
    // Only apply preset values that exist in the current headers
    setColMap({
      number: headers.includes(preset.number) ? preset.number : "",
      name:   headers.includes(preset.name)   ? preset.name   : "",
      type:   headers.includes(preset.type)   ? preset.type   : "",
      desc:   headers.includes(preset.desc)   ? preset.desc   : "",
    });
  }

  function handleDrop(e) {
    e.preventDefault(); setDragover(false);
    const f = e.dataTransfer.files[0];
    if(f) handleFile(f);
  }

  function handleProceed() {
    if(!colMap.name){ setError("Please map at least the Account Name column."); return; }
    const accounts = rawRows.map((row,i)=>({
      _id: i,
      num:  colMap.number ? (row[colMap.number]||"").trim() : "",
      name: (row[colMap.name]||"").trim(),
      type: colMap.type   ? (row[colMap.type]||"").trim()   : "",
      desc: colMap.desc   ? (row[colMap.desc]||"").trim()   : "",
      original: true,
    })).filter(r=>r.name);
    onComplete({ accounts, fileName: file.name, rowCount: accounts.length, platform });
  }

  const previewRows = rawRows.slice(0,4);
  const canProceed = file && colMap.name;

  return (
    <div className="upload-screen">
      <div className="upload-card">
        <div className="screen-eyebrow" style={{textAlign:"left",marginBottom:8}}>Audit & Clean — Step 1 of 3</div>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.5rem",fontWeight:600,marginBottom:8,color:"var(--ink)"}}>
          Upload the existing Chart of Accounts
        </h2>
        <p style={{fontSize:"0.86rem",color:"var(--muted)",marginBottom:24,lineHeight:1.6}}>
          Export a CSV from QuickBooks, Xero, Sage, or any accounting platform and upload it here.
        </p>

        <div
          className={`upload-zone${dragover?" dragover":""}${file?" has-file":""}`}
          onDragOver={e=>{e.preventDefault();setDragover(true);}}
          onDragLeave={()=>setDragover(false)}
          onDrop={handleDrop}
        >
          {/* Fix: attach ref so we can reset value after X removal */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,.tsv"
            className="upload-input"
            onChange={e=>handleFile(e.target.files[0])}
          />
          {!file ? (
            <>
              <span className="upload-icon">📂</span>
              <div className="upload-title">Drop your COA file here</div>
              <div className="upload-sub">or click to browse</div>
              <div className="upload-formats">
                {["QBO Export","Xero Export","Sage 50","Generic CSV"].map(f=>(
                  <span key={f} className="upload-format-badge">{f}</span>
                ))}
              </div>
            </>
          ) : (
            <>
              <span className="upload-icon">✅</span>
              <div className="upload-title">File ready — click to replace</div>
              <div className="upload-sub">{rawRows.length} accounts detected</div>
            </>
          )}
        </div>

        {file && (
          <div className="upload-file-info">
            <span className="upload-file-icon">📋</span>
            <div>
              <div className="upload-file-name">{file.name}</div>
              <div className="upload-file-meta">{rawRows.length} rows · {headers.length} columns detected</div>
            </div>
            <button className="upload-file-remove" title="Remove file" onClick={resetFile}>✖</button>
          </div>
        )}

        {error && <div style={{fontSize:"0.8rem",color:"var(--warn)",marginTop:10}}>{error}</div>}

        {file && headers.length > 0 && (
          <div className="col-map-card">
            <div className="col-map-title">Confirm Column Mapping</div>

            {/* Platform quick-select */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:"0.72rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--muted)",marginBottom:8}}>
                Which platform did you export from?
              </div>
              <div className="platform-select-row">
                {[
                  {key:"qbo",   label:"QuickBooks Online"},
                  {key:"xero",  label:"Xero"},
                  {key:"sage50",label:"Sage 50"},
                  {key:"generic",label:"Other / Generic"},
                ].map(p=>(
                  <button
                    key={p.key}
                    className={`platform-btn${platform===p.key?" active":""}`}
                    onClick={()=>applyPlatformPreset(p.key)}>
                    {p.label}
                  </button>
                ))}
              </div>
              {platform&&platform!=="generic"&&(
                <div style={{fontSize:"0.76rem",color:"var(--accent)",marginTop:4}}>
                  ✔ Column mapping pre-filled for {platform==="qbo"?"QuickBooks Online":platform==="xero"?"Xero":"Sage 50"}. Verify below.
                </div>
              )}
            </div>

            <div className="col-map-sub">Map your file's columns to COA Builder fields. Click ? for guidance on each field.</div>

            <div className="col-map-grid">
              {[
                {key:"number", label:"Account Number"},
                {key:"name",   label:"Account Name ★"},
                {key:"type",   label:"Account Type"},
                {key:"desc",   label:"Detail Type / Description"},
              ].map(({key,label})=>(
                <div className="field" key={key}>
                  <label style={{fontSize:"0.7rem",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--muted)",marginBottom:4,display:"flex",alignItems:"center",gap:4}}>
                    {label}
                    <span
                      className="col-map-helper-icon"
                      onClick={()=>setOpenHelper(openHelper===key?null:key)}
                      title="What is this field?">
                      i
                    </span>
                  </label>
                  {openHelper===key&&(
                    <div className="col-map-helper-panel">
                      <div>{COL_HELPER_TEXT[key]?.what}</div>
                      {platform&&platform!=="generic"&&COL_HELPER_TEXT[key]?.[platform]&&(
                        <div style={{marginTop:6,paddingTop:6,borderTop:"1px solid var(--accent-pale)"}}>
                          <strong>{platform==="qbo"?"QuickBooks:":platform==="xero"?"Xero:":"Sage 50:"}</strong> {COL_HELPER_TEXT[key][platform]}
                        </div>
                      )}
                    </div>
                  )}
                  <select className="inline-select" value={colMap[key]}
                    onChange={e=>setColMap(m=>({...m,[key]:e.target.value}))}>
                    <option value="">— not mapped —</option>
                    {headers.map(h=><option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {previewRows.length > 0 && colMap.name && (
              <div className="col-map-preview">
                <div className="col-map-preview-title">Preview — first {previewRows.length} rows</div>
                {previewRows.map((row,i)=>(
                  <div key={i} className="col-map-row">
                    <span className="col-map-cell" style={{color:"var(--muted)",width:70,flex:"0 0 70px"}}>{colMap.number?row[colMap.number]:""}</span>
                    <span className="col-map-cell" style={{fontWeight:500,color:"var(--ink)",flex:2}}>{colMap.name?row[colMap.name]:""}</span>
                    <span className="col-map-cell" style={{flex:1}}>{colMap.type?row[colMap.type]:""}</span>
                    <span className="col-map-cell" style={{color:"var(--muted)",flex:1}}>{colMap.desc?row[colMap.desc]:""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{display:"flex",gap:10,marginTop:24}}>
          <button className="btn-back" onClick={onBack}>← Back</button>
          <button className="btn-primary" style={{flex:1,marginTop:0}}
            disabled={!canProceed} onClick={handleProceed}>
            Confirm & Continue *
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------
//  AI COA AUDIT FUNCTION
// ---------------------------------------------
async function analyzeExistingCOA(apiKey, session, interviewData, uploadedAccounts) {
  const profile = buildBusinessProfile(session, interviewData.answers, interviewData);
  const framework = GAAP_FRAMEWORKS.find(f=>f.value===interviewData.answers.gaap_framework)?.label || "US GAAP";

  // Format uploaded COA for Claude
  const coaText = uploadedAccounts
    .filter(r=>!r.section)
    .map(r=>`${r.num||"?"} | ${r.name} | ${r.type||"?"} | ${r.desc||""}`)
    .join("\n");

  const system = `You are a Big 4-trained CPA and financial systems architect with 20+ years of experience auditing and redesigning Charts of Accounts. You specialize in ${framework} and have deep knowledge of GAAP naming conventions, account classification rules, and best practices for the industries you work with.

Your task: analyze the provided Chart of Accounts against the business profile and ${framework} conventions. Identify genuine issues — not stylistic preferences — that would cause problems in financial reporting, audit, or ERP configuration.

For each finding, classify severity:
- CRITICAL: Misclassification that distorts financial statements (e.g. expense coded as asset, liability coded as equity)
- WARNING: Non-standard practice that causes reporting confusion or audit questions
- SUGGESTION: Naming convention improvement, missing recommended account, or structural refinement

Respond ONLY with valid JSON, no other text:
{
  "findings": [
    {
      "id": "f1",
      "severity": "CRITICAL",
      "account_num": "5000",
      "account_name": "Office Equipment",
      "issue_type": "Misclassification",
      "issue": "One sentence describing the specific problem",
      "recommendation": "Specific, actionable fix — include suggested account name/number/type where applicable",
      "recommended_name": "Suggested account name if renaming is the fix, otherwise null",
      "recommended_type": "Suggested account type if reclassification is the fix, otherwise null"
    }
  ],
  "overall_assessment": "2-3 sentence summary of the COA quality and primary concerns"
}

Be clinically honest and appropriately aggressive. A thorough audit should produce 8-20 findings. Challenge naming conventions, collapsed accounts, missing granularity, and relevance. If the COA looks like a generic template rather than purpose-built for this entity, say so in the overall_assessment.`;

  const userMsg = `Business Profile:
${profile}

Existing Chart of Accounts (${uploadedAccounts.filter(r=>!r.section).length} accounts):
Num | Name | Type | Description
${coaText}

Analyze this COA and return findings.
/* ── FINISH LINE ── */
.finish-line-bar { margin-top:32px; padding:24px 28px; background:var(--accent-xpale); border:2px solid var(--accent-pale); border-radius:var(--radius-lg); display:flex; align-items:center; justify-content:space-between; gap:20px; flex-wrap:wrap; }
.finish-line-left { display:flex; align-items:center; gap:16px; }
.finish-line-check { flex-shrink:0; }
.finish-line-title { font-family:'Playfair Display',serif; font-size:1.25rem; font-weight:700; color:var(--ink); margin-bottom:4px; }
.finish-line-sub { font-size:0.82rem; color:var(--accent); font-weight:500; }
.finish-line-actions { display:flex; gap:10px; align-items:center; flex-shrink:0; flex-wrap:wrap; }
`;

  const raw = await callClaude(apiKey, system, userMsg, 10000);
  const clean = raw.replace(/```json|```/g,"").trim();
  try {
    return JSON.parse(clean);
  } catch(e) {
    // Partial recovery
    const match = clean.match(/"findings"\s*:\s*(\[.*)/s);
    if(match) {
      try { return { findings: JSON.parse(match[1].replace(/}[^}]*$/,"}")+"]"), overall_assessment:"" }; } catch(_){}
    }
    throw new Error("Could not parse audit response. Please try again.");
  }
}

// ---------------------------------------------
//  AUDIT RECOMMENDATIONS SCREEN
// ---------------------------------------------
function AuditScreen({session, interviewData, uploadData, apiKey, onComplete, onBack}) {
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState([]);
  const [assessment, setAssessment] = useState("");
  const [error, setError] = useState("");
  const [decisions, setDecisions] = useState({}); // {findingId: {action:"accept"|"modify"|"ignore", modifiedValue:""}}
  const [expanded, setExpanded] = useState({});

  useEffect(()=>{ runAudit(); // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  async function runAudit() {
    setLoading(true); setError("");
    try {
      const result = await analyzeExistingCOA(apiKey, session, interviewData, uploadData.accounts);
      setFindings(result.findings||[]);
      setAssessment(result.overall_assessment||"");
      // Default all to "review" state (no decision yet)
      const d = {};
      (result.findings||[]).forEach(f=>{ d[f.id]={action:null,modifiedValue:""}; });
      setDecisions(d);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function setDecision(id, action) {
    setDecisions(d=>({...d,[id]:{...d[id],action: d[id]?.action===action ? null : action}}));
  }
  function setModified(id, val) {
    setDecisions(d=>({...d,[id]:{...d[id],modifiedValue:val}}));
  }
  function toggleExpanded(id) {
    setExpanded(e=>({...e,[id]:!e[id]}));
  }

  function handleGenerateRevised() {
    // Apply accepted/modified recommendations to the uploaded accounts
    const revisedAccounts = uploadData.accounts.map(acct=>{
      // Find any accepted finding for this account
      const finding = findings.find(f=>
        f.account_name === acct.name ||
        (f.account_num && f.account_num === acct.num)
      );
      if(!finding) return acct;
      const dec = decisions[finding.id];
      if(!dec || dec.action==="ignore" || dec.action===null) return acct;
      if(dec.action==="accept") {
        return {
          ...acct,
          name: finding.recommended_name || acct.name,
          type: finding.recommended_type || acct.type,
          _revised: true,
          _finding: finding.issue,
        };
      }
      if(dec.action==="modify" && dec.modifiedValue) {
        return {...acct, name: dec.modifiedValue, _revised:true, _finding: finding.issue};
      }
      return acct;
    });

    // Build audit report summary
    const accepted = Object.values(decisions).filter(d=>d.action==="accept").length;
    const modified = Object.values(decisions).filter(d=>d.action==="modify").length;
    const ignored  = Object.values(decisions).filter(d=>d.action==="ignore").length;
    const pending  = Object.values(decisions).filter(d=>d.action===null).length;

    onComplete({
      revisedAccounts,
      findings,
      decisions,
      assessment,
      auditSummary: { accepted, modified, ignored, pending, total: findings.length }
    });
  }

  const critCount = findings.filter(f=>f.severity==="CRITICAL").length;
  const warnCount = findings.filter(f=>f.severity==="WARNING").length;
  const suggCount = findings.filter(f=>f.severity==="SUGGESTION").length;
  const decidedCount = Object.values(decisions).filter(d=>d.action!==null).length;

  const severityOrder = {CRITICAL:0,WARNING:1,SUGGESTION:2};
  const sorted = [...findings].sort((a,b)=>severityOrder[a.severity]-severityOrder[b.severity]);

  return (
    <div className="audit-screen">
      <div className="audit-main">
        <h2 className="audit-headline">COA Audit — {session.businessName}</h2>
        <p className="audit-sub">
          {loading ? "Analyzing your Chart of Accounts…" :
           error ? error :
           assessment || `${findings.length} finding${findings.length!==1?"s":""} identified. Review each recommendation below, then generate your revised COA.`}
        </p>

        {loading && (
          <div className="audit-loading">
            <div className="audit-loading-spinner"/>
            <div style={{fontSize:"0.88rem",color:"var(--muted)"}}>Reviewing {uploadData.rowCount} accounts against {interviewData.answers.industry_primary||"business"} profile…</div>
          </div>
        )}

        {!loading && !error && findings.length > 0 && (
          <>
            <div className="audit-summary-bar">
              <div className="audit-stat critical">
                <div className="audit-stat-number">{critCount}</div>
                <div className="audit-stat-label">Critical</div>
              </div>
              <div className="audit-stat warning">
                <div className="audit-stat-number">{warnCount}</div>
                <div className="audit-stat-label">Warnings</div>
              </div>
              <div className="audit-stat suggestion">
                <div className="audit-stat-number">{suggCount}</div>
                <div className="audit-stat-label">Suggestions</div>
              </div>
              <div className="audit-stat total">
                <div className="audit-stat-number">{decidedCount}/{findings.length}</div>
                <div className="audit-stat-label">Reviewed</div>
              </div>
            </div>

            {sorted.map(finding=>{
              const dec = decisions[finding.id]||{action:null,modifiedValue:""};
              const isExpanded = expanded[finding.id];
              return (
                <div key={finding.id}
                  className={`finding-card${dec.action==="accept"||dec.action==="modify"?" accepted":dec.action==="ignore"?" ignored":""}`}>
                  <div className="finding-header" onClick={()=>toggleExpanded(finding.id)}>
                    <span className={`finding-severity ${finding.severity.toLowerCase()}`}>{finding.severity}</span>
                    {finding.account_num&&<span className="finding-account">{finding.account_num}</span>}
                    <span className="finding-issue">{finding.account_name} — {finding.issue_type}</span>
                    <div className="finding-actions" onClick={e=>e.stopPropagation()}>
                      <button className={`btn-accept${dec.action==="accept"?" active":""}`}
                        onClick={()=>setDecision(finding.id,"accept")}>
                        {dec.action==="accept"?"✔ Accepted":"Accept"}
                      </button>
                      <button className={`btn-modify${dec.action==="modify"?" active":""}`}
                        onClick={()=>{setDecision(finding.id,"modify");setExpanded(e=>({...e,[finding.id]:true}));}}>
                        Modify
                      </button>
                      <button className={`btn-ignore${dec.action==="ignore"?" active":""}`}
                        onClick={()=>setDecision(finding.id,"ignore")}>
                        {dec.action==="ignore"?"Ignored":"Ignore"}
                      </button>
                    </div>
                  </div>
                  {isExpanded&&(
                    <div className="finding-body">
                      <div className="finding-detail">{finding.issue}</div>
                      <div className="finding-rec-label">Recommendation</div>
                      <div className="finding-recommendation">{finding.recommendation}</div>
                      {dec.action==="modify"&&(
                        <>
                          <input className="finding-modify-input"
                            placeholder="Enter your modified account name or change…"
                            value={dec.modifiedValue}
                            onChange={e=>setModified(finding.id,e.target.value)}/>
                          {dec.modifiedValue&&(
                            <button className="finding-modify-save"
                              onClick={()=>setExpanded(e=>({...e,[finding.id]:false}))}>
                              Save Modification
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="audit-actions">
              <button className="btn-generate-audit" onClick={handleGenerateRevised}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8h12M8 2l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Generate Revised COA
              </button>
              <div style={{fontSize:"0.78rem",color:"var(--muted)",lineHeight:1.5}}>
                {decidedCount < findings.length
                  ? <><strong style={{color:"var(--warn)"}}>{findings.length - decidedCount} finding{findings.length-decidedCount!==1?"s":""} not yet reviewed.</strong> You can proceed — unreviewed items will be left unchanged.</>
                  : "All findings reviewed. Ready to generate your revised COA."
                }
              </div>
            </div>
          </>
        )}

        {!loading && !error && findings.length === 0 && (
          <div style={{background:"var(--accent-xpale)",border:"1px solid var(--accent-pale)",borderRadius:"var(--radius-lg)",padding:"32px",textAlign:"center",marginTop:24}}>
            <div style={{fontSize:"2rem",marginBottom:12}}>✅</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.1rem",fontWeight:600,marginBottom:8}}>No significant issues found</div>
            <div style={{fontSize:"0.88rem",color:"var(--muted)",lineHeight:1.6}}>
              This Chart of Accounts appears well-structured for the business profile provided.
              You can still export it as a clean reference copy.
            </div>
            <button className="btn-generate-audit" style={{margin:"20px auto 0",display:"flex"}} onClick={handleGenerateRevised}>
              Export Clean COA
            </button>
          </div>
        )}

        {error && (
          <div style={{background:"var(--warn-pale)",border:"1px solid #f0b07a",borderRadius:"var(--radius)",padding:"16px 20px",marginTop:20,fontSize:"0.84rem",color:"var(--ink-soft)"}}>
            <strong>Analysis failed:</strong> {error}
            <button onClick={runAudit} style={{marginLeft:16,padding:"6px 14px",background:"var(--warn)",color:"white",border:"none",borderRadius:"var(--radius)",cursor:"pointer",fontSize:"0.8rem"}}>Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------
//  HELP BUTTON — persistent floating CTA
// ---------------------------------------------
function HelpButton() {
  return (
    <a
      className="help-fab"
      href="https://calendly.com/phil-adastracfo/discovery-briefing"
      target="_blank"
      rel="noopener noreferrer"
      title="Talk to a CPA — schedule time with a real accounting professional"
    >
      <div className="help-fab-dot"/>
      <div className="help-fab-label">
        <span className="help-fab-title">Talk to an Expert</span>
        <span className="help-fab-sub">A human, not a bot.</span>
      </div>
    </a>
  );
}

// ---------------------------------------------
//  APP ROOT
// ---------------------------------------------
const SCREEN={LANDING:"landing",APIKEY:"apikey",FLOW:"flow",MODE:"mode",UPLOAD:"upload",INTERVIEW:"interview",REVIEW:"review",FOLLOWUP:"followup",AUDIT:"audit",PROCESSING:"processing",OUTPUT:"output"};

export default function App() {
  const [screen, setScreen] = useState(SCREEN.LANDING);
  const [session, setSession] = useState(null);
  const [interviewData, setInterviewData] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [aiCOA, setAiCOA] = useState(null);
  const [generationError, setGenerationError] = useState("");
  const [startAtQuestion, setStartAtQuestion] = useState(null);
  const [appFlow, setAppFlow] = useState("build"); // "build" | "audit"
  const appFlowRef = useRef("build");
  // Keep ref in sync with state so closures always read current value
  useEffect(()=>{ appFlowRef.current = appFlow; }, [appFlow]);
  const [uploadData, setUploadData] = useState(null); // parsed uploaded COA
  const [auditData, setAuditData] = useState(null);   // findings + decisions

  function reconcileAfterDiagnostic(data) {
    // If the user went through the "Help me decide" diagnostic path,
    // resolve the accounting_method answer and clear its uncertain flag.
    const ans = data.answers;
    if (ans.accounting_method === "help") {
      if (ans.accrual_diagnostic_revenue === "yes") {
        // Diagnostic concluded: accrual
        ans.accounting_method = "accrual";
      } else if (ans.accrual_diagnostic_revenue === "no") {
        // Diagnostic concluded: cash
        ans.accounting_method = "cash";
      }
      // Either way, clear the uncertain flag on accounting_method
      if (data.uncertainFlags) {
        data.uncertainFlags.accounting_method = false;
      }
    }
    return data;
  }

  function handleInterviewComplete(data){
    const resolved = reconcileAfterDiagnostic(data);
    setInterviewData({...resolved});
    setStartAtQuestion(null);
    setScreen(SCREEN.REVIEW);
  }
  function handleGoToReview(data){
    const resolved = reconcileAfterDiagnostic(data);
    setInterviewData({...resolved});
    setStartAtQuestion(null);
    setScreen(SCREEN.REVIEW);
  }

  function handleGenerate(){
    // Use ref to read current appFlow — avoids stale closure issues
    const currentFlow = appFlowRef.current;
    if(currentFlow==="audit"){
      // Audit flow: go to audit analysis screen
      if(apiKey){
        setScreen(SCREEN.AUDIT);
      } else {
        // No API key — can't do audit without AI
        setGenerationError("COA Audit requires an API key. Please restart and connect your Anthropic API key.");
        setScreen(SCREEN.OUTPUT);
      }
    } else if(apiKey){
      // Build flow with AI: follow-up questions first
      setScreen(SCREEN.FOLLOWUP);
    } else {
      // Build flow without AI: standard deterministic generation
      setScreen(SCREEN.PROCESSING);
      setTimeout(()=>setScreen(SCREEN.OUTPUT),3200);
    }
  }

  function handleGoToInterview(questionId){
    // Route back into the interview at a specific question, then return to review
    setStartAtQuestion(questionId);
    setScreen(SCREEN.INTERVIEW);
  }

  function handleNewBuild(){
    // Reset all session state, keep API key active for the session
    setSession(null);
    setInterviewData(null);
    setAiCOA(null);
    setGenerationError("");
    setUploadData(null);
    setAuditData(null);
    setAppFlow("build");
    setScreen(SCREEN.FLOW);
  }

  function handleAuditComplete(result) {
    setAuditData(result);
    // Build the revised COA in output format from revised accounts + audit report
    const revisedCOA = result.revisedAccounts.map((r,i)=>({
      ...r,
      _id: i,
      flagged: false,
      deleted: false,
      _auditRevised: r._revised||false,
    }));
    setAiCOA(revisedCOA);
    setScreen(SCREEN.OUTPUT);
  }

  function handleFollowUpComplete(enrichedData){
    setInterviewData(enrichedData);
    setScreen(SCREEN.PROCESSING);
    const attemptGenerate = (attempt=1) => {
      generateAICOA(apiKey, session, enrichedData)
        .then(coa=>{ setAiCOA(coa); setScreen(SCREEN.OUTPUT); })
        .catch(err=>{
          const isOverload = err.message?.toLowerCase().includes("overload") || err.message?.includes("529");
          if(attempt===1 && isOverload){
            console.warn("COA generation overloaded — retrying in 10s...");
            setTimeout(()=>attemptGenerate(2), 10000);
          } else {
            console.error("AI COA generation failed:", err);
            setGenerationError(err.message);
            setAiCOA(null);
            setScreen(SCREEN.OUTPUT);
          }
        });
    };
    attemptGenerate();
  }

  return(
    <>
      <style>{STYLES}</style>
      <div className="topbar">
        <div className="logo" onClick={()=>setScreen(SCREEN.LANDING)}>COA<span>Builder</span></div>
        <div className="topbar-right">
          {session?.businessName&&<div className="topbar-session">{session.businessName}</div>}
          {/* Subtle AdAstra attribution in topbar */}
          <a
            href="https://adastracfo.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{fontSize:"0.7rem",color:"var(--muted-light)",textDecoration:"none",letterSpacing:"0.03em",transition:"color 0.15s"}}
            onMouseOver={e=>e.target.style.color="var(--accent)"}
            onMouseOut={e=>e.target.style.color="var(--muted-light)"}
          >
            by <strong style={{fontWeight:500}}>AdAstra CFO</strong>
          </a>
          {apiKey
            ? <div className="topbar-badge" style={{background:"var(--accent-pale)",color:"var(--accent)"}}>AI Active</div>
            : <div className="topbar-badge">Beta</div>
          }
        </div>
      </div>
      <div className="app">
        {screen===SCREEN.LANDING&&<LandingScreen onStart={()=>setScreen(SCREEN.APIKEY)}/>}
        {screen===SCREEN.APIKEY&&(
          <ApiKeyScreen
            onSubmit={k=>{setApiKey(k);setScreen(SCREEN.FLOW);}}
            onSkip={()=>setScreen(SCREEN.FLOW)}
          />
        )}
        {screen===SCREEN.FLOW&&(
          <FlowSelectScreen onSelect={flow=>{
            appFlowRef.current = flow; // sync ref immediately before navigation
            setAppFlow(flow);
            setScreen(SCREEN.MODE);
          }}/>
        )}
        {screen===SCREEN.MODE&&<ModeSelectScreen appFlow={appFlow} onProceed={s=>{setSession(s);setScreen(appFlow==="audit"?SCREEN.UPLOAD:SCREEN.INTERVIEW);}}/>}
        {screen===SCREEN.UPLOAD&&(
          <COAUploadScreen
            session={session}
            onComplete={data=>{setUploadData(data);setScreen(SCREEN.INTERVIEW);}}
            onBack={()=>setScreen(SCREEN.MODE)}
          />
        )}
        {screen===SCREEN.INTERVIEW&&<InterviewScreen session={session} initialData={interviewData} onComplete={handleInterviewComplete} onGoToReview={handleGoToReview} startAtQuestion={startAtQuestion}/>}
        {screen===SCREEN.REVIEW&&<ReviewScreen session={session} interviewData={interviewData} onGoToQuestion={()=>setScreen(SCREEN.INTERVIEW)} onGoToInterview={handleGoToInterview} onGenerate={handleGenerate} appFlow={appFlow}/>}
        {screen===SCREEN.FOLLOWUP&&(
          <FollowUpScreen
            session={session}
            interviewData={interviewData}
            apiKey={apiKey}
            onComplete={handleFollowUpComplete}
          />
        )}
        {screen===SCREEN.AUDIT&&(
          <AuditScreen
            session={session}
            interviewData={interviewData}
            uploadData={uploadData}
            apiKey={apiKey}
            onComplete={handleAuditComplete}
            onBack={()=>setScreen(SCREEN.REVIEW)}
          />
        )}
        {screen===SCREEN.PROCESSING&&<ProcessingScreen aiMode={!!apiKey}/>}
        {screen===SCREEN.OUTPUT&&(
          <OutputScreen
            session={session}
            interviewData={interviewData}
            aiCOA={aiCOA}
            apiKey={apiKey}
            generationError={generationError}
            onNewBuild={handleNewBuild}
            auditData={auditData}
            appFlow={appFlow}
          />
        )}
      </div>

      {/* Floating Help Button — visible once user is in active workflow */}
      {[SCREEN.MODE, SCREEN.UPLOAD, SCREEN.INTERVIEW, SCREEN.REVIEW,
        SCREEN.FOLLOWUP, SCREEN.AUDIT, SCREEN.PROCESSING, SCREEN.OUTPUT
       ].includes(screen) && <HelpButton/>}
    </>
  );
}
